import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import NavigationSidebar from '../../components/ui/NavigationSidebar';
import DocumentationSidebar from './components/DocumentationSidebar';
import QuickReferencePanel from './components/QuickReferencePanel';
import CodeBlock from './components/CodeBlock';
import SearchBar from './components/SearchBar';
import BreadcrumbNavigation from './components/BreadcrumbNavigation';
import APIMethodTable from './components/APIMethodTable';
import StatusCodesTable from './components/StatusCodesTable';
import { logout } from '../../utils/auth';

const APIDocumentationHub = () => {
  // Sidebar and panel states
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [docSidebarCollapsed, setDocSidebarCollapsed] = useState(false);
  const [quickPanelCollapsed, setQuickPanelCollapsed] = useState(false);
  const [userRole] = useState('admin');
  const [isAuthenticated] = useState(true);
  const navigate = useNavigate();

  // Responsive sidebar logic
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setSidebarCollapsed(true);
        setDocSidebarCollapsed(true);
        setQuickPanelCollapsed(true);
      } else {
        setSidebarCollapsed(false);
        setDocSidebarCollapsed(false);
        setQuickPanelCollapsed(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Logout handler
  const handleLogout = (e) => {
    e.preventDefault();
    logout(navigate);
  };

  // Sample data and constants
  const sampleCurlRequest = `{ 
    "merchant_id": "<your_merchant_id>", 
    "secret_key": "<your_secret_key>" 
  }`;

  const sampleJsonResponse = `[
    {
      "primary_category_id": 105,
      "primary_category_name": "Digital Camera & Camcorders",
      "category_id": 107,
      "category_name": "Cameras",
      "sub_category_id": 113,
      "sub_category_name": "Digital Cameras",
      "product_id": 2071,
      "sku_code": "CAM-CMR-DIC-0054",
      "brand_id": 291,
      "brand_name": "Sony",
      "images": {
        "image1": "https://images.lakeeshop.com/Images/CAM-CMR-DIC-0054.jpg",
        "image2": "https://images.lakeeshop.com/Images/CAM-CMR-DIC-0054B.jpg",
        "image3": "https://images.lakeeshop.com/Images/CAM-CMR-DIC-0054C.jpg"
      },
      "basic_price": 40585,
      "shipping_amount": 100,
      "tax_amount": 7305,
      "tax_percentage": 18,
      "product_price": 44490,
      "product_name": "SONY ILCE-6000L WITH SELP16-50 LENS MIRRORLESS CAMERA",
      "short_description": "Sample Short Description",
      "detail_description": "SONY ILCE-6000L WITH SELP16-50 LENS MIRRORLESS CAMERA",
      "product_mrp": 47990,
      "transfer_price": 44490,
      "spec1": null,
      "spec2": null,
      "spec3": null,
      "spec4": null,
      "qty": 1,
      "discountPercentage": 0,
      "todayDeal": 0,
      "discountDeal": 0,
      "flashDeal": 0
    }
  ]`;

const sampleSuccessResponse = `[
  {
    "primary_category_id": 1,
    "primary_category_name": "Automotive Accessories",
    "sub_category_id": 14,
    "sub_category_name": "Care",
    "product_id": 14829,
    "sku_code": "APR-WAT-UNX-0220",
    "brand_id": 419,
    "brand_name": "Pebble",
    "images": {
      "image1": "https://images.lakeeshop.com/Images/APR-WAT-UNX-0220.jpg",
      "image2": "https://images.lakeeshop.com/Images/APR-WAT-UNX-0220B.jpg",
      "image3": "https://images.lakeeshop.com/Images/APR-WAT-UNX-0220C.jpg"
  },
    "basic_price": 0,
    "shipping_amount": 100,
    "tax_amount": 0,
    "tax_percentage": 18,
    "product_price": 3320,
    "product_name": "Pebble Cosmos Prime Smart watch with Bluetooth Calling_EliteDeal",
    "short_description": "The Pebble Cosmos Prime Smartwatch with Bluetooth Calling comes with a number of advanced features, such as a heart rate monitor, step tracker, and sleep monitor, to help you stay on task with your health and fitness objectives. It enables you to receive notifications, make calls, and transmit messages from the palm of your hand. With a battery life of up to 7 days, you can remain connected on the go without having to worry about running out of juice.",
    "detail_description": "NA",
    "product_mrp": 7499,
    "transfer_price": 3320,
    "spec1": null,
    "spec2": null,
    "spec3": null,
    "spec4": null,
    "qty": 1,
    "discountPercentage": 0,
    "todayDeal": 0,
    "discountDeal": 0,
    "flashDeal": 0
  },
  {
    "primary_category_id": 1,
    "primary_category_name": "Automotive Accessories",
    "sub_category_id": 14,
    "sub_category_name": "Care",
    "product_id": 14830,
    "sku_code": "APR-WAT-UNX-0221",
    "brand_id": 419,
    "brand_name": "Pebble",
    "images": {
      "image1": "https://images.lakeeshop.com/Images/APR-WAT-UNX-0221.jpg",
      "image2": "https://images.lakeeshop.com/Images/APR-WAT-UNX-0221B.jpg",
      "image3": "https://images.lakeeshop.com/Images/APR-WAT-UNX-0221C.jpg"
    },
    "basic_price": 0,
    "shipping_amount": 100,
    "tax_amount": 0,
    "tax_percentage": 18,
    "product_price": 2999,
    "product_name": "Pebble Vision Smart watch with Bluetooth Calling_EliteDeal",
    "short_description": "The Pebble Vision Smartwatch with Bluetooth Calling is the perfect accessory for those who demand the ultimate in style and functionality. It features a sleek and sophisticated design with 80+ cloud-based watch faces that are sure to turn heads, while also offering a range of advanced features such as a 2.05 Inches ultra HD display with 600nits of brightness and 7days stand by battery that make it the ultimate smartwatch for busy professionals and fitness enthusiasts alike.",
    "detail_description": "NA",
    "product_mrp": 5999,
    "transfer_price": null,
    "spec1": null,
    "spec2": null,
    "spec3": null,
    "spec4": null,
    "qty": 1,
    "discountPercentage": 0,
    "todayDeal": 0,
    "discountDeal": 0,
    "flashDeal": 0
  }
]`;

  const ordersampleentityResponse = `[
  {
    "merchant_id": "[MERCHANT_ID]"
    client_order_id": "string"
    order_date": "2023-09-23"
    customer_details": {
      customer_name": "Yuvaraj Murugadas",
      customer_email": "yuvayr@gmail.com",
      customer_mobile": 8667767666,
      alternate_number": 7700899888,
      address_details": {
        ship_to_name": "Yuvaraja Murugadas",
        address_line1": "10/21, Srinivasan Street",
        address_line2": "Raja Annmalai Puram",
        address_line3": "Mandaveli",
        address_line4": "",
        landmark": "St Marry Road",
        city": "Chennai",
        state": "Tamil Nadu",
        pincode": 600004
      }
    },
    "product_details": {
      client_product_code": "HOK-KAP-SDM-0053",
      product_name": "BPL BSMG0052S 750W MX Series Grill Sandwich Maker",
      quantity": 1
    },
    "additional_info": {
      program_name": "payback rewards",
      po_number": "PO768768ST",
      invoice_price": "5000",
      redemption_details": {
        redemption_type": "test",
        points": 0,
        amount": 0
      }
    }
  }
]`;

  const invoicesampleentityResponse = `{
  "status": "string",
  "code": "string",
  "message": "string",
  "data": {
    "vendor": {
      "vendorName": "string",
      "vendorLogo": "string",
      "vendorAddress": "string",
      "GSTNumber": "string",
      "contactNumber": "string",
      "email": "string",
      "vendorOrderId": "string",
      "partnerOrderId": "string",
      "invoiceId": "string",
      "items": [
        {
          "productName": "string",
          "price": 0,
          "quantity": 0,
          "basePrice": 0,
          "hsnNo": "string",
          "cgstPercentage": 0,
          "cgstAmount": 0,
          "sgstPercentage": 0,
          "sgstAmount": 0,
          "igstPercentage": 0,
          "igstAmount": 0,
          "totalPrice": "string",
          "imeiNumber": "string"
        }
      ]
    },
    "customerData": {
      "recipientName": "string",
      "shippingAddress": "string",
      "recipientEmail": "string",
      "recipientPhone": "string",
      "recipientAddress": "string"
    },
    "paymentMode": "string",
    "additionalDetails": {
      "shippingCharges": 0,
      "totalInvoiceValue": "string"
    },
    "invoiceData": {
      "invoiceNumber": "string",
      "invoiceDate": "2023-11-25"
    },
    "thankYouMessage": "string",
    "contactUsEmail": "string",
    "termsAndConditions": "string"
  }
}`;

  const sampleOrderCurl = `curl -X 'POST' 
  'https://lsorder.lakeetech.com/order/create' 
  -H 'accept: */*' 
  -H 'Authorization: Bearer [TOKEN]' 
  -H 'Content-Type: application/json' 
  -d '[
  {
    "merchant_id": "[MERCHANT_ID]"
    client_order_id": "string"
    order_date": "2023-09-23"
    customer_details": {
      customer_name": "Yuvaraj Murugadas",
      customer_email": "yuvayr@gmail.com",
      customer_mobile": 8667767666,
      alternate_number": 7700899888,
      address_details": {
        ship_to_name": "Yuvaraja Murugadas",
        address_line1": "10/21, Srinivasan Street",
        address_line2": "Raja Annmalai Puram",
        address_line3": "Mandaveli",
        address_line4": "",
        landmark": "St Marry Road",
        city": "Chennai",
        state": "Tamil Nadu",
        pincode": 600004
      }
    },
    "product_details": {
      client_product_code": "HOK-KAP-SDM-0053",
      product_name": "BPL BSMG0052S 750W MX Series Grill Sandwich Maker",
      quantity": 1
    },
    "additional_info": {
      program_name": "payback rewards",
      po_number": "PO768768ST",
      invoice_price": "5000",
      redemption_details": {
        redemption_type": "test",
        points": 0,
        amount": 0
      }
    }
  }
]'`;

  const authenticationExample = `"email": "test@lakeeshop.com",
  "password": "Nice@321"'`;

  const gettokensamplecurl = `curl -X 'POST' 
'https://lvalidate.lakeetech.com/token' 
-H 'accept: */*' 
-H 'Content-Type: application/json' 
-d '{
  "merchant_id": "g8qQvtiK6xSnybj",
  "secret_key": "iRVn4gw/0MdtAgQaVplppOoysdA8bFanMh9rdOdX2qw="
}'`;

  const gettokensamplerequestbody = `{ 
  "merchant_id": "r4jhih6xSnybj", 
  "secret_key": "iRVn4gw/0MdtAgQaHyjkklsue7bFanMh9rdOdX2qw=" 
}`;

  const gettokensamplesuccessresponse = `{
  "status": "success",
  "code": "200",
  "message": "Authorized",
  "token": "eyJhbGciOiJSUzI1NiJ9.eyJyb2xlIjpbIlJPTEVfQ0xJRU5UIl0sImNsaWVudGlkIjozNiwibmFtZSI6Ill1dmFyYWpUZXN0IiwibHNlc3NpZCI6Mjc3LCJwaE5vIjo3MjAwNDY4OTk5LCJsdXNlcmlkIjozNiwic3ViIjoiaXRwcm9qZWN0c0BsYWtlZXNob3AuY29tIiwiaXNzIjoiTGFrZWUiLCJpYXQiOjE2OTU0Njk0MTEsImV4cCI6MTY5NTQ5ODIxMX0.b8ZUeVQ-PHzbY6dSr2vjLOZCWPbWx7JnNWqEUC5JSt81p03NaYHRof-QyfhPcArxNZqcQdjZheJJIyCz7_Fjs_4QaY-faXToXPGgZfGLHypd1KSg3d3PJocvbgbnmFppC6Tx1-_2rZVmXNdAmVa8LFXQYU1ZT9-hQ261x94Rle6W1uHwkq_0xp8TKl2Np9Zbg-CKCa2yuue20EO17ozDKnzBl6FnCrFPBrwh4oHOsJ4L8HIDOza3oZyT22Nee_Swi6DkWUFOMEO_CUqRuYOU5Q2OHbQqkb6_AarfSjtMcKh9sgGx5VM6IHWoTMCVkooXDEelVfFWmfISnUegVgh9EA"
}`;

  const gettokensamplefailedresponse = `{
  "status": "error",
  "code": "401",
  "message": "Unauthorized",
  "errors": "null"
}
{
  "status": "error",
  "code": "500",
  "message": "Internal Server Error",
  "errors": "null"
}`;

  const productsampleentity = `[
  {
    "primary_category_id": 105,
    "primary_category_name": "Digital Camera & Camcorders",
    "category_id": 107,
    "category_name": "Cameras",
    "sub_category_id": 113,
    "sub_category_name": "Digital Cameras",
    "product_id": 2071,
    "sku_code": "CAM-CMR-DIC-0054",
    "brand_id": 291,
    "brand_name": "Sony",
    "images": {
      "image1": "https://images.lakeeshop.com/Images/CAM-CMR-DIC-0054.jpg",
      "image2": "https://images.lakeeshop.com/Images/CAM-CMR-DIC-0054B.jpg",
      "image3": "https://images.lakeeshop.com/Images/CAM-CMR-DIC-0054C.jpg"
    },
    "basic_price": 40585,
    "shipping_amount": 100,
    "tax_amount": 7305,
    "tax_percentage": 18,
    "product_price": 47990,
    "product_name": "SONY ILCE-6000L WITH SELP16-50 LENS MIRRORLESS CAMERA",
    "short_description": "Sample Short Description",
    "detail_description": "SONY ILCE-6000L WITH SELP16-50 LENS MIRRORLESS CAMERA",
    "product_mrp": 47990,
    "transfer_price": null,
    "spec1": null,
    "spec2": null,
    "spec3": null,
    "spec4": null,
    "qty": 1,
    "discountPercentage": 0,
    "todayDeal": 0,
    "discountDeal": 0,
    "flashDeal": 0
  }
]`;

  const webhookExample = `{
  "event": "order.created",
  "data": {
    "order_id": 67890,
    "customer_id": 12345,
    "status": "pending",
    "total_amount": 149.99,
    "currency": "USD",
    "items": [
      {
        "product_id": 12345,
        "quantity": 1,
        "price": 149.99
      }
    ],
    "created_at": "2025-01-20T15:30:00Z"
  },
  "timestamp": "2025-01-20T15:30:05Z",
  "signature": "sha256=abc123def456..."
}`;


const categorySampleEntity = `[
  {
    "categoryId": 1,
    "parentInt": 0,
    "name": "Automotive Accessories",
    "isActive": 1,
    "description": null,
    "level": 1
  },
  {
    "categoryId": 15,
    "parentInt": 1,
    "name": "Essentials",
    "isActive": 1,
    "description": null,
    "level": 2
  },
  {
    "categoryId": 16,
    "parentInt": 1,
    "name": "Bike",
    "isActive": 1,
    "description": null,
    "level": 2
  }
]`;

const categoryActiveSample = categorySampleEntity;
const categoryParentSample = categorySampleEntity;
const categoryIdSample = categorySampleEntity;
const categoryLevelSample = categorySampleEntity;

const categoryCreateRequest = `{
  "category_id": 2,
  "category": "Test Cat",
  "description": "Test Description",
  "parent_category_id": 133,
  "level": 2,
  "levels": 3
}`;

const categoryCreateSuccess = `{
  "status": "success",
  "code": "200",
  "message": "Address Details Created",
  "data": {
    "categoryId": null,
    "parentInt": 133,
    "name": "Test Cat",
    "isActive": 1,
    "description": "Test Description",
    "level": 2
  }
}`;

const categoryCreateError = `{
  "status": "error",
  "code": "400",
  "message": "Validation failure",
  "errors": {
    "level": "must be greater than 0",
    "vprhRefId": "must be greater than 0",
    "levels": "must be greater than 0"
  }
}`;


const ordersampleentity = `[
  {
    "merchant_id": "[MERCHANT_ID]",
    "client_order_id": "string",
    "order_date": "2023-09-23",
    "customer_details": {
      "customer_name": "Yuvaraj Murugadas",
      "customer_email": "yuvayr@gmail.com",
      "customer_mobile": 8667767666,
      "alternate_number": 7700899888,
      "address_details": {
        "ship_to_name": "Yuvaraja Murugadas",
        "address_line1": "10/21, Srinivasan Street",
        "address_line2": "Raja Annmalai Puram",
        "address_line3": "Mandaveli",
        "address_line4": "",
        "landmark": "St Marry Road",
        "city": "Chennai",
        "state": "Tamil Nadu",
        "pincode": 600004
      }
    },
    "product_details": {
      "client_product_code": "HOK-KAP-SDM-0053",
      "product_name": "BPL BSMG0052S 750W MX Series Grill Sandwich Maker",
      "quantity": 1
    },
    "additional_info": {
      "program_name": "payback rewards",
      "po_number": "PO768768ST",
      "invoice_price": "5000",
      "redemption_details": {
        "redemption_type": "test",
        "points": 0,
        "amount": 0
      }
    }
  }
]`;

// const authorizationStatus = [
//   {
//     code: '200',
//     status: 'Success',
//     description: 'Authorization successful, request allowed',
//     category: 'success',
//     example: 'Token verified successfully'
//   },
//   {
//     code: '401',
//     status: 'Unauthorized',
//     description: 'Authorization failed, invalid or missing token',
//     category: 'client-error',
//     example: 'Invalid or expired token provided'
//   }
// ];
// Table Data
const webhookStatusCodes = [
  {
    code: '200',
    status: 'Success',
    description: 'Webhook processed successfully',
    category: 'success',
    example: 'Order status updated correctly'
  },
  {
    code: '401',
    status: 'Unauthorized',
    description: 'Invalid or missing authentication token',
    category: 'client-error',
    example: 'Token not provided in Authorization header'
  },
  {
    code: '400',
    status: 'Bad Request',
    description: 'Order product not found or invalid payload',
    category: 'client-error',
    example: 'referenceId not valid'
  },
  {
    code: '403',
    status: 'Forbidden',
    description: 'Insufficient permissions to process webhook',
    category: 'client-error',
    example: 'Webhook signature validation failed'
  }
];

// Table Component
const WebhookStatusTable = () => (
  <div className="overflow-x-auto rounded-lg border border-border shadow-sm">
    <table className="w-full border-collapse">
      <thead className="bg-muted/50">
        <tr>
          <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Code</th>
          <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Status</th>
          <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Description</th>
          <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Example</th>
        </tr>
      </thead>
      <tbody>
        {webhookStatusCodes.map((item, index) => (
          <tr
            key={item.code}
            className={index % 2 === 0 ? 'bg-background' : 'bg-muted/20'}
          >
            <td className="px-4 py-3 font-mono text-sm text-primary">{item.code}</td>
            <td className="px-4 py-3 text-sm font-medium text-foreground">{item.status}</td>
            <td className="px-4 py-3 text-sm text-muted-foreground">{item.description}</td>
            <td className="px-4 py-3 text-sm text-muted-foreground">{item.example}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

  const glossaryTerms = [
    {
      term: "API Key",
      definition: "A unique identifier used to authenticate requests to the API. Keep this secure and never share it publicly."
    },
    {
      term: "Endpoint",
      definition: "A specific URL where an API can be accessed by a client application to retrieve or send data."
    },
    {
      term: "Webhook",
      definition: "A method for an application to provide real-time information to other applications by sending HTTP POST requests."
    },
    {
      term: "JSON",
      definition: "JavaScript Object Notation - a lightweight data interchange format that\'s easy for humans to read and write."
    },
    {
      term: "REST",
      definition: "Representational State Transfer - an architectural style for designing networked applications using HTTP methods."
    },
    {
      term: "OAuth 2.0",
      definition: "An authorization framework that enables applications to obtain limited access to user accounts."
    },
    {
      term: "JWT",
      definition: "JSON Web Token - a compact, URL-safe means of representing claims to be transferred between two parties."
    },
    {
      term: "HTTPS",
      definition: "HTTP Secure - an extension of HTTP that uses encryption for secure communication over a network."
    },
    {
      term: "Pagination",
      definition: "The process of dividing large datasets into smaller, manageable chunks or pages."
    }
  ];

    const productEntity = [
    {
      term: "primary_category_id",
      definition: "Integer - The unique identifier for product primary category ID."
    },
    {
      term: "primary_category_name",
      definition: "String - Product primary category name."
    },
    {
      term: "category_id",
      definition: "Integer - The unique identifier for product category ID."
    },
    {
      term: "category_name",
      definition: "String - Product category name."
    },
    {
      term: "sub_category_id",
      definition: "Integer - The unique identifier for product sub-category of each product."
    },
    {
      term: "sub_category_name",
      definition: "String - Product sub-category name."
    },
    {
      term: "product_id",
      definition: "Integer - The unique identifier for a specific product."
    },
    {
      term: "sku_code",
      definition: "String - The unique identifier (SKU) for a product."
    },
    {
      term: "brand_id",
      definition: "Integer - Product-specific brand ID."
    },
    {
      term: "brand_name",
      definition: "String - Brand name."
    },
    {
      term: "images",
      definition: "Object - Image of each product. If no image, the response will be null."
    },
    {
      term: "basic_price",
      definition: "Integer - Product base price."
    },
    {
      term: "shipping_amount",
      definition: "Integer - Product shipping amount."
    },
    {
      term: "tax_amount",
      definition: "Integer - Product tax amount."
    },
    {
      term: "tax_percentage",
      definition: "Integer - Product tax percentage."
    },
    {
      term: "product_price",
      definition: "Integer - Product price inclusive of Base price + Shipping + Tax."
    },
    {
      term: "product_name",
      definition: "String - Product name."
    },
    {
      term: "short_description",
      definition: "String - Product short description."
    },
    {
      term: "detail_description",
      definition: "String - Product detailed description."
    },
    {
      term: "product_mrp",
      definition: "Integer - Product MRP."
    },
    {
      term: "transfer_price",
      definition: "Integer - Product transfer price."
    },
    {
      term: "spec1",
      definition: "String - Product specification detail 1."
    },
    {
      term: "spec2",
      definition: "String - Product specification detail 2."
    },
    {
      term: "spec3",
      definition: "String - Product specification detail 3."
    },
    {
      term: "spec4",
      definition: "String - Product specification detail 4."
    },
    {
      term: "qty",
      definition: "Integer - Product quantity."
    },
    {
      term: "discountPercentage",
      definition: "Integer - Product discount percentage.."
    },
    {
      term: "todayDeal",
      definition: "Integer - Product today deal price."
    },
    {
      term: "discountDeal",
      definition: "Integer - Product discount price."
    },
    {
      term: "flashDeal",
      definition: "Integer - Product flash deal price."
    }
  ];

  const imageObject = [
    {
      term: "image1",
      definition: "String - The link to default image of the product."
    },
    {
      term: "image2",
      definition: "String - The link to the second image of the product."
    },
    {
      term: "image3",
      definition: "String - The link to the third image of the product."
    }
  ];

  const errorResponseParameters = [
    {
      term: "Error",
      definition: "Cause",
      solution: "Solution"
    },
    {
      term: "The API provided is invalid.",
      definition: (
        <ul className="list-disc list-inside">
          <li>The API credentials passed in the API call differ from the ones generated on the Dashboard.</li>
          <li>Different keys for test mode and live modes.</li>
          <li>Expired API key.</li>
        </ul>
      ),
      solution: "The API keys must be active and entered correctly with no whitespace before or after the keys."
    },
    {
      term: "The field name is required",
      definition: "A mandatory field is missing.",
      solution: "Ensure all mandatory fields and values are present."
    }
  ];

  const pathParameter = [
    {
      term: "page",
      definition: "integer - Send page count to fetch number of pages. Pages starting from 0 - eg: 1"
    },
    {
      term: "size",
      definition: "integer - Send number of items per pageinteger Give product count to fetch number of products in each page. - eg: 10."
    }
  ];

  const productcodepathParameter = [
    {
      term: "product_code",
      definition: "string - Unique identifier of the product to be fetch.Use product code to get specific product information - eg: HOK-KAP-SDM-0053"
    }
  ];

  const productidpathParameter = [
    {
      term: "product_id",
      definition: "integer - Unique identifier of the product id to be fetch product.Use product_id to get specific product information - eg: 2071"
    }
  ];

  const brandidpathParameter = [
    {
      term: "brand_id",
      definition: "integer - Unique identifier of the brand to be fetch products.Use brand_id to get specific brand product information - eg: 291"
    }
  ];

  const categoryPathParameter = [
    {
      term: "parent_id",
      definition: "integer - Unique identifier of the category to be fetch details.Use Parent_id to get category information eg: 1"
    }
  ];

  const categoryidParameter = [
    {
      term: "category_id",
      definition: "integer - Unique identifier of the category to be fetch details.Use category_id to get specific category information eg: 1"
    }
  ];

  const categorylevelParameter = [
    {
      term: "level",
      definition: "integer - Unique identifier of the category level.Use level to get category list eg: For Parent Category=1, Sub Category=2"
    }
  ];

   const categoryidpathParameter = [
    {
      term: "category_id",
      definition: "integer - Unique identifier of the category to be fetch products.Use category_id to get specific category product information - eg: 14"
    }
  ];
  
  const brandEntity = [
    {
      term: "brand_id",
      definition: "integer - The unique identifier for brand."
    },
    {
      term: "brand_name",
      definition: "string - Name of the brand."
    },
    {
      term: "description",
      definition: "string - Description for each brand."
    }
  
  ];

    const responseParameters = [
    {
      term: "status",
      definition: "Request success or fail status. eg: success"
    },
    {
      term: "code",
      definition: "Request status code. eg: 200"
    },
    {
      term: "message",
      definition: "Request Status message."
    }
  
  ];

  const dataObject = [
    {
      term: "brand_id",
      definition: "integer - The unique identifier for brand."
    },
    {
      term: "brand_name",
      definition: "string - Name of the brand."
    },
    {
      term: "description",
      definition: "string - Description for each brand."
    }
  
  ];

  const categoryEntity = [
    { term: "categoryId", definition: "integer - The unique identifier for category" },
    { term: "parentInt", definition: "integer - Parent category id" },
    { term: "name", definition: "string - Category Name" },
    { term: "isActive", definition: "integer - Category active status. Eg. Active = 1, Deactive = 0." },
    { term: "description", definition: "string - Category description." },
    { term: "level", definition: "string - Category level eg. Parent Category = 1, Sub Category = 2." }
  ];

  const createCategoryEntity = [
    {
      term: "category_id",
      definition: "integer - category id, Value = 1."
    },
    {
      term: "category",
      definition: "string - Category Name."
    },
    {
      term: "description",
      definition: "string - Category description."
    },
    {
      term: "parent_category_id",
      definition: "integer - Parent Category of category, Get category id from Fetch Active Category."
    },
    {
      term: "level",
      definition: "	integer - Category level eg. Parent Category = 1, Sub Category = 2, Sub Sub Category = 3"
    },
    {
      term: "levels",
      definition: "integer - Total Number of levels in Category."
    }
  
  ];

  const pincodePathParameter = [
    { term: "pincode", definition: "Mandatory integer. Use pincode to get service availability. Example: 600002" }
  ];

  const pincodeErrorResponseParameters = [
    { term: "The API provided is invalid.", definition: "The API credentials passed differ from the ones generated on the Dashboard.", solution: "Ensure you’re using the correct API key for the environment (test/live)." },
    { term: "Expired API key.", definition: "The API key is no longer valid.", solution: "Generate a new key and ensure no whitespace before or after the token." },
    { term: "The field name is required", definition: "A mandatory field is missing.", solution: "Ensure all required fields and values are provided." }
  ];

  const orderidPathParameters = [
    { term: "order_id", definition: "string - Unique identifier of the order to be fetch.Use client_order_id used in order create eg: SAMPLE-LS-1-34903017"}
    
  ];

  const batchnoPathParameters = [
    { term: "batch_no", definition: "string - Unique identifier of the order batch number to fetch order details.Use batch_no get in order create success response eg: 20230000000010"}
    
  ];

  const clientorderidpathParameters = [
    { term: "order_id (client_order_id)", definition: "string - Unique identifier of the client order id to fetch order details.Use order_id to cancel order eg: SAMPLE-LS-1-34903016"}
    
  ];

  const invoiceorderidpathParameters = [
    { term: "order_id", definition: "string - Unique identifier of the order to be fetch.Use client_order_id used in order create eg: SAMPLE-LS-1-34903016" }

  ];

  const stockPathParameters = [
    { term: "product_code", definition: "Mandatory string. Use product code to get stock availability. Example: MTA-MOA-EPH-0368" },
    { term: "quantity", definition: "Mandatory integer. Use quantity to validate. Example: 2" }
  ];

  const stockResponseParameters = [
    { term: "status", definition: "Request success or fail status. Example: success" },
    { term: "code", definition: "Request status code. Example: 200" },
    { term: "message", definition: "Request status message" }
  ];

  const stockErrorResponseParameters = [
    { term: "The API provided is invalid.", definition: "The API credentials passed in the API call differ from the ones generated on the Dashboard.", solution: "Ensure you’re using the correct API key (test/live)." },
    { term: "Expired API key.", definition: "The API keys must be active and entered correctly with no whitespace before or after the keys.", solution: "Generate and use a new valid API key." },
    { term: "The field name is required", definition: "A mandatory field is missing.", solution: "Ensure all mandatory fields and values are present." }
  ];

  const orderEntity = [
    { term: "merchant_id", definition: "string – The unique identifier for the merchant initiating the order." },
    { term: "client_order_id", definition: "string – Unique identifier for the order provided by the client." },
    { term: "order_date", definition: "string – The date when the order is placed (YYYY-MM-DD)." },
    { term: "customer_details", definition: "object – Customer details including name, email, mobile number, and address." },
    { term: "product_details", definition: "object – Product details including product code, name, and quantity." },
    { term: "additional_info", definition: "object – Program name, PO number, invoice price, redemption details." }
  ];

  const customerDetailsObject = [
    { term: "customer_name", definition: "string – Customer’s first and last name." },
    { term: "customer_email", definition: "string – Customer email address." },
    { term: "customer_mobile", definition: "integer – 10 digit mobile number." },
    { term: "alternate_number", definition: "integer – Alternate 10 digit contact number." },
    { term: "address_details", definition: "object – Address details of the customer." }
  ];

  const addressDetailsObject = [
    { term: "ship_to_name", definition: "string – Customer shipping name." },
    { term: "address_line1", definition: "string – Address line 1." },
    { term: "address_line2", definition: "string – Address line 2." },
    { term: "address_line3", definition: "string – Address line 3." },
    { term: "address_line4", definition: "string – Address line 4." },
    { term: "landmark", definition: "string – Landmark." },
    { term: "city", definition: "string – City name." },
    { term: "state", definition: "string – State name." },
    { term: "pincode", definition: "integer – Postal code." }
  ];

  const productDetailsObject = [
    { term: "client_product_code", definition: "string – Product code provided by client." },
    { term: "product_name", definition: "string – Name of the product." },
    { term: "quantity", definition: "integer – Quantity ordered." }
  ];

  const additionalInfoObject = [
    { term: "program_name", definition: "string – Reward Program name." },
    { term: "po_number", definition: "string – Client PO number." },
    { term: "invoice_price", definition: "string – Product invoice price." },
    { term: "redemption_details", definition: "object – Redemption details (type, points, amount)." }
  ];

  const redemptionDetailsObject = [
    { term: "redemption_type", definition: "string – Type of redemption." },
    { term: "points", definition: "integer – Redemption points (optional)." },
    { term: "amount", definition: "integer – Redemption amount (optional)." }
  ];

  const orderErrorResponseParameters = [
    { term: "The API provided is invalid.", definition: "API credentials differ from those generated on the Dashboard.", solution: "Use the correct API key for the environment (test/live)." },
    { term: "Expired API key.", definition: "The API key is no longer valid.", solution: "Generate a new key, ensure no whitespace before/after the token." },
    { term: "The field name is required", definition: "A mandatory field is missing.", solution: "Ensure all required fields and values are present." }
  ];

  // const ordersErrorResponseParameters = [
  //   {
  //     term: "Error",
  //     definition: "Cause",
  //     solution: "Solution"
  //   },
  //   {
  //     term: "Invalid order ID.",
  //     definition: "String - The provided order ID does not exist or is incorrect.",
  //     solution: "Verify the order ID and try again."
  //   },
  //   {
  //     term: "Missing required parameters.",
  //     definition: "String - Some required fields for creating the order are missing.",
  //     solution: "Ensure all mandatory fields are included in the request."
  //   },
  //   {
  //     term: "Unauthorized access.",
  //     definition: "String - The API key or token is missing or invalid.",
  //     solution: "Check authentication headers and provide a valid API key."
  //   }
  // ];

  // const ordersEntityTable = [
  //   {
  //     field: "order_id",
  //     type: "Integer",
  //     description: "Unique identifier for the order."
  //   },
  //   {
  //     field: "customer_id",
  //     type: "Integer",
  //     description: "ID of the customer who placed the order."
  //   },
  //   {
  //     field: "order_date",
  //     type: "DateTime",
  //     description: "The date and time when the order was created."
  //   },
  //   {
  //     field: "status",
  //     type: "String",
  //     description: "Current status of the order (e.g., pending, confirmed, shipped, delivered)."
  //   },
  //   {
  //     field: "total_amount",
  //     type: "Decimal",
  //     description: "Total cost of the order including taxes and discounts."
  //   },
  //   {
  //     field: "payment_status",
  //     type: "String",
  //     description: "Payment state of the order (e.g., paid, pending, failed)."
  //   }
  // ];

  const orderEntityTable = [
      {
        term: "merchant_id",
        definition: "string - The unique identifier for the merchant initiating the order."
      },
      {
        term: "client_order_id",
        definition: "string - Sampel Order number BAZJ577115 The unique identifier for the order provided by the client."
      },
      {
        term: "order_date",
        definition: "string - The date when the order is placed, in the format \"YYYY-MM-DD\"."
      },
      {
        term: "customer_details",
        definition: "object - Details about the customer placing the order, including name, email, mobile number, and address."
      },
      {
        term: "product_details",
        definition: " object - Details about the product being ordered, including product code, name, and quantity."
      },
      {
        term: "additional_info",
        definition: "object - Additional information related to the order, including program name, PO number, invoice price, and redemption details."
      }
  ];

  const ordercustomerdetailsObject = [
      {
        term: "customer_name",
        definition: "string - Customers first name and last name."
      },
      {
        term: "customer_email",
        definition: "string - Customer email address."
      },
      {
        term: "customer_mobile",
        definition: "integer - Customers 10 digit mobile number."
      },
      {
        term: "alternate_number",
        definition: "integer - Customers alternate contact number should be 10 digit."
      },
      {
        term: "address_details",
        definition: " object - Address details of the customers."
      }
    
  ];

  const customerdetailsObject = [
    {
      term: "ship_to_name",
      definition: "string - Customers shipping name."
    },
    {
      term: "address_line1",
      definition: "string - Customers shipping address line 1."
    },
    {
      term: "address_line2",
      definition: "string - Customers shipping address line 2."
    },
    {
      term: "address_line3",
      definition: "string - Customers shipping address line 3."
    },
    {
      term: "address_line4",
      definition: " string - Customers shipping address line 4."
    },
    {
      term: "landmark",
      definition: " string - Customers shipping address landmark."
   },
   {
      term: "city",
      definition: " string - Customer shipping to city name."
   },
   {
      term: "state",
      definition: " string - Customers Shipping to state name."
   },
    {
      term: "pincode",
      definition: " integer - Customers Shipping to postal code."
   }
  ];

  const productdetailsObject = [
    {
      term: "client_product_code",
      definition: "string - Product code provided by client."
    },
    {
      term: "product_name",
      definition: "string - Name of the product."
    },
    {
      term: "quantity",
      definition: "integer - Product shipping quantity."
    }
  ];

  const additionalinfoObject = [
    {
      term: "program_name",
      definition: "string - Reward Program name."
    },
    {
      term: "po_number",
      definition: "string - Client PO Number."
    },
    {
      term: "invoice_price",
      definition: "string - Product invoice price."
    },
    {
      term: "redemption_details",
      definition: "object - Additional information related to the redemption details, redemption type, points and amount."
    }
  ];

  const redemptiondetailsObject = [
    {
      term: "redemption_type",
      definition: "string - Enter redemption type."
    },
    {
      term: "points",
      definition: "integer - Enter redemption amount OPTIONAL."
    },
    {
      term: "amount",
      definition: "integer - Enter redemption amount OPTIONAL."
    }
  ];

  const orderresponseParameters = [
    {
      term: "status",
      definition: "Request success or fail status. eg: success"
    },
    {
      term: "code",
      definition: "Request status code. eg: 200"
    },
    {
      term: "message",
      definition: "Request Status message."
    },
     {
      term: "data",
      definition: "object - data about the order status and order date."
    }
  ];

  const orderdataObject = [
     {
      term: "client_order_id",
      definition: "string - Client unique number used for checking order status."
    },
    {
      term: "merchant_id",
      definition: "date - Merchant ID"
    },
    {
      term: "status",
      definition: "string - Particular order status."
    },
    {
      term: "order_date",
      definition: "date - Particular order date. Date format YYYY-MM-DD"
    },
    {
      term: "dispatch_date",
      definition: " date - Particular order dispatch date. Date format YYYY-MM-DD"
    },
     {
      term: "delivery_date",
      definition: " date - Particular order delivery date. Date format YYYY-MM-DD"
   },
   {
      term: "return_date",
      definition: " date - Particular order return date. Date format YYYY-MM-DD"
   },
   {
      term: "awb_no",
      definition: " date - Airway Bill Number assigned for particular order id."
   },
   {
      term: "courier",
      definition: " date - Particular order courier details."
   },
   {
      term: "remarks",
      definition: " date - Order remarks."
   }
  ];

  const clientorderidParameters = [
    {
      term: "client_order_id",
      definition: "Order reference number of each product eg: SAMPLE-LS-1-34903016"
    },
    {
      term: "status",
      definition: "Particular order status."
    },
    {
      term: "order_date",
      definition: "eg: 2023-09-22 Order Date."
    }
  ];

  const clientorderresponseParameters = [
    {
      term: "status",
      definition: "Particular order status."
    },
    {
      term: "code",
      definition: "Order cancel status"
    },
    {
      term: "message",
      definition: "Order Detail message"
    }
  ];

  const invoiceEntityObject = [
    { term: "status", definition: "Request success or fail status. eg: success" },
    { term: "code", definition: "Request status code. eg: 200" },
    { term: "message", definition: "Request Status message." },
    { term: "vendor", definition: "object - Details about the vendor, including vendorName, vendorAddress, GSTNumber, contactNumber etc." },
    { term: "customerData", definition: "object - Details about the customer who placed the order, including recipientName, shippingAddress, recipientEmail, and recipientPhone." },
    { term: "paymentMode", definition: "string - Detail about payment method eg. PREPAID." },
    { term: "additionalDetails", definition: "object - Details about additional order information, including shippingCharges and totalInvoiceValue." },
    { term: "invoiceData", definition: "object - Details about the invoice, including invoiceNumber and invoiceDate." },
    { term: "thankYouMessage", definition: "string - Thanks message detail." },
    { term: "contactUsEmail", definition: "string - Contact email detail." },
    { term: "termsAndConditions", definition: "string - Order terms and condition details." }
  ];

  const vendorDataObject = [
    { term: "vendorName", definition: "string - Vendor name." },
    { term: "vendorLogo", definition: "string - Vendor logo URL." },
    { term: "vendorAddress", definition: "string - Vendor complete address information." },
    { term: "GSTNumber", definition: "string - Vendor GST detail." },
    { term: "contactNumber", definition: "string - Vendor contact number detail." },
    { term: "email", definition: "string - Vendor email address." },
    { term: "vendorOrderId", definition: "string - Vendor unique order reference number." },
    { term: "partnerOrderId", definition: "string - Client unique order reference number." },
    { term: "invoiceId", definition: "string - Invoice unique order reference number." },
    { term: "items", definition: "object - Details about the product, including productName, price, quantity, etc." }
  ];

  const itemsDataObject = [
    { term: "productName", definition: "string - Ordered product name." },
    { term: "price", definition: "integer - Ordered product pricing detail." },
    { term: "quantity", definition: "integer - Ordered product quantity." },
    { term: "basePrice", definition: "integer - Ordered product base price detail." },
    { term: "hsnNo", definition: "string - Ordered product HSN code." },
    { term: "cgstPercentage", definition: "integer - Product CGST percentage." },
    { term: "cgstAmount", definition: "integer - Product CGST pricing." },
    { term: "sgstPercentage", definition: "integer - Product SGST percentage." },
    { term: "sgstAmount", definition: "integer - Product SGST pricing." },
    { term: "igstPercentage", definition: "integer - Product IGST percentage." },
    { term: "igstAmount", definition: "integer - Product IGST pricing." },
    { term: "totalPrice", definition: "string - Ordered product total price." },
    { term: "imeiNumber", definition: "string - Mobile product IMEI number." }
  ];

  const customerDataObject = [
    { term: "recipientName", definition: "string - Customers first name and last name." },
    { term: "shippingAddress", definition: "string - Shipping address details of the customers." },
    { term: "recipientEmail", definition: "string - Customer email address." },
    { term: "recipientPhone", definition: "string - Customers 10 digit mobile number." },
    { term: "recipientAddress", definition: "string - Recipient address details of the customers." }
  ];

  const additionalDetailsObject = [
    { term: "shippingCharges", definition: "integer - Order shipping charges detail." },
    { term: "totalInvoiceValue", definition: "string - Order invoice value." }
  ];

  const invoiceDetailsObject = [
    { term: "invoiceNumber", definition: "string - Order invoice number." },
    { term: "invoiceDate", definition: "string - Order invoice date." }
  ];

  const invoicedataObject = [
    { term: "vendor", definition: "object - Details about the vendor, including vendorName, vendorAddress, GSTNumber, contactNumber etc." },
    { term: "customerData", definition: "object - Details about the customer who placed the order, including recipientName, shippingAddress, recipientEmail, and recipientPhone." },
    { term: "paymentMode", definition: "string - Detail about payment method. eg: PREPAID." },
    { term: "additionalDetails", definition: "object - Details about additional order information in invoice, including shippingCharges and totalInvoiceValue." },
    { term: "invoiceData", definition: "object - Details about the invoice, including invoiceNumber and invoiceDate." },
    { term: "thankYouMessage", definition: "string - Thanks message detail." },
    { term: "contactUsEmail", definition: "string - Contact email detail." },
    { term: "termsAndConditions", definition: "string - Order terms and condition details." }
  ];

  const errorcodeResponseParameters = [
    { term: "error", definition: "object - The error object." },
    { term: "code", definition: "string - Type of the error." },
    { term: "description", definition: "string - Descriptive text about the error." },
    { term: "field", definition: "string - Name of the parameter in the API request that caused the error." },
    { term: "source", definition: "string - The point of failure in the specific operation (order in this case). Possible values vary by method (Card, Netbanking, Wallets, UPI Collect, UPI Intent, Cardless EMI, Emandate)." },
    { term: "step", definition: "string - The stage where the transaction failure occurred. Possible values vary by order method (Card, Netbanking, Wallets, UPI Collect, UPI Intent, Cardless EMI, Emandate)." },
    { term: "reason", definition: "string - The exact error reason. It can be handled programmatically." },
    { term: "metadata", definition: "object - Contains additional information about the request." }
  ];

  const listOfErrors = [
    { term: "GET", definition: "Requests a representation of the specified resource. Requests using GET should only retrieve data." },
    { term: "POST", definition: "Submits an entity to the specified resource, often causing a change in state or side effects on the server." },
    { term: "PUT", definition: "Replaces all current representations of the target resource with the request payload." }
  ];

  return (
    <>
      <Helmet>
        <title>API Documentation Hub - Lakeee Shop</title>
        <meta name="description" content="Comprehensive API documentation for Lakeee Shop platform. Learn how to integrate with our REST API, manage authentication, and implement webhooks." />
        <meta name="keywords" content="API documentation, REST API, Lakeee Shop, developer guide, integration" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <NavigationSidebar
          isCollapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          userRole={userRole}
          isAuthenticated={isAuthenticated}
          onLogout={handleLogout}
        />
        <div className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-80'}`}>
          <div className="flex h-screen">
            {/* Documentation Sidebar */}
            {/* <div className={`${docSidebarCollapsed ? 'w-0' : 'w-80'} transition-all duration-300 hidden lg:block`}>
              <DocumentationSidebar
                isCollapsed={docSidebarCollapsed}
                onToggle={() => setDocSidebarCollapsed(!docSidebarCollapsed)}
              />
            </div> */}
            
            {/* Mobile Documentation Sidebar */}
            {!docSidebarCollapsed && (
              <div className="lg:hidden fixed inset-0 z-50 bg-black bg-opacity-50">
                <div className="w-80 h-full">
                  <DocumentationSidebar
                    isCollapsed={false}
                    onToggle={() => setDocSidebarCollapsed(true)}
                  />
                </div>
              </div>
            )}
            
            {/* Main Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-6 lg:p-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Menu"
                      onClick={() => setDocSidebarCollapsed(!docSidebarCollapsed)}
                      className="lg:hidden"
                    />
                    <div>
                      <h1 className="text-3xl font-bold text-foreground">API Documentation Hub</h1>
                      <p className="text-muted-foreground mt-1">
                        Comprehensive guide to integrating with Lakeee Shop API
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <SearchBar className="w-80 hidden md:block" />
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName={quickPanelCollapsed ? "PanelRightOpen" : "PanelRightClose"}
                      onClick={() => setQuickPanelCollapsed(!quickPanelCollapsed)}
                      className="hidden xl:flex"
                    />
                  </div>
                </div>
                
                <BreadcrumbNavigation />
                
                {/* Mobile Search */}
                <div className="md:hidden mb-6">
                  <SearchBar />
                </div>
                
                {/* Content Sections */}
                <div className="space-y-12">
                <section id="introduction" className="scroll-mt-6">
                  <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg p-8 mb-8">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon name="Rocket" size={24} color="white" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-foreground mb-3">Welcome to Lakeee Shop API</h2>
                        <p className="text-muted-foreground text-lg leading-relaxed mb-4">
                          Get started with Lakeee Shop APIs and test them on Postman. Generate API Keys in Test and Live Modes.Process Orders without a website or email with lakeeeshop APIs. Such as Orders, Product Catalog, Order Status and Orders List.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <a
                              href="https://www.postman.com/gold-firefly-948144/lakeeshop-api/collection/24015095-1dc395af-44c3-4219-a64e-3e2b954347ac"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Button iconName="Play">Run in Postman</Button>
                            </a>

                            <a
                              href="https://lakeeshop.com/json_collection/Lakeeshop_postman_collection.zip"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Button variant="outline" iconName="Download">
                                Download Collection (.ZIP)
                              </Button>
                            </a>
                          </div>

                        {/* <div className="flex flex-wrap gap-3">
                            <Button variant="default" iconName="Play" iconPosition="left">
                              Get Started
                            </Button>
                            <Button variant="outline" iconName="Download" iconPosition="left">
                              Download SDK
                            </Button>
                            <Button variant="ghost" iconName="ExternalLink" iconPosition="right">
                              View Examples
                            </Button>
                        </div> */}
                      </div>
                    </div>
                  </div>
                </section>
                
                {/* Getting Started */}
                <section id="getting-started" className="scroll-mt-6">
                  <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center">
                    <Icon name="Play" size={24} className="mr-3 text-primary" />
                    Getting Started
                  </h2>
                  <div className="grid md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-card border border-border rounded-lg p-6">
                      <div className="flex items-center mb-4">
                        <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center mr-3">
                        <Icon name="Key" size={16} className="text-primary" />
                        </div>
                        <h3 className="text-lg font-semibold text-foreground">1. Get API Keys</h3>
                      </div>
                        <p className="text-muted-foreground mb-4">
                          Sign up for a developer account and generate your API keys from the dashboard.
                        </p>
                        <Button variant="outline" size="sm" iconName="ArrowRight" iconPosition="right">
                          Get API Keys
                        </Button>
                    </div>
                     <div className="bg-card border border-border rounded-lg p-6">
                        <div className="flex items-center mb-4">
                          <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center mr-3">
                            <Icon name="Code" size={16} className="text-accent" />
                          </div>
                          <h3 className="text-lg font-semibold text-foreground">2. Make First Call</h3>
                        </div>
                        <p className="text-muted-foreground mb-4">
                          Test your integration with a simple API call to fetch product data.
                        </p>
                        <Button variant="outline" size="sm" iconName="ArrowRight" iconPosition="right">
                          Try API Call
                        </Button>
                     </div>
                  </div>
                </section>
                
                {/* Base URL */}
                  <section id="base-url" className="scroll-mt-6">
                    <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center">
                      <Icon name="Globe" size={24} className="mr-3 text-primary" />
                      Base URL & Endpoints
                    </h2>
                    <div className="bg-card border border-border rounded-lg p-6 mb-6">
                      <h3 className="text-lg font-semibold text-foreground mb-4">Production Environment</h3>
                      <div className="bg-slate-100 rounded-lg p-4 mb-4">
                        <code className="text-slate-800 font-mono">https://order.lakeetech.com/</code>
                      </div>
                      <h3 className="text-lg font-semibold text-foreground mb-4">Sandbox Environment</h3>
                      <div className="bg-slate-100 rounded-lg p-4">
                        <code className="text-slate-800 font-mono">https://lsorder.lakeetech.com/</code>
                      </div>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <Icon name="AlertTriangle" size={20} className="text-amber-600 mr-3 mt-0.5" />
                        <div>
                          <h4 className="font-semibold text-amber-800 mb-1">Important Note</h4>
                          <p className="text-amber-700 text-sm">
                            Always use HTTPS for API requests. HTTP requests will be rejected for security reasons.
                          </p>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* HTTP Methods */}
                  <section id="http-methods" className="scroll-mt-6">
                    <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center">
                      <Icon name="Zap" size={24} className="mr-3 text-primary" />
                      HTTP Methods
                    </h2>
                    <p className="text-muted-foreground mb-6">
                      Our API follows RESTful conventions and supports the following HTTP methods:
                    </p>
                    <APIMethodTable />
                  </section>
                  {/* Response Format */}
                  <section id="response-format" className="scroll-mt-6">
                    <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center">
                      <Icon name="FileJson" size={24} className="mr-3 text-primary" />
                      Response Format
                    </h2>
                    <p className="text-muted-foreground mb-6">
                      All API responses are returned in JSON format with a consistent structure:
                    </p>
                    <CodeBlock
                      title="Sample API Response"
                      language="json"
                      code={sampleJsonResponse}
                      className="mb-6"
                    />
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="bg-card border border-border rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-foreground mb-4">
                          Response Fields
                        </h3>
                        <ul className="space-y-2 text-sm">
                          <li className="flex items-start">
                            <code className="bg-slate-100 px-2 py-1 rounded text-xs mr-3 mt-0.5">status</code>
                            <span className="text-muted-foreground">Indicates success or error</span>
                          </li>
                          <li className="flex items-start">
                            <code className="bg-slate-100 px-2 py-1 rounded text-xs mr-3 mt-0.5">data</code>
                            <span className="text-muted-foreground">Contains the requested information</span>
                          </li>
                        </ul>
                      </div>
                      <div className="bg-card border border-border rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-foreground mb-4">
                          Content Types
                        </h3>
                        <ul className="space-y-2 text-sm">
                          <li className="flex items-center">
                            <Icon name="Check" size={16} className="text-green-600 mr-2" />
                            <code className="text-muted-foreground">application/json</code>
                          </li>
                          <li className="flex items-center">
                            <Icon name="Check" size={16} className="text-green-600 mr-2" />
                            <code className="text-muted-foreground">multipart/form-data</code>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </section>
                  
                  {/* HTTP Status Codes */}
                  <section id="status-codes" className="scroll-mt-6">
                    <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center">
                      <Icon name="Activity" size={24} className="mr-3 text-primary" />
                      HTTP Status Codes
                    </h2>
                    <p className="text-muted-foreground mb-6">
                      Our API uses conventional HTTP response codes to indicate the success or failure of requests:
                    </p>
                    <StatusCodesTable />
                  </section>
                  
                  {/* Authentication Overview */}
                  <section id="auth-overview" className="scroll-mt-6 mt-12">
                    <h2 className="text-3xl font-bold text-foreground mb-10 flex items-center">
                      <Icon name="Shield" size={28} className="mr-3 text-primary" />
                      Authentication Overview
                    </h2>
                    <div className="grid md:grid-cols-2 gap-8">
                      <div className="bg-card border border-border rounded-2xl p-6 shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                        <div className="flex items-center mb-4">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center shadow-sm mr-4">
                            <Icon name="Key" size={20} className="text-primary" />
                          </div>
                          <h3 className="text-xl font-semibold text-foreground">
                            Custom Token Authentication
                          </h3>
                        </div>
                        <p className="text-muted-foreground mb-4 text-sm leading-relaxed">
                          Secure and stateless authentication using short-lived JWTs issued after exchanging
                          <code className="px-1 py-0.5 bg-slate-100 rounded text-xs font-mono">merchant_id</code>and <code className="px-1 py-0.5 bg-slate-100 rounded text-xs font-mono">secret_key</code>.
                        </p>
                        <ul className="text-sm text-muted-foreground space-y-2">
                          <li>• Easy to implement</li>
                          <li>• Suitable for server-to-server communication</li>
                          <li>• Token has expiration for added security</li>
                          <li>• Authenticated via 
                            <code className="ml-1 px-1 py-0.5 bg-slate-100 rounded text-xs font-mono">Authorization: Bearer &lt;token&gt;</code>
                          </li>
                        </ul>
                      </div>
                      
                      {/* OAuth 2.0 */}
                      <div className="bg-card border border-border rounded-2xl p-6 shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                        <div className="flex items-center mb-4">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-accent/20 to-accent/10 flex items-center justify-center shadow-sm mr-4">
                            <Icon name="Lock" size={20} className="text-accent" />
                          </div>
                          <h3 className="text-xl font-semibold text-foreground">
                            OAuth 2.0
                          </h3>
                        </div>
                        <p className="text-muted-foreground mb-4 text-sm leading-relaxed">
                          Industry-standard authorization flow designed for user-facing applications.
                        </p>
                        <ul className="text-sm text-muted-foreground space-y-2">
                          <li>• Secure user authorization</li>
                          <li>• Token-based access</li>
                          <li>• Granular permissions</li>
                        </ul>
                      </div>
                    </div>
                  </section>
                  
                  {/* API Keys */}
                  <section id="api-auth" className="scroll-mt-6">
                    <h3 className="text-2xl font-bold text-foreground mb-2">API Key Authentication</h3>
                    <p className="text-muted-foreground mb-8">
                      All Lakeeshop APIs are authenticated using Basic Auth. Basic auth requires the following:
                    </p>
                    <CodeBlock
                      title="API Key Authentication Example"
                      language="bash"
                      code={sampleCurlRequest}
                      className="mb-6"
                    />
                    <div className="bg-blue-50 border border-blue-200 text-blue-800 text-sm rounded-xl p-4 mb-6 shadow-sm">
                      <p className="font-medium mb-1">🔑 Basic Authentication</p>
                      <p>
                        Basic auth expects a <span className="font-semibold">REQUEST BODY</span> having
                        <code className="bg-slate-100 px-1 py-0.5 rounded text-xs mx-1">MERCHANT_ID</code>
                        and
                        <code className="bg-slate-100 px-1 py-0.5 rounded text-xs mx-1">AUTHENTICATION_KEY</code>
                        in JSON format.
                      </p>
                      <p className="mt-2">
                        <span className="font-semibold">MERCHANT_ID:AUTHENTICATION_KEY</span> are provided in the
                        <span className="italic"> client API credentials page.</span>
                      </p>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <Icon name="AlertTriangle" size={20} className="text-red-600 mr-3 mt-0.5" />
                        <div>
                          <h4 className="font-semibold text-red-800 mb-1">Security Warning</h4>
                          <p className="text-red-700 text-sm">
                            Never expose your API keys in client-side code, public repositories, or logs. 
                            Always use environment variables or secure configuration management.
                          </p>
                        </div>
                      </div>
                    </div>
                  </section>
                  
                  {/* Generate API Keys */}
                  <section id="api-keys" className="scroll-mt-6">
                    <h3 className="text-2xl font-bold text-foreground mb-4 flex items-center">
                      <Icon name="Key" size={22} className="mr-2 text-primary" />
                      Generate API Keys
                    </h3>
                    <p className="text-muted-foreground mb-6 text-sm">
                      Follow these steps to securely generate your API keys:
                    </p>
                    <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 shadow-sm rounded-xl p-6 mb-8">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-lg font-semibold text-slate-800 flex items-center">
                          <Icon name="Globe" size={18} className="mr-2 text-primary" />
                          Base URL
                        </h4>
                        <span className="text-xs text-muted-foreground italic">
                          Client Portal
                        </span>
                      </div>
                      <div className="bg-slate-900 text-slate-100 font-mono text-sm rounded-lg px-4 py-3 flex items-center justify-between">
                        <code>https://client.lakeetech.com/</code>
                        <button className="ml-4 px-3 py-1.5 rounded-md bg-slate-800 text-slate-200 hover:bg-slate-700 text-xs flex items-center">
                          <Icon name="Clipboard" size={14} className="mr-1" />
                          Copy
                        </button>
                      </div>
                    </div>
                    <div className="bg-white border border-border rounded-2xl shadow-sm p-6 space-y-6 ">
                      <h3 className="text-lg font-semibold text-foreground flex items-center">
                        <Icon name="KeyRound" size={20} className="text-primary mr-2" />
                        API Authentication Steps
                      </h3>
                      <div className="flex items-start gap-3">
                        <div className="w-7 h-7 flex items-center justify-center rounded-full bg-primary text-white text-sm font-bold">1</div>
                        <p className="text-muted-foreground">
                          Log in to your <span className="font-medium">client portal</span> with appropriate credentials.
                        </p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-7 h-7 flex items-center justify-center rounded-full bg-primary text-white text-sm font-bold">2</div>
                        <div className="flex-1 space-y-3">
                          <p className="text-muted-foreground">
                            Select <span className="font-medium">API Keys</span> menu to generate the API Authentication key.
                          </p>
                          <div className="grid gap-4 sm:grid-cols-2">
                            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                              <p className="font-semibold text-blue-800">🔹 Test Mode</p>
                              <p className="text-xs text-blue-700 mt-1">
                                A simulation mode for testing your integration flow. Enabled automatically when logging in with test credentials.
                              </p>
                            </div>
                            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                              <p className="font-semibold text-green-800">🟢 Live Mode</p>
                              <p className="text-xs text-green-700 mt-1">
                                Switch to live mode after completing integration. Generate live keys and replace test keys to accept real payments.
                              </p>
                            </div>
                          </div>
                          <div className="bg-muted/40 border border-border rounded-xl p-3 text-xs text-muted-foreground space-y-1">
                            <p>• Token has expiration for added security</p>
                            <p>• Authenticated via <code className="bg-slate-100 px-1 py-0.5 rounded">Authorization: Bearer &lt;token&gt;</code></p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-7 h-7 flex items-center justify-center rounded-full bg-primary text-white text-sm font-bold">3</div>
                        <p className="text-muted-foreground">
                          The <span className="font-medium">Merchant ID</span> and <span className="font-medium">Authentication Key</span> will appear on the credentials page.
                        </p>
                      </div>
                    </div>
                    <CodeBlock
                      title="Sample Authentication Request"
                      language="bash"
                      code={authenticationExample}
                      className="mb-6 mt-6"
                    />
                    
                    {/* Security Warning */}
                    <div className="bg-gradient-to-r from-amber-50 to-amber-100 border-l-4 border-amber-500 rounded-xl p-5 shadow-sm">
                      <div className="flex items-start gap-3">
                        <Icon name="ShieldAlert" size={22} className="text-amber-600 mt-0.5" />
                        <div>
                          <h4 className="font-semibold text-amber-800 text-base mb-2">⚠️ Important Security Note</h4>
                          <ul className="list-disc list-inside text-sm text-amber-900 space-y-2">
                            <li>
                              After generating the keys from the Dashboard, <span className="font-medium">download and save them securely</span>. If lost, you must re-generate them and replace wherever required.
                            </li>
                            <li>
                              <span className="font-medium">Never share your API Key secret</span> publicly. Doing so can expose your Lakeeshop account to security risks.
                            </li>
                            <li>
                              Only the <span className="font-medium">Key ID</span> remains visible on the Dashboard.
                              The <span className="italic">Key Secret</span> is hidden to prevent misuse.
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    
                    {/* Roll API Keys */}
                    <section id="roll-api-keys" className="mt-12">
                      <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center">
                        <Icon name="Key" size={24} className="mr-3 text-primary" />
                        Roll API Keys
                      </h2>
                      <p className="text-muted-foreground leading-relaxed">
                        If you lose or expose your Live or Test mode API keys, you can immediately
                        <span className="font-medium"> regenerate new keys</span>. The old keys will be deactivated once re-generated,
                        ensuring your account security.
                      </p>
                    </section>
                    
                    {/* Get Token */}
                    <section id="get-token" className="mt-12">
                      <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center">
                        <Icon name="KeyRound" size={24} className="mr-3 text-primary" />
                        Get Token
                      </h2>
                      <p className="text-muted-foreground leading-relaxed">
                        Use the <span className="font-medium">Lakeeshop Order API</span> to fetch specific order details.
                        The API response includes information such as <span className="italic">order state, order amount, order ID</span>, and more — 
                        all returned in JSON format.
                      </p>
                    </section>
                    <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 shadow-sm rounded-xl p-6 mb-8">
                      <div className="flex items-center justify-between mb-4">
                        <span className="px-3 py-1 text-sm font-semibold rounded-md bg-green-100 text-green-800">
                          POST
                        </span>
                        <span className="text-xs text-muted-foreground italic">Authentication Endpoint</span>
                      </div>
                      <div className="bg-slate-900 text-slate-100 font-mono text-sm rounded-lg px-4 py-3 flex items-center justify-between">
                        <code>https://lvalidate.lakeetech.com/token</code>
                        <button className="ml-4 px-2 py-1 rounded-md bg-slate-800 text-slate-200 hover:bg-slate-700 text-xs">
                          Copy
                        </button>
                      </div>
                    </div>
                    <CodeBlock
                      title="Sample Authentication Request"
                      language="bash"
                      code={gettokensamplerequestbody}
                      className="mb-6"
                    />
                    <CodeBlock
                      title="Sample Curl Request"
                      language="bash"
                      code={gettokensamplecurl}
                      className="mb-6"
                    />
                    <CodeBlock
                      title="Sample Successful Response"
                      language="bash"
                      code={gettokensamplesuccessresponse}
                      className="mb-6"
                    />
                    <CodeBlock
                      title="Sample Failed Response"
                      language="bash"
                      code={gettokensamplefailedresponse}
                      className="mb-6"
                    />
                  </section>

                  {/* Best Practices */}
                  <section id="best-practices" className="scroll-mt-6">
                    <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center">
                      <Icon name="Shield" size={24} className="mr-3 text-primary" />
                      Best Practices
                    </h2>
                    <p className="text-muted-foreground mb-6 text-sm">
                      Follow these best practices to keep your Lakeeshop API integration secure and reliable.
                    </p>
                    <div className="space-y-4">
                      <div className="flex items-start bg-rose-50 border border-rose-200 rounded-xl p-4">
                        <Icon name="Shield" size={18} className="text-rose-600 mr-3 mt-1" />
                        <div>
                          <h4 className="font-semibold text-rose-800 mb-1">Keep API Keys Private</h4>
                          <p className="text-sm text-muted-foreground">
                            Never share your API Key secret publicly. Exposure can lead to unauthorized access
                            and compromise your Lakeeshop account.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start bg-blue-50 border border-blue-200 rounded-xl p-4">
                        <Icon name="Server" size={18} className="text-blue-600 mr-3 mt-1" />
                        <div>
                          <h4 className="font-semibold text-blue-800 mb-1">Handle DNS Responsibly</h4>
                          <p className="text-sm text-muted-foreground">
                            Honour the TTL values provided by Lakeeshop servers and avoid aggressive DNS caching
                            to ensure smoother request routing.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                        <Icon name="CheckCircle" size={18} className="text-emerald-600 mr-3 mt-1" />
                        <div>
                          <h4 className="font-semibold text-emerald-800 mb-1">Validate User Inputs</h4>
                          <p className="text-sm text-muted-foreground">
                            Always sanitize and validate inputs before sending API requests to avoid security
                            vulnerabilities and bad data.
                          </p>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Error Handling */}
                  <section id="error-handling" className="scroll-mt-6">
                    <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center">
                      <Icon name="AlertCircle" size={24} className="mr-3 text-primary" />
                      Error Handling
                    </h2>
                    <p className="text-muted-foreground mb-6">
                      Our API uses conventional HTTP response codes and provides detailed error information:
                    </p>
                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                      <div className="bg-card border border-border rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-foreground mb-4">Error Response Format</h3>
                        <CodeBlock
                          language="json"
                            code={`{
                            "status": "error",
                            "code": "500",
                            "message": "Order not found",
                            "errors": null
                          }`}
                        />
                      </div>
                      <div className="bg-card border border-border rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-foreground mb-4">Best Practices</h3>
                        <ul className="space-y-3 text-sm text-muted-foreground">
                          <li className="flex items-start">
                            <Icon name="Check" size={16} className="text-green-600 mr-2 mt-0.5" />
                            Always check HTTP status codes
                          </li>
                          <li className="flex items-start">
                            <Icon name="Check" size={16} className="text-green-600 mr-2 mt-0.5" />
                            Implement exponential backoff for retries
                          </li>
                          <li className="flex items-start">
                            <Icon name="Check" size={16} className="text-green-600 mr-2 mt-0.5" />
                            Log request_id for debugging
                          </li>
                          <li className="flex items-start">
                            <Icon name="Check" size={16} className="text-green-600 mr-2 mt-0.5" />
                            Handle rate limiting gracefully
                          </li>
                          <li className="flex items-start">
                            <Icon name="Check" size={16} className="text-green-600 mr-2 mt-0.5" />
                            Validate data before sending requests
                          </li>
                        </ul>
                      </div>
                    </div>
                  </section>
                  
                  {/* Glossary */}
                  <section id="terms-definitions" className="scroll-mt-6">
                    <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center">
                      <Icon name="BookText" size={24} className="mr-3 text-primary" />
                      Terms & Definitions
                    </h2>
                    <p className="text-muted-foreground mb-6">
                      Common terms and concepts used throughout the Lakeee Shop API documentation:
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full border border-border rounded-xl overflow-hidden shadow-sm">
                        <thead>
                          <tr className="bg-primary/5 text-left">
                            <th className="px-6 py-3 text-sm font-semibold text-foreground border-b border-border w-1/4">
                              Term
                            </th>
                            <th className="px-6 py-3 text-sm font-semibold text-foreground border-b border-border">
                              Definition
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {glossaryTerms?.map((term, index) => (
                            <tr
                              key={`glossary-term-${index}`}
                              className="hover:bg-muted/30 transition-colors"
                            >
                              <td className="px-6 py-4 border-b border-border align-top">
                                <code className="bg-muted/40 px-2 py-1 rounded text-sm font-medium text-primary">
                                  {term?.term}
                                </code>
                              </td>
                              <td className="px-6 py-4 border-b border-border text-sm text-muted-foreground leading-relaxed">
                                {term?.definition}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                  
                  {/* Product Catalog */}
                  <section id="products-api" className="scroll-mt-6">
                    <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center">
                      <Icon name="Package" size={24} className="mr-3 text-primary" />
                      Product Catalog
                    </h2>
                    <p className="text-muted-foreground mb-4">
                      Manage your product catalog with comprehensive <span className="font-semibold text-foreground">CRUD operations</span>.
                    </p>
                    <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 shadow-sm rounded-xl p-6 mb-8">
                      <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                        <Icon name="Link2" size={18} className="mr-2 text-primary" />
                        Base URL
                      </h3>
                      <div className="bg-slate-900 text-slate-100 font-mono text-sm rounded-lg px-4 py-3 flex items-center justify-between">
                        <code>https://client.lakeetech.com/</code>
                        <button className="ml-4 px-2 py-1 rounded-md bg-slate-800 text-slate-200 hover:bg-slate-700 text-xs">
                          Copy
                        </button>
                      </div>
                    </div>
                    
                    {/* Highlighted Note */}
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-start">
                      <Icon name="Info" size={18} className="text-blue-600 mr-3 mt-0.5" />
                      <p className="text-sm text-blue-900">
                        Lakeee Shop APIs are <strong>completely RESTful</strong>, and all responses are returned in <strong>JSON format</strong> using a reactive, asynchronous approach.
                      </p>
                    </div>
                    
                    {/* API Description */}
                    <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 shadow-sm rounded-xl p-6 mb-8">
                      <div className="flex items-start mb-4">
                        <Icon name="Database" size={20} className="text-primary mr-3 mt-1" />
                        <div>
                          <h3 className="text-lg font-semibold text-foreground mb-2">
                            Why Use Product Catalog API?
                          </h3>
                          <p className="text-sm text-muted-foreground mb-3">
                            The Product Catalog API empowers you to retrieve detailed information about your products quickly and efficiently.
                          </p>
                        </div>
                      </div>
                      <ul className="space-y-3 text-sm text-muted-foreground">
                        <li className="flex items-start">
                          <Icon name="Tag" size={16} className="text-blue-600 mr-2 mt-0.5" />
                          <span>Includes pricing details like </span>
                          <span className="ml-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-medium">MRP</span>
                          <span> and </span>
                          <span className="ml-1 px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-medium">Transfer Pricing</span>.
                        </li>
                        <li className="flex items-start">
                          <Icon name="BarChart3" size={16} className="text-indigo-600 mr-2 mt-0.5" />
                          Provides <span className="font-medium text-foreground">structured product data</span> for analytics and reporting.
                        </li>
                        <li className="flex items-start">
                          <Icon name="Zap" size={16} className="text-yellow-600 mr-2 mt-0.5" />
                          Enables a <span className="font-medium text-foreground">faster, data-driven approach</span> to managing product catalogs.
                        </li>
                      </ul>
                    </div>
                    {/* Product Entity */}
                    <section id="product-entity" className="scroll-mt-6">
                      <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center mt-8">
                        <Icon name="Box" size={24} className="mr-3 text-primary" />
                        Product Entity
                      </h2>
                      <p className="text-muted-foreground mb-6">
                        The product entity has the following fields:
                      </p>
                      <div className="overflow-x-auto rounded-xl border border-border shadow-sm">
                        <table className="w-full border-collapse text-sm">
                          <thead>
                            <tr className="bg-muted/50 text-left">
                              <th className="px-4 py-3 font-medium text-foreground">Field</th>
                              <th className="px-4 py-3 font-medium text-foreground">Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            {productEntity?.map((term, index) => (
                              <tr
                                key={`product-entity-${index}`}
                                className="border-t border-border hover:bg-muted/30 transition-colors"
                              >
                                <td className="px-4 py-3 font-mono text-primary text-sm">
                                  {term?.term}
                                </td>
                                <td className="px-4 py-3 text-muted-foreground">
                                  {term?.definition}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </section>
                    {/* Image Object */}
                    <section id="image-object" className="scroll-mt-6">
                      <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center mt-10">
                        <Icon name="Image" size={24} className="mr-3 text-primary" />
                        Image Object
                      </h2>
                      <p className="text-muted-foreground mb-6">
                        The image object has the following fields:
                      </p>
                      <div className="overflow-x-auto rounded-xl border border-border shadow-sm">
                        <table className="w-full border-collapse text-sm">
                          <thead>
                            <tr className="bg-muted/50 text-left">
                              <th className="px-4 py-3 font-medium text-foreground">Field</th>
                              <th className="px-4 py-3 font-medium text-foreground">Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            {imageObject?.map((term, index) => (
                              <tr
                              key={`image-object-${index}`}
                              className="border-t border-border hover:bg-muted/30 transition-colors"
                              >
                                <td className="px-4 py-3 font-mono text-primary text-sm">
                                  {term?.term}
                                </td>
                                <td className="px-4 py-3 text-muted-foreground">
                                  {term?.definition}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </section>

                  {/* Sample JSON */}
                  <CodeBlock
                    title="Sample Product Entity"
                    language="json"
                    code={productsampleentity}
                    className="mb-6 mt-8"
                  />

                  {/* Fetch Product Catalog */}
                  <section id="api-keys" className="scroll-mt-6">
                    <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center">
                      <Icon name="Package" size={24} className="mr-3 text-primary" />
                      Fetch Product Catalog
                    </h2>
                    <p className="text-muted-foreground mb-8">
                      Fetch product catalog data via API for dynamic rendering and streamlined integration.
                    </p>
                    <div className="bg-card border border-border rounded-xl shadow-sm p-6 mb-8">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm font-semibold bg-green-100 text-green-700 px-3 py-1 rounded-full">
                          GET
                        </span>
                        <span className="text-xs text-muted-foreground">Base URL</span>
                      </div>
                      <div className="bg-slate-100 rounded-lg p-4">
                        <code className="text-slate-800 font-mono text-sm">
                          https://lsorder.lakeetech.com
                        </code>
                      </div>
                    </div>
                  </section>
                  <section id="authentication" className="scroll-mt-6">
                    <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center">
                      <Icon name="KeyRound" size={24} className="mr-3 text-primary" />
                      Authentication
                    </h2>
                    <p className="text-muted-foreground mb-6">
                      This API is secured with <span className="font-semibold">API key authentication</span>.
                      Include your API key in the request headers to access the endpoint.
                    </p>
                    <div className="bg-muted rounded-lg p-4 mb-8">
                      <CodeBlock
                        language="bash"
                        code={`Authorization: Bearer [TOKEN]`}
                      />
                    </div>
                  </section>
                  <section id="endpoints" className="scroll-mt-6">
                    <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center">
                      <Icon name="Server" size={24} className="mr-3 text-primary" />
                      Endpoints
                    </h2>
                    <div className="bg-card border border-border rounded-xl shadow-sm p-6 mb-8">
                      <h3 className="text-lg font-semibold text-foreground mb-4">Retrieve Product Details by ID</h3>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm font-semibold bg-green-100 text-green-700 px-3 py-1 rounded-full">
                          GET
                        </span>
                        <span className="text-xs text-muted-foreground">Endpoint</span>
                      </div>
                      <div className="bg-slate-100 rounded-lg p-4 mb-4">
                        <code className="text-slate-800 font-mono text-sm">/product_details/all</code>
                      </div>
                    </div>
                    <CodeBlock
                      language="CURL"
                      code={`curl -X 'GET'
  'https://lsorder.lakeetech.com/product_details/all'
  -H 'accept: */*'
  -H 'Authorization: Bearer [TOKEN]'`} className="mb-12 mt-2"/>
                        </section>
                  
                  {/* Fetch Product Catalog Pagewise */}
                  <section id="fetch-product-catalog-pagewise" className="scroll-mt-6">
                    <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center">
                      <Icon name="Layers" size={24} className="mr-3 text-primary" />
                      Fetch Product Catalog (Pagewise)
                    </h2>
                    <p className="text-muted-foreground mb-6">
                      Retrieve product catalog details with <span className="font-semibold text-foreground">paginated results</span> using the endpoint below.
                    </p>
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-6">
                      <h3 className="text-lg font-semibold text-blue-800 mb-2 flex items-center">
                        <Icon name="Shield" size={20} className="mr-2 text-blue-600" />
                        Authentication Required
                      </h3>
                      <p className="text-sm text-blue-700 mb-3">
                        Provide your API key in the request headers to authenticate requests.
                      </p>
                      <CodeBlock
                        language="json"
                        code={`Authorization: Bearer [TOKEN]`}
                      />
                    </div>
                    <div className="bg-card border border-border rounded-xl shadow-sm p-6 mb-6">
                      <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                        <span className="bg-green-100 text-green-700 font-mono text-xs px-2 py-1 rounded mr-2">
                          GET
                        </span>
                        Retrieve Product Details (Pagewise)
                      </h3>
                      <div className="bg-slate-100 rounded-lg p-4">
                        <code className="text-slate-800 font-mono text-sm">
                          /product_details/all/page
                        </code>
                      </div>
                    </div>
                  </section>
                  {/* Path Parameter */}
                  <section id="terms-definitions" className="scroll-mt-6 mt-12">
                    <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 shadow-sm">
                      <h3 className="text-lg font-semibold text-orange-800 flex items-center mb-4">
                        <Icon name="BookText" size={20} className="mr-2 text-orange-600" />
                        Path Parameter
                      </h3>
                      <div className="overflow-x-auto mb-6">
                        <table className="w-full border-collapse border border-border rounded-lg">
                          <tbody>
                            {pathParameter?.map((term, index) => (
                              <tr
                                  key={`path-Parameter-${index}`}
                                  className={index % 2 === 0 ? "bg-background" : "bg-muted/20"}>
                                    <td className="border border-border px-6 py-4">
                                      <code className="bg-slate-100 px-2 py-1 rounded text-sm font-medium text-slate-800">
                                        {term?.term}
                                      </code>
                                    </td>
                                    <td className="border border-border px-6 py-4 text-sm text-muted-foreground">
                                      <code className="bg-slate-100 px-2 py-1 rounded text-sm font-medium text-slate-800">
                                        {term?.definition}
                                      </code>
                                    </td>
                                 </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                      {/* Example CURL */}
                      <CodeBlock
                      language="bash"
                      code={`curl -X 'GET'
    'https://order.lakeetech.com/product_details/all/page?page=1&size=10'
    -H 'accept: */*'`}/>
                    </div>
                  </section>
                  
                  {/* Response Headers */}
                  <section id="response-headers" className="scroll-mt-6 mt-12">
                    <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center">
                      <Icon name="Server" size={24} className="mr-3 text-primary" />
                      Response Headers
                    </h2>
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-6">
                      <h3 className="text-lg font-semibold text-blue-800 mb-2 flex items-center">
                        <Icon name="Info" size={20} className="mr-2 text-blue-600" />
                        About <code className="bg-white px-1 py-0.5 rounded text-xs font-mono">x-total-count</code>
                      </h3>
                      <p className="text-sm text-blue-700 leading-relaxed">
                        The <code className="bg-white px-1 py-0.5 rounded text-xs font-mono">x-total-count</code> header 
                        indicates the <span className="font-semibold text-foreground">total number of products</span> available 
                        (e.g., <code className="font-mono">1225</code>). 
                        This helps manage pagination when combined with 
                        <code className="bg-white px-1 py-0.5 rounded text-xs font-mono">page</code> and 
                        <code className="bg-white px-1 py-0.5 rounded text-xs font-mono">size</code> parameters, ensuring accurate navigation through API results.
                      </p>
                    </div>
                    <div className="bg-card border border-border rounded-xl shadow-sm p-6">
                      <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                        <Icon name="Terminal" size={20} className="mr-2 text-primary" />
                        Example Response Headers
                      </h3>
                      <CodeBlock
                        language="http"
                        code={`cache-control: no-cache,no-store,max-age=0,must-revalidate
connection: keep-alive
content-type: application/json
date: Tue, 16 Jul 2024 06:48:51 GMT
expires: 0
pragma: no-cache
referrer-policy: no-referrer
server: nginx
transfer-encoding: chunked
vary: Origin,Access-Control-Request-Method,Access-Control-Request-Headers
x-content-type-options: nosniff
x-frame-options: DENY
x-total-count: 7847
x-xss-protection: 0,1; mode=block`}
    />
                  </div>
                </section>
                
                {/* Fetch Product by Code */}
                <section id="api-keys" className="scroll-mt-6 mt-12">
                  <h2 className="text-2xl font-bold text-foreground flex items-center mb-4">
                    <Icon name="Box" size={24} className="mr-2 text-primary" />
                    Fetch Product by Code
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    Retrieves the details of a specific product identified by its unique 
                    <code className="bg-slate-100 px-2 py-0.5 rounded text-sm font-mono mx-1">product_code</code>.
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8 shadow-sm">
                    <h3 className="text-lg font-semibold text-blue-800 flex items-center mb-3">
                      <Icon name="Shield" size={20} className="mr-2 text-blue-600" />
                      Authentication
                    </h3>
                    <p className="text-sm text-blue-700 mb-4">
                      All requests to this API require authentication using an API key.  
                      Include it in the headers as shown below:
                    </p>
                    <CodeBlock
                      language="http"
                      code={`Authorization: Bearer [TOKEN]`}
                    />
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-8 shadow-sm">
                    <h3 className="text-lg font-semibold text-green-800 flex items-center mb-3">
                      <Icon name="Globe" size={20} className="mr-2 text-green-600" />
                      Endpoint
                    </h3>
                    <p className="text-sm text-green-700 mb-3">Push Bulk Orders.</p>
                    <div className="bg-slate-100 rounded-lg p-4">
                      <code className="text-slate-800 font-mono text-sm">
                        GET /product_details/by_code/:product_code
                      </code>
                    </div>
                  </div>
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 shadow-sm">
                    <h3 className="text-lg font-semibold text-orange-800 flex items-center mb-4">
                      <Icon name="BookText" size={20} className="mr-2 text-orange-600" />
                      Path Parameter
                    </h3>
                      <div className="overflow-x-auto mb-6">
                        <table className="w-full border-collapse border border-border rounded-lg">
                          <tbody>
                            {productcodepathParameter?.map((term, index) => (
                              <tr
                                key={`productcodepath-Parameter-${index}`}
                                className={index % 2 === 0 ? 'bg-background' : 'bg-muted/20'}
                              >
                                <td className="border border-border px-6 py-4">
                                  <code className="bg-slate-100 px-2 py-1 rounded text-sm font-medium text-slate-800">
                                    {term?.term}
                                  </code>
                                </td>
                                <td className="border border-border px-6 py-4 text-sm text-muted-foreground">
                                  {term?.definition}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <CodeBlock
                      language="bash"
                      code={`curl -X 'GET' \\
  'https://lsorder.lakeetech.com/product_details/by_code/CAM-CMR-SLR-0123' \\
  -H 'accept: */*' \\
  -H 'Authorization: Bearer [TOKEN]'`}
    />
                  </div>
                </section>
                
                {/* ================= Fetch Product by Product ID ================= */}
                <section id="fetch-product-id" className="scroll-mt-6 mt-12">
                  <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-2xl p-8 shadow-sm">
                    <h2 className="text-2xl font-bold text-blue-800 flex items-center mb-4">
                      <Icon name="Box" size={22} className="mr-2 text-blue-600" />
                      Fetch Product with <code className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded">product_id</code>
                    </h2>
                    <p className="text-muted-foreground mb-6">
                      Retrieves the details of a specific product identified by its unique <code>product_id</code>.
                    </p>
                    <div className="bg-white border border-border rounded-xl p-6 mb-6 shadow-sm">
                      <h3 className="text-lg font-semibold text-foreground flex items-center mb-2">
                        <Icon name="Key" size={18} className="mr-2 text-green-600" />
                        Authentication
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        This API uses <span className="font-medium text-foreground">API key–based authentication</span>.  
                        Provide your API key in the request headers.
                      </p>
                      <CodeBlock language="json" code={`Authorization: Bearer [TOKEN]`} />
                    </div>
                    <div className="bg-white border border-border rounded-xl p-6 mb-6 shadow-sm">
                      <h3 className="text-lg font-semibold text-foreground flex items-center mb-3">
                        <Icon name="Network" size={18} className="mr-2 text-purple-600" />
                        Endpoint
                      </h3>
                      <div className="bg-slate-100 rounded-lg p-4">
                        <code className="text-slate-800 font-mono">GET /product_details/by_id/:product_id</code>
                      </div>
                    </div>
                    <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 mb-6 shadow-sm">
                      <h3 className="text-lg font-semibold text-orange-800 flex items-center mb-3">
                        <Icon name="BookText" size={18} className="mr-2 text-orange-600" />
                        Path Parameter
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-border rounded-lg">
                          <tbody>
                            {productidpathParameter?.map((term, index) => (
                              <tr
                                key={`productidpath-Parameter-${index}`}
                                className={index % 2 === 0 ? "bg-background" : "bg-muted/20"}
                              >
                                <td className="border border-border px-6 py-4">
                                  <code className="bg-slate-100 px-2 py-1 rounded text-sm font-medium text-slate-800">
                                    {term?.term}
                                  </code>
                                </td>
                                <td className="border border-border px-6 py-4 text-sm text-muted-foreground">
                                  {term?.definition}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <CodeBlock
                      title="Example Request"
                      language="bash"
                      code={`curl -X 'GET' \\
  'https://lsorder.lakeetech.com/product_details/by_id/2071' \\
  -H 'accept: */*' \\
  -H 'Authorization: Bearer [TOKEN]'`}
    />
                  </div>
                </section>
                
                {/* ================= Fetch Products by Brand ID ================= */}
                <section id="fetch-brand-id" className="scroll-mt-6 mt-12">
                  <div className="bg-gradient-to-br from-indigo-50 to-white border border-indigo-200 rounded-2xl p-8 shadow-sm">
                    <h2 className="text-2xl font-bold text-indigo-800 flex items-center mb-4">
                      <Icon name="Tags" size={22} className="mr-2 text-indigo-600" />
                      Fetch Products with <code className="bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded">brand_id</code>
                    </h2>
                    <p className="text-muted-foreground mb-6">
                      Retrieves the details of all products associated with a specific <code>brand_id</code>.
                    </p>
                    <div className="bg-white border border-border rounded-xl p-6 mb-6 shadow-sm">
                      <h3 className="text-lg font-semibold text-foreground flex items-center mb-2">
                        <Icon name="Key" size={18} className="mr-2 text-green-600" />
                        Authentication
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        All API requests require authentication using an API key in the request headers.
                      </p>
                      <CodeBlock language="json" code={`Authorization: Bearer [TOKEN]`} />
                    </div>
                    <div className="bg-white border border-border rounded-xl p-6 mb-6 shadow-sm">
                      <h3 className="text-lg font-semibold text-foreground flex items-center mb-3">
                        <Icon name="Network" size={18} className="mr-2 text-purple-600" />
                        Endpoint
                      </h3>
                      <div className="bg-slate-100 rounded-lg p-4">
                        <code className="text-slate-800 font-mono">GET /product_details/by_brand_id/:brand_id</code>
                      </div>
                    </div>
                    <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 mb-6 shadow-sm">
                      <h3 className="text-lg font-semibold text-orange-800 flex items-center mb-3">
                        <Icon name="BookText" size={18} className="mr-2 text-orange-600" />
                        Path Parameter
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-border rounded-lg">
                          <tbody>
                            {brandidpathParameter?.map((term, index) => (
                              <tr
                                key={`brandidpath-Parameter-${index}`}
                                className={index % 2 === 0 ? "bg-background" : "bg-muted/20"}
                              >
                                <td className="border border-border px-6 py-4">
                                  <code className="bg-slate-100 px-2 py-1 rounded text-sm font-medium text-slate-800">
                                    {term?.term}
                                  </code>
                                </td>
                                <td className="border border-border px-6 py-4 text-sm text-muted-foreground">
                                  {term?.definition}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <CodeBlock
                      title="Example Request"
                      language="bash"
                      code={`curl -X 'GET' \\
  'https://lsorder.lakeetech.com/product_details/by_brand_id/291' \\
  -H 'accept: */*' \\
  -H 'Authorization: Bearer [TOKEN]'`}
    />
                </div>
              </section>
              
              {/* ============ Fetch Products by Category ID ============ */}
              <section id="fetch-category-id" className="scroll-mt-6 mt-12">
                <div className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 rounded-2xl p-8 shadow-sm">
                  <h2 className="text-2xl font-bold text-emerald-800 flex items-center mb-4">
                    <Icon name="Boxes" size={22} className="mr-2 text-emerald-600" />
                    Fetch Products with <code className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded">category_id</code>
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    Retrieves the details of all products associated with a specific <code>category_id</code>.
                  </p>
                  <div className="bg-white border border-border rounded-xl p-6 mb-6 shadow-sm">
                    <h3 className="text-lg font-semibold text-foreground flex items-center mb-2">
                      <Icon name="KeyRound" size={18} className="mr-2 text-green-600" />
                      Authentication
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      This API requires API key authentication. The key must be included in the request headers.
                    </p>
                    <CodeBlock language="json" code={`Authorization: Bearer [TOKEN]`} />
                  </div>
                  <div className="bg-white border border-border rounded-xl p-6 mb-6 shadow-sm">
                    <h3 className="text-lg font-semibold text-foreground flex items-center mb-3">
                      <Icon name="Server" size={18} className="mr-2 text-emerald-600" />
                      Endpoint
                    </h3>
                    <div className="bg-slate-100 rounded-lg p-4">
                      <code className="text-slate-800 font-mono">GET /product_details/by_category_id/:category_id</code>
                    </div>
                  </div>
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 mb-6 shadow-sm">
                    <h3 className="text-lg font-semibold text-orange-800 flex items-center mb-3">
                      <Icon name="BookText" size={18} className="mr-2 text-orange-600" />
                      Path Parameter
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse border border-border rounded-lg">
                        <tbody>
                          {categoryidpathParameter?.map((term, index) => (
                            <tr
                              key={`categoryidpath-Parameter-${index}`}
                              className={index % 2 === 0 ? "bg-background" : "bg-muted/20"}
                            >
                              <td className="border border-border px-6 py-4">
                                <code className="bg-slate-100 px-2 py-1 rounded text-sm font-medium text-slate-800">
                                  {term?.term}
                                </code>
                              </td>
                              <td className="border border-border px-6 py-4 text-sm text-muted-foreground">
                                <code className="bg-slate-100 px-2 py-1 rounded text-sm font-medium text-slate-800">
                                  {term?.definition}
                                </code>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <CodeBlock
                    title="Example Request"
                    language="bash"
                    code={`curl -X 'GET' \\
  'https://lsorder.lakeetech.com/product_details/by_category_id/14' \\
  -H 'accept: */*' \\
  -H 'Authorization: Bearer [TOKEN]'`}
    />
                  </div>
                </section>

                  {/* Success Response */}
                  <div className="mt-8 rounded-xl border border-border shadow-sm mb-8 overflow-hidden">
                    <div className="flex items-center justify-between bg-muted/40 px-4 py-2">
                      <div className="flex items-center gap-2">
                        <Icon name="CheckCircle" size={18} className="text-green-600" />
                        <span className="font-semibold text-foreground">Sample Success Response</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Icon name="CheckCircle" size={18} className="text-green-600" />
                        <span className="font-semibold text-foreground">Sample Success Response</span>
                      </div>
                    </div>
                    <div className="bg-slate-950 text-slate-100 text-sm font-mono p-4 overflow-x-auto">
                      <CodeBlock
                      language="json"
                      code={sampleSuccessResponse}
                      className="!m-0 !bg-transparent"
                      />
                    </div>
                  </div>
                  
                  {/* Failed Response */}
                  <div className="mt-8 rounded-xl border border-border shadow-sm mb-8 overflow-hidden">
                    <div className="flex items-center justify-between bg-muted/40 px-4 py-2">
                      <div className="flex items-center gap-2">
                        <Icon name="AlertTriangle" size={18} className="text-red-600" />
                        <span className="font-semibold text-foreground">Failed Response</span>
                      </div>
                    </div>
                    <div className="bg-slate-950 text-slate-100 text-sm font-mono p-4 overflow-x-auto">
                      <CodeBlock
                      language="json"
                       code={`{
                      "status": "error",
                      "code": "404",
                      "message": "Product not found",
                      "data": null
                      }`}
                      className="!m-0 !bg-transparent"/>
                    </div>
                  </div>
                  <section id="terms-definitions" className="scroll-mt-6">
                    <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center mt-8">
                      <Icon name="AlertCircle" size={24} className="mr-3 text-red-500" />
                      Error Response Parameters
                    </h2>
                    <p className="text-muted-foreground mb-6">
                      Below is a list of possible errors you may encounter while creating an order, 
                      along with their causes and suggested solutions.
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse rounded-xl overflow-hidden shadow-sm">
                        <thead>
                          <tr className="bg-red-50 border-b border-border">
                            <th className="px-6 py-3 text-left font-semibold text-red-700">Error</th>
                            <th className="px-6 py-3 text-left font-semibold text-red-700">Cause</th>
                            <th className="px-6 py-3 text-left font-semibold text-red-700">Solution</th>
                          </tr>
                        </thead>
                        <tbody>
                          {errorResponseParameters.slice(1).map((item, index) => (
                            <tr
                              key={`error-row-${index}`}
                              className={`transition-colors ${
                              index % 2 === 0 ? "bg-white" : "bg-red-50/40"
                              } hover:bg-red-100/50`}>
                                <td className="px-6 py-4 text-sm font-medium text-foreground">
                                  <code className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs">
                                    {item.term}
                                  </code>
                                </td>
                                <td className="px-6 py-4 text-sm text-muted-foreground">
                                  {item.definition}
                                </td>
                                <td className="px-6 py-4 text-sm text-muted-foreground">
                                  {item.solution}
                                </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                </section>
                
                {/* Brands API */}
                <section id="brands-api" className="scroll-mt-6">
                  <div className="flex items-center mb-6">
                    <div className="p-2 bg-primary/10 rounded-lg mr-3">
                      <Icon name="Package" size={26} className="text-primary"/>
                    </div>
                    <h2 className="text-3xl font-extrabold text-foreground">Brands</h2>
                  </div>
                  <p className="text-muted-foreground mb-8 text-base">
                    Easily fetch <span className="font-medium text-foreground">brand lists</span> and their details using the Lakeeshop APIs.
                  </p>
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-border rounded-2xl p-6 mb-8 shadow-sm">
                    <h3 className="text-lg font-semibold text-indigo-900 mb-3 flex items-center">
                      <Icon name="Link" size={20} className="mr-2 text-indigo-600" />
                      Base URL
                    </h3>
                    <div className="bg-slate-900 text-slate-100 rounded-lg px-4 py-3 font-mono text-sm shadow-inner">
                      https://lsorder.lakeetech.com
                    </div>
                  </div>
                  <div className="bg-card border border-border rounded-xl p-6 mb-10 shadow-sm">
                    <p className="text-muted-foreground leading-relaxed">
                      Lakeee Shop APIs are <span className="font-semibold text-foreground">fully RESTful</span>, returning data in JSON format with a 
                      <span className="font-semibold text-foreground"> reactive and asynchronous</span> approach.
                    </p>
                  </div>
                  <section id="brand-entity" className="scroll-mt-6">
                    <div className="flex items-center mb-6">
                      <div className="p-2 bg-purple-100 rounded-xl mr-3 shadow-sm">
                        <Icon name="BookText" size={22} className="text-purple-600" />
                      </div>
                      <h2 className="text-2xl font-extrabold text-foreground">Brand Entity</h2>
                    </div>
                      <p className="text-muted-foreground mb-8 text-base leading-relaxed">
                        The <span className="font-semibold text-foreground">brand entity</span> provides structured details 
                        about brands, including their <span className="text-foreground">ID</span>, 
                        <span className="text-foreground"> name</span>, and <span className="text-foreground">description</span>.
                      </p>
                      <div className="overflow-x-auto mb-10">
                        <table className="w-full border border-border rounded-xl shadow-sm overflow-hidden">
                          <thead className="bg-gradient-to-r from-purple-50 to-indigo-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-sm font-semibold text-foreground">Field</th>
                              <th className="px-6 py-3 text-left text-sm font-semibold text-foreground">Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            {brandEntity?.map((term, index) => (
                              <tr
                                key={`brand-entity-${index}`}
                                className={index % 2 === 0 ? "bg-background" : "bg-muted/20"}>
                                <td className="px-6 py-4 border-t border-border">
                                  <code className="bg-slate-100 px-2 py-1 rounded-md text-sm font-mono text-slate-800">
                                    {term?.term}
                                  </code>
                                </td>
                                <td className="px-6 py-4 border-t border-border text-sm text-muted-foreground">
                                  {term?.definition}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {/* Sample Response */}
                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 shadow-sm">
                        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                          <Icon name="Code" size={18} className="mr-2 text-primary" />
                          Sample Brand Entity
                        </h3>
                        <CodeBlock
                          language="json"
                          code={`[
                            {
                              "brand_id": 1,
                              "brand_name": "3M",
                              "description": "3M"
                            },
                            {
                              "brand_id": 2,
                              "brand_name": "Accu-Chek",
                              "description": "Blood Glucose Meter"
                            }
                          ]`}
                        />
                      </div>
                    </section>
                    {/* Fetch Brands */}
                    <section id="fetch-brands" className="scroll-mt-6 mt-10">
                      <div className="flex items-center mb-6">
                        <div className="p-2 bg-blue-100 rounded-lg mr-3 shadow-sm">
                          <Icon name="Package" size={22} className="text-blue-600" />
                        </div>
                        <h2 className="text-2xl font-extrabold text-foreground">Fetch Brands</h2>
                      </div>
                      <p className="text-muted-foreground mb-8 text-base leading-relaxed">
                        Use this endpoint to <span className="font-semibold text-foreground">retrieve details of all active brands</span>.
                      </p>
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 mb-8 shadow-sm">
                        <h3 className="text-lg font-semibold text-blue-800 flex items-center mb-4">
                          <Icon name="Lock" size={18} className="mr-2 text-blue-600" />
                          Authentication
                        </h3>
                        <p className="text-muted-foreground mb-4">
                          This API requires authentication using an <span className="font-medium text-foreground">API key</span>.
                          Include it in the request headers as follows:
                        </p>
                        <CodeBlock
                          language="json"
                          code={`Authorization: Bearer [TOKEN]`}
                          className="mb-2"
                        />
                      </div>
                      <div className="bg-card border border-border rounded-xl p-6 mb-10 shadow-sm">
                        <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center">
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-md mr-2">
                            GET
                          </span>
                          Retrieve All Brands
                        </h3>
                        <div className="bg-slate-100 rounded-md p-4">
                          <code className="text-slate-800 font-mono text-sm">/brand/active</code>
                        </div>
                      </div>
                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-10 shadow-sm">
                        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                          <Icon name="Terminal" size={18} className="mr-2 text-primary" />
                          Example CURL
                        </h3>
                        <CodeBlock
                          language="bash"
                          code={`curl -X 'GET' 
  'https://lsorder.lakeetech.com/brand/active' 
  -H 'accept: */*' 
  -H 'Authorization: Bearer [TOKEN]'`} />
                      </div>
                      {/* Sample Response */}
                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 shadow-sm">
                        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                          <Icon name="Code" size={18} className="mr-2 text-primary" />
                          Sample Success Response
                        </h3>
                        <CodeBlock
                          language="json"
                          code={`[
                            {
                              "brand_id": 1,
                              "brand_name": "3M",
                              "description": "3M"
                            },
                            {
                              "brand_id": 2,
                              "brand_name": "Accu-Chek",
                              "description": "Blood Glucose Meter"
                            }
                          ]
                        `}
                        />
                      </div>
                    </section>
                    
                    {/* Fetch Brand by ID */}
                    <section id="fetch-brand-id" className="scroll-mt-6 mt-12">
                      <div className="flex items-center mb-6">
                        <div className="p-2 bg-indigo-100 rounded-lg mr-3 shadow-sm">
                          <Icon name="Package" size={22} className="text-indigo-600" />
                        </div>
                        <h2 className="text-2xl font-extrabold text-foreground">Fetch Brand by ID</h2>
                      </div>
                      <p className="text-muted-foreground mb-8 leading-relaxed text-base">
                        Use this endpoint to <span className="font-semibold text-foreground">retrieve details of a specific brand</span>
                        by providing its <code className="px-1 py-0.5 bg-slate-100 rounded text-sm">brand_id</code>.
                      </p>
                      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-xl p-6 mb-10 shadow-sm">
                        <h3 className="text-lg font-semibold text-indigo-800 flex items-center mb-4">
                          <Icon name="Lock" size={18} className="mr-2 text-indigo-600" />
                          Authentication
                        </h3>
                        <p className="text-muted-foreground mb-4">
                          This API requires authentication using an API key.
                          Include it in the request headers like so:
                        </p>
                        <CodeBlock
                          language="json"
                          code={`Authorization: Bearer [TOKEN]`}
                        />
                      </div>
                      <div className="bg-card border border-border rounded-xl p-6 mb-12 shadow-sm">
                        <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center">
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-md mr-2">
                            GET
                          </span>
                          Endpoint
                        </h3>
                        <div className="bg-slate-100 rounded-md p-4">
                          <code className="text-slate-800 font-mono text-sm">/brand/:brand_id</code>
                        </div>
                      </div>
                      <section id="brand-path-parameter" className="scroll-mt-6 mb-12">
                          <div className="flex items-center mb-6">
                            <div className="p-2 bg-primary/10 rounded-lg mr-3">
                              <Icon name="BookText" size={22} className="text-primary" />
                            </div>
                            <h3 className="text-xl font-bold text-foreground">Path Parameter</h3>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full border border-border rounded-xl overflow-hidden shadow-sm">
                              <tbody>
                                {brandidpathParameter?.map((term, index) => (
                                  <tr
                                    key={`brand-path-${index}`}
                                    className={`transition-colors ${
                                    index % 2 === 0 ? "bg-background" : "bg-muted/20"
                                    } hover:bg-muted/40`}>
                                    <td className="border border-border px-6 py-4">
                                      <code className="bg-slate-100 px-2 py-1 rounded text-sm font-medium text-slate-800">
                                        {term?.term}
                                      </code>
                                    </td>
                                    <td className="border border-border px-6 py-4 text-sm text-muted-foreground">
                                      {term?.definition}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </section>
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-10 shadow-sm">
                          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                            <Icon name="Terminal" size={18} className="mr-2 text-primary" />
                            Example CURL
                          </h3>
                          <CodeBlock
                            language="bash"
                            code={`curl -X 'GET'
  'https://lsorder.lakeetech.com/brand/20'
  -H 'accept: */*' 
  -H 'Authorization: Bearer [TOKEN]'`}
    />
                        </div>
                        {/* Success Response */}
                        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 mb-10 shadow-sm">
                          <h3 className="text-lg font-semibold text-emerald-800 mb-4 flex items-center">
                            <Icon name="CheckCircle" size={18} className="mr-2 text-emerald-600" />
                            Sample Success Response
                          </h3>
                          <CodeBlock
                            language="json"
                            code={`{
                              "status": "success",
                              "code": "200",
                              "message": "Brand Details",
                              "data": {
                                "brand_id": 20,
                                "brand_name": "Style Sutra",
                                "description": "Sarees"
                              }
                            }`} />
                        </div>
                        {/* Error Response */}
                        <div className="bg-rose-50 border border-rose-200 rounded-xl p-6 shadow-sm">
                          <h3 className="text-lg font-semibold text-rose-800 mb-4 flex items-center">
                            <Icon name="XCircle" size={18} className="mr-2 text-rose-600" />
                            Failed Response
                          </h3>
                          <CodeBlock
                            language="json"
                            code={`{
                              "status": "error",
                              "code": "404",
                              "message": "Brand not found",
                              "data": null
                            }`}
                          />
                        </div>
                      </section>
                      {/* Response Parameters */}
                      <section id="brand-response-parameters" className="scroll-mt-6 mt-12">
                        <div className="flex items-center mb-6">
                          <div className="p-2 bg-primary/10 rounded-lg mr-3">
                            <Icon name="BookText" size={22} className="text-primary" />
                          </div>
                          <h2 className="text-2xl font-bold text-foreground">Response Parameters</h2>
                        </div>
                        <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
                          <div className="bg-gradient-to-r from-primary/10 to-primary/5 px-6 py-3 border-b border-border">
                            <h3 className="text-sm font-semibold text-primary">Fields</h3>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                              <tbody>
                                {responseParameters?.map((term, index) => (
                                  <tr
                                    key={`brand-path-${index}`}
                                    className={`transition-colors ${
                                      index % 2 === 0 ? "bg-background" : "bg-muted/20"
                                    } hover:bg-muted/40`}
                                  >
                                    <td className="px-6 py-4">
                                      <code className="bg-slate-100 px-2 py-1 rounded text-sm font-medium text-slate-800">
                                        {term?.term}
                                      </code>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-muted-foreground">
                                      <code className="bg-slate-100 px-2 py-1 rounded text-sm font-medium text-slate-800">
                                        {term?.definition}
                                      </code>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </section>
                      {/* Data Object */}
                      <section id="brand-data-object" className="scroll-mt-6 mt-12">
                        <div className="flex items-center mb-4">
                          <div className="p-2 bg-blue-100 rounded-lg mr-3">
                            <Icon name="Database" size={22} className="text-blue-600" />
                          </div>
                          <h2 className="text-2xl font-bold text-foreground">Data Object</h2>
                        </div>
                        <p className="text-muted-foreground mb-6 text-base">
                          The <span className="font-semibold text-foreground">data</span> object contains the following fields:
                        </p>
                        <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
                          <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-6 py-3 border-b border-border">
                            <h3 className="text-sm font-semibold text-blue-600">Object Fields</h3>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                              <tbody>
                                {dataObject?.map((term, index) => (
                                <tr
                                  key={`brand-data-${index}`}
                                  className={`transition-colors ${
                                    index % 2 === 0 ? "bg-background" : "bg-muted/20"
                                  } hover:bg-muted/40`}
                                  >
                                  <td className="px-6 py-4">
                                    <code className="bg-slate-100 px-2 py-1 rounded text-sm font-medium text-slate-800">
                                      {term?.term}
                                    </code>
                                  </td>
                                  <td className="px-6 py-4 text-sm text-muted-foreground">
                                    {term?.definition}
                                  </td>
                                </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </section>
                    {/* Error Response Parameters */}
                    <section id="brand-error-response" className="scroll-mt-6 mt-12">
                      <div className="flex items-center mb-6">
                        <div className="p-2 bg-rose-100 rounded-lg mr-3">
                          <Icon name="AlertTriangle" size={22} className="text-rose-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-foreground">Error Response Parameters</h2>
                      </div>
                      <p className="text-muted-foreground mb-6">
                        Possible errors you may encounter while fetching brand details:
                      </p>
                      <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
                        <div className="bg-gradient-to-r from-rose-50 to-rose-100 px-6 py-3 border-b border-border">
                          <h3 className="text-sm font-semibold text-rose-600">Errors</h3>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse">
                            <tbody>
                              {errorResponseParameters?.map((item, index) => (
                                <tr
                                  key={`brand-error-${index}`}
                                  className={`transition-colors ${
                                    index % 2 === 0 ? "bg-background" : "bg-muted/20"
                                  } hover:bg-muted/40`}
                                >
                                  <td className="px-6 py-4 text-sm font-medium text-foreground">
                                    {item.term}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-muted-foreground">
                                    {item.definition}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-muted-foreground">
                                    {item.solution}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </section>
                  </section>
                  {/* Category API */}
                  <section id="categories-api" className="scroll-mt-6 mt-10">
                    <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center">
                      <Icon name="FolderTree" size={28} className="mr-3 text-primary" />
                      Category API
                    </h2>
                    <p className="text-muted-foreground mb-4">
                      Fetch category details seamlessly using <span className="font-semibold text-foreground">Lakeee Shop APIs</span>.
                    </p>
                    <div className="grid gap-6 md:grid-cols-2">
                      <div className="bg-gradient-to-r from-indigo-50 via-white to-indigo-50 border border-border rounded-xl p-6 shadow-sm">
                        <h3 className="text-lg font-semibold text-foreground flex items-center mb-4">
                          <Icon name="Link" size={20} className="mr-2 text-primary" />
                          Base URL
                        </h3>
                        <div className="bg-slate-900 text-slate-100 rounded-lg px-4 py-3 font-mono text-sm overflow-x-auto">
                          <code>https://lsorder.lakeetech.com</code>
                        </div>
                      </div>
                      <div className="bg-gradient-to-r from-emerald-50 via-white to-emerald-50 border border-border rounded-xl p-6 shadow-sm">
                        <h3 className="text-lg font-semibold text-foreground flex items-center mb-4">
                          <Icon name="Terminal" size={20} className="mr-2 text-primary" />
                          Method
                        </h3>
                        <span className="inline-block bg-emerald-600 text-white text-sm font-semibold px-3 py-1 rounded-full shadow">
                          GET
                        </span>
                      </div>
                    </div>
                    <div className="mt-8 space-y-4">
                      <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
                        <h4 className="text-lg font-semibold text-foreground mb-2">About</h4>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Lakeee Shop APIs are <span className="font-medium text-foreground">fully RESTful</span>,
                          returning responses in JSON with a <span className="italic">reactive and asynchronous</span> approach.
                        </p>
                      </div>
                      <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
                        <h4 className="text-lg font-semibold text-foreground mb-2">What You Get</h4>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          The <span className="font-medium text-foreground">Category API</span> helps retrieve detailed
                          category information, including <code className="bg-slate-100 px-2 py-0.5 rounded">parentCategoryId</code> 
                          and <code className="bg-slate-100 px-2 py-0.5 rounded">activeStatus</code>.
                        </p>
                      </div>
                    </div>
                  </section>
                  {/* Category Entity */}
                  <section id="category-entity" className="scroll-mt-6 mt-10">
                    <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center">
                      <Icon name="BookText" size={28} className="mr-3 text-primary" />
                      Category Entity
                    </h2>
                    <p className="text-muted-foreground mb-6">
                      The category entity contains the following fields that define category structure:
                    </p>
                    <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/40">
                          <tr>
                            <th className="px-6 py-3 text-left font-semibold text-foreground">Field</th>
                            <th className="px-6 py-3 text-left font-semibold text-foreground">Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {categoryEntity?.map((term, index) => (
                            <tr
                              key={`category-entity-${index}`}
                              className={index % 2 === 0 ? "bg-background" : "bg-muted/10"}
                            >
                              <td className="px-6 py-4 align-top">
                                <code className="bg-slate-100 px-2 py-1 rounded text-xs font-medium text-slate-800">
                                  {term?.term}
                                </code>
                              </td>
                              <td className="px-6 py-4 text-muted-foreground">
                                {term?.definition}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-8">
                      <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center">
                        <Icon name="Code2" size={20} className="mr-2 text-primary" />
                        Sample Category Entity
                      </h3>
                      <CodeBlock
                        language="json"
                        code={categorySampleEntity}
                        className="rounded-xl border border-border shadow-sm bg-slate-900 text-slate-100"
                      />
                    </div>
                  </section>

                  <section id="fetch-active-category" className="scroll-mt-6 mt-12">
                    <div className="flex items-center mb-6">
                      <Icon name="FolderOpen" size={28} className="mr-3 text-primary" />
                      <h2 className="text-2xl font-bold text-foreground">Fetch Active Category</h2>
                    </div>
                    <p className="text-muted-foreground mb-8">
                      Use this endpoint to retrieve details of all <span className="font-medium text-foreground">active product categories</span>.
                    </p>
                    <div className="bg-card border border-border rounded-xl shadow-sm p-6 mb-10">
                      <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center">
                        <Icon name="KeyRound" size={20} className="mr-2 text-primary" />
                        Authentication
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        This API requires authentication using an API key. Include your API key in the request headers:
                      </p>
                      <CodeBlock
                        language="json"
                        code={`Authorization: Bearer [TOKEN]`}
                        className="rounded-lg"
                      />
                    </div>
                    <div className="bg-card border border-border rounded-xl shadow-sm p-6 mb-10">
                      <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center">
                        <Icon name="Globe" size={20} className="mr-2 text-primary" />
                        Endpoint
                      </h3>
                      <p className="text-muted-foreground mb-4">Retrieve all active product categories.</p>
                      <div className="bg-slate-100 rounded-lg p-4 flex items-center justify-between">
                        <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-1 rounded-md mr-3">GET</span>
                        <code className="text-slate-800 font-mono text-sm">/category/active</code>
                      </div>
                    </div>
                    <div className="mb-10">
                      <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center">
                        <Icon name="Terminal" size={20} className="mr-2 text-primary" />
                        Example Request
                      </h3>
                      <CodeBlock
                      language="bash"
                      code={`curl -X GET 
'https://lsorder.lakeetech.com/category/active' 
-H 'accept: */*' 
-H 'Authorization: Bearer [TOKEN]'`}
      className="rounded-lg"
    />
                    </div>
                    {/* Sample Success Response */}
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center">
                        <Icon name="CheckCircle2" size={20} className="mr-2 text-primary" />
                        Sample Success Response
                      </h3>
                      <CodeBlock
                        language="json"
                        code={categoryActiveSample}
                        className="rounded-lg border border-border shadow-sm bg-slate-900 text-slate-100"
                      />
                    </div>
                  </section>

                  <section id="fetch-category-parent" className="scroll-mt-6 mt-12">
                    <div className="flex items-center mb-6">
                      <Icon name="FolderSearch" size={28} className="mr-3 text-primary" />
                      <h2 className="text-2xl font-bold text-foreground">Fetch Category by Parent ID</h2>
                    </div>
                    <p className="text-muted-foreground mb-8">
                      Use this endpoint to retrieve category details using a specific 
                      <code className="bg-slate-100 px-1 py-0.5 rounded text-xs ml-1">parent_id</code>.
                    </p>
                    <div className="bg-card border border-border rounded-xl shadow-sm p-6 mb-10">
                      <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center">
                        <Icon name="KeyRound" size={20} className="mr-2 text-primary" />
                        Authentication
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        This API requires authentication using an API key. Include your API key in the request headers:
                      </p>
                      <CodeBlock
                      language="json"
                      code={`Authorization: Bearer [TOKEN]`}
                      className="rounded-lg"
                      />
                    </div>
                    <div className="bg-card border border-border rounded-xl shadow-sm p-6 mb-10">
                      <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center">
                        <Icon name="Globe" size={20} className="mr-2 text-primary" />
                        Endpoint
                      </h3>
                      <p className="text-muted-foreground mb-4">Retrieve product categories based on <code>parent_id</code>.</p>
                      <div className="bg-slate-100 rounded-lg p-4 flex items-center justify-between">
                        <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-1 rounded-md mr-3">GET</span>
                        <code className="text-slate-800 font-mono text-sm">/category/parent_id/:parent_id</code>
                      </div>
                    </div>
                    <section id="category-path-parameter" className="scroll-mt-6 mb-10">
                      <h3 className="text-xl font-bold text-foreground mb-4 flex items-center">
                        <Icon name="BookText" size={22} className="mr-2 text-primary" />
                        Path Parameter
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-border rounded-lg shadow-sm">
                          <tbody>
                            {categoryPathParameter?.map((term, index) => (
                              <tr
                                key={`category-path-${index}`}
                                className={index % 2 === 0 ? "bg-background" : "bg-muted/20"}
                              >
                                <td className="border border-border px-6 py-3">
                                  <code className="bg-slate-100 px-2 py-1 rounded text-sm font-medium text-slate-800">
                                    {term?.term}
                                  </code>
                                </td>
                                <td className="border border-border px-6 py-3 text-sm text-muted-foreground">
                                  {term?.definition}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </section>
                    <div className="mb-10">
                      <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center">
                        <Icon name="Terminal" size={20} className="mr-2 text-primary" />
                        Example Request
                      </h3>
                      <CodeBlock
                      language="bash"
                      code={`curl -X GET 
'https://lsorder.lakeetech.com/category/parent_id/1' 
-H 'accept: */*' 
-H 'Authorization: Bearer [TOKEN]'`}
      className="rounded-lg"
    />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center">
                        <Icon name="CheckCircle2" size={20} className="mr-2 text-primary" />
                        Sample Success Response
                      </h3>
                      <CodeBlock
                        language="json"
                        code={categoryParentSample}
                        className="rounded-lg border border-border shadow-sm bg-slate-900 text-slate-100"
                      />
                    </div>
                  </section>
                  <section id="fetch-category-id" className="scroll-mt-6">
                    <div className="mb-10">
                      <h2 className="text-2xl font-extrabold flex items-center gap-3 text-black">
                        <Icon name="FolderOpen" size={28} className="text-primary" />
                        Fetch Category by Category ID
                      </h2>
                      <p className="text-muted-foreground mt-3 text-base">
                        Retrieve detailed information of a specific category using its unique
                        <code className="bg-slate-100 px-2 py-1 rounded text-sm mx-1">category_id</code>.
                      </p>
                    </div>
                    <section className="mb-10">
                      <h3 className="text-xl font-semibold flex items-center gap-2 mb-3">
                        <Icon name="ShieldCheck" size={22} className="text-green-600" />
                        Authentication
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        This API requires an API key. Include it in the request header as shown:
                      </p>
                      <div className="rounded-xl border bg-slate-50 p-4">
                        <CodeBlock language="json" code={`Authorization: Bearer [TOKEN]`} />
                      </div>
                    </section>
                    <section className="mb-10">
                      <h3 className="text-xl font-semibold flex items-center gap-2 mb-3">
                        <Icon name="Server" size={22} className="text-blue-600" />
                        Endpoint
                      </h3>
                      <p className="text-muted-foreground mb-4">Use the following endpoint:</p>
                      <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
                        <span className="inline-block text-xs font-bold uppercase text-green-700 bg-green-100 px-2 py-1 rounded">
                          GET
                        </span>
                        <div className="mt-3 bg-slate-100 rounded-lg p-4">
                          <code className="text-slate-800 font-mono">category/:category_id</code>
                        </div>
                      </div>
                    </section>
                    <section className="mb-10">
                      <h3 className="text-xl font-semibold flex items-center gap-2 mb-3">
                        <Icon name="BookText" size={22} className="text-purple-600" />
                        Path Parameters
                      </h3>
                      <div className="overflow-x-auto rounded-lg border">
                        <table className="w-full border-collapse">
                          <thead className="bg-muted/30">
                            <tr>
                              <th className="border px-6 py-3 text-left text-sm font-medium">Parameter</th>
                              <th className="border px-6 py-3 text-left text-sm font-medium">Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            {categoryidParameter?.map((term, index) => (
                            <tr
                              key={`category-path-${index}`}
                              className={index % 2 === 0 ? "bg-background" : "bg-muted/20"}
                              >
                                <td className="border px-6 py-4">
                                  <code className="bg-slate-100 px-2 py-1 rounded text-sm font-medium text-slate-800">
                                    {term?.term}
                                  </code>
                                </td>
                                <td className="border px-6 py-4 text-sm text-muted-foreground">
                                  {term?.definition}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </section>

                    <section className="mb-10">
                      <h3 className="text-xl font-semibold flex items-center gap-2 mb-3">
                        <Icon name="Terminal" size={22} className="text-slate-700" />
                        Example Request
                      </h3>
                      <CodeBlock
                        language="CURL"
                        code={`curl -X 'GET' 
'https://lsorder.lakeetech.com/category/1' 
-H 'accept: */*' 
-H 'Authorization: Bearer [TOKEN]'`}
    />
                    </section>
                    <section>
                      <h3 className="text-xl font-semibold flex items-center gap-2 mb-3">
                        <Icon name="Code" size={22} className="text-orange-500" />
                        Sample Success Response
                      </h3>
                      <CodeBlock
                      title="Success Response"
                      language="json"
                      code={categoryIdSample}
                      className="mb-6 mt-4"
                      />
                    </section>
                  </section>
                  
                  <section id="fetch-category-level" className="scroll-mt-6">
                    <div className="mb-10">
                      <h2 className="text-2xl font-extrabold flex items-center gap-3 text-black">
                        <Icon name="Layers" size={28} className="text-primary" />
                        Fetch Category by Level
                      </h2>
                      <p className="text-muted-foreground mt-3 text-base">
                        Retrieve all categories belonging to a specific
                        <code className="bg-slate-100 px-2 py-1 rounded text-sm mx-1">level</code>.
                      </p>
                    </div>
                    <section className="mb-10">
                      <h3 className="text-xl font-semibold flex items-center gap-2 mb-3">
                        <Icon name="ShieldCheck" size={22} className="text-green-600" />
                        Authentication
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        Include your API key in the request header as shown:
                      </p>
                      <div className="rounded-xl border bg-slate-50 p-4">
                        <CodeBlock language="json" code={`Authorization: Bearer [TOKEN]`} />
                      </div>
                    </section>
                    <section className="mb-10">
                      <h3 className="text-xl font-semibold flex items-center gap-2 mb-3">
                        <Icon name="Server" size={22} className="text-blue-600" />
                        Endpoint
                      </h3>
                      <p className="text-muted-foreground mb-4">Use the following endpoint to fetch categories:</p>
                      <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
                        <span className="inline-block text-xs font-bold uppercase text-green-700 bg-green-100 px-2 py-1 rounded">
                          GET
                        </span>
                        <div className="mt-3 bg-slate-100 rounded-lg p-4">
                          <code className="text-slate-800 font-mono">category/level/:level</code>
                        </div>
                      </div>
                    </section>
                    
                    <section className="mb-10">
                      <h3 className="text-xl font-semibold flex items-center gap-2 mb-3">
                        <Icon name="BookText" size={22} className="text-purple-600" />
                        Path Parameters
                      </h3>
                      <div className="overflow-x-auto rounded-lg border shadow-sm">
                        <table className="w-full border-collapse">
                          <thead className="bg-muted/30">
                            <tr>
                              <th className="border px-6 py-3 text-left text-sm font-medium">Parameter</th>
                              <th className="border px-6 py-3 text-left text-sm font-medium">Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            {categorylevelParameter?.map((term, index) => (
                              <tr
                              key={`category-level-${index}`}
                              className={index % 2 === 0 ? "bg-background" : "bg-muted/20"}
                              >
                                <td className="border px-6 py-4">
                                  <code className="bg-slate-100 px-2 py-1 rounded text-sm font-medium text-slate-800">
                                    {term?.term}
                                  </code>
                                </td>
                                <td className="border px-6 py-4 text-sm text-muted-foreground">
                                  {term?.definition}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </section>
                    
                    <section className="mb-10">
                      <h3 className="text-xl font-semibold flex items-center gap-2 mb-3">
                        <Icon name="Terminal" size={22} className="text-slate-700" />
                        Example Request
                      </h3>
                      <CodeBlock
      language="CURL"
      code={`curl -X 'GET' 
'https://lsorder.lakeetech.com/category/level/2' 
-H 'accept: */*' 
-H 'Authorization: Bearer [TOKEN]'`}
                      />
                    </section>
                  </section>

                  <section>
                    <h3 className="text-xl font-semibold flex items-center gap-2 mb-3">
                      <Icon name="Code" size={22} className="text-orange-500" />
                      Sample Success Response
                    </h3>
                    <CodeBlock
                      title="Success Response"
                      language="json"
                      code={categoryLevelSample}
                      className="mb-6 mt-4"
                    />
                  </section>

                  {/* Create Category */}
                  <section id="create-category" className="scroll-mt-6">
                    <div className="mb-10">
                      <h2 className="text-2xl font-extrabold flex items-center gap-3 text-black">
                        <Icon name="PlusCircle" size={28} className="text-primary" />
                        Create Category
                      </h2>
                      <p className="text-muted-foreground mt-3 text-base">
                        Use this endpoint to <span className="font-medium text-foreground">create a new product category</span>.
                      </p>
                    </div>
                    <section className="mb-10">
                      <h3 className="text-xl font-semibold flex items-center gap-2 mb-3">
                        <Icon name="ShieldCheck" size={22} className="text-green-600" />
                        Authentication
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        This API requires an API key. Include it in the request headers:
                      </p>
                      <div className="rounded-xl border bg-slate-50 p-4 shadow-sm">
                        <CodeBlock language="json" 
                        code={`Authorization: Bearer [TOKEN]`} />
                      </div>
                    </section>
                    
                    <section className="mb-10">
                      <h3 className="text-xl font-semibold flex items-center gap-2 mb-3">
                        <Icon name="Server" size={22} className="text-blue-600" />
                        Endpoint
                      </h3>
                      <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
                        <span className="inline-block text-xs font-bold uppercase text-pink-700 bg-pink-100 px-2 py-1 rounded">
                          POST
                        </span>
                        <div className="mt-3 bg-slate-100 rounded-lg p-4">
                          <code className="text-slate-800 font-mono">/category</code>
                        </div>
                      </div>
                    </section>
                    <section className="mb-10">
                      <h3 className="text-xl font-semibold flex items-center gap-2 mb-3">
                        <Icon name="Terminal" size={22} className="text-slate-700" />
                        Example Request
                      </h3>
                      <CodeBlock
                      language="CURL"
                      code={`curl -X 'POST' 
'https://lsorder.lakeetech.com/category'
-H 'accept: */*'
-H 'Authorization: Bearer [TOKEN]' 
-H 'Content-Type: application/json'
-d '{
  "category_id": 2,
  "category": "Test Cat",
  "description": "Test Description",
  "parent_category_id": 133,
  "level": 2,
  "levels": 3
}'`}
    />
                      </section>
                      
                      <section>
                        <h3 className="text-xl font-semibold flex items-center gap-2 mb-3">
                          <Icon name="FileJson" size={22} className="text-orange-500" />
                          Request Payload
                        </h3>
                        <CodeBlock
                        language="json"
                        code={`{
                          "category_id": 2,
                          "category": "Test Cat",
                          "description": "Test Description",
                          "parent_category_id": 133,
                          "level": 2,
                          "levels": 3
                        }`}
                        className="mb-14 mt-4"
                      />
                    </section>
                    <section id="category-entity" className="scroll-mt-12">
                      <h2 className="text-2xl font-extrabold flex items-center gap-3 text-black mb-4">
                        <Icon name="Layers" size={28} className="text-primary" />
                        Category Entity
                      </h2>
                      <p className="text-muted-foreground mb-6">
                        The <span className="font-medium text-foreground">Category Entity</span> defines all fields required for creating and managing categories.
                      </p>
                      <div className="rounded-xl border bg-card shadow-sm overflow-hidden mb-10">
                        <table className="w-full border-collapse">
                          <thead className="bg-muted/40">
                            <tr>
                              <th className="text-left px-6 py-3 font-semibold text-sm text-foreground">Field</th>
                              <th className="text-left px-6 py-3 font-semibold text-sm text-foreground">Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            {createCategoryEntity?.map((term, index) => (
                              <tr
                              key={`category-entity-${index}`}
                              className={index % 2 === 0 ? "bg-background" : "bg-muted/20"}
                              >
                                <td className="px-6 py-4">
                                  <code className="bg-slate-100 px-2 py-1 rounded text-xs font-medium text-slate-800">
                                    {term?.term}
                                  </code>
                                </td>
                                <td className="px-6 py-4 text-sm text-muted-foreground">
                                  {term?.definition}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="space-y-8">
                        <CodeBlock
                          title="📦 Sample Request Body"
                          language="json"
                          code={categoryCreateRequest}
                        />
                        <CodeBlock
                          title="✅ Sample Success Response"
                          language="json"
                          code={categoryCreateSuccess}
                          />
                          <CodeBlock
                            title="❌ Sample Error Response"
                            language="json"
                            code={categoryCreateError}
                          />
                        </div>
                      </section>
                      
                      <section id="response-parameters" className="scroll-mt-10 mt-12">
                        <h3 className="text-2xl font-bold flex items-center gap-2 mb-4">
                          <Icon name="ClipboardList" size={24} className="text-blue-600" />
                          Response Parameters
                        </h3>
                        <p className="text-muted-foreground mb-8">
                          Response parameters for this API follow the same structure as in the 
                          <span className="font-medium">Order Entity</span>.
                        </p>
                      </section>
                      
                      <section id="error-response" className="scroll-mt-10 mt-12">
                        <h2 className="text-2xl font-bold flex items-center gap-3 mb-4">
                          <Icon name="AlertTriangle" size={26} className="text-red-600" />
                          Error Response Parameters
                        </h2>
                        <p className="text-muted-foreground mb-6">
                          Below are possible errors you may encounter while creating a category:
                        </p>
                        <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
                          <table className="w-full border-collapse">
                            <thead className="bg-red-50">
                              <tr>
                                <th className="px-6 py-3 text-left font-semibold text-sm text-foreground">Error</th>
                                <th className="px-6 py-3 text-left font-semibold text-sm text-foreground">Cause</th>
                                <th className="px-6 py-3 text-left font-semibold text-sm text-foreground">Solution</th>
                              </tr>
                            </thead>
                            <tbody>
                              {errorResponseParameters.slice(1).map((item, index) => (
                                <tr
                                key={`error-row-${index}`}
                                className={index % 2 === 0 ? "bg-background" : "bg-muted/20"}
                                >
                                  <td className="px-6 py-4 text-sm font-medium text-foreground">
                                    {item.term}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-muted-foreground">
                                    {item.definition}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-muted-foreground">
                                    {item.solution}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </section>
                    </section>
                    
                    {/* Pincode API */}
                    <section id="pincode-api" className="scroll-mt-6 space-y-10">
                      <div className="flex items-center gap-3">
                        <Icon name="MapPin" size={30} className="text-primary" />
                        <h2 className="text-3xl font-bold text-foreground">Pincode Service Availability</h2>
                      </div>
                      <p className="text-muted-foreground text-lg leading-relaxed">
                        Fetch pincode service availability using <span className="font-semibold text-primary">Lakeeshop APIs</span>.
                      </p>
                      <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-xl shadow-md p-6">
                        <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center">
                          🌐 Base URL
                        </h3>
                        <code className="bg-slate-100 text-slate-800 px-3 py-2 rounded-lg block font-mono text-sm">
                          https://lsorder.lakeetech.com
                        </code>
                      </div>
                      <div className="bg-card border border-border rounded-xl shadow-sm p-6">
                        <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                          <Icon name="Lock" size={20} className="text-primary" />
                          Authentication
                        </h3>
                        <p className="text-muted-foreground mb-4">
                          This API requires authentication using an API key. Include it in the request headers:
                        </p>
                        <CodeBlock language="json" code={`Authorization: Bearer [TOKEN]`} />
                      </div>
                      <div className="bg-card border border-border rounded-xl shadow-sm p-6">
                        <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                          <Icon name="GitPullRequest" size={20} className="text-primary" />
                          Endpoint
                        </h3>
                        <p className="text-muted-foreground mb-4">
                          Use the following endpoint to validate pincode service availability:
                        </p>
                        <div className="flex items-center gap-3 bg-slate-100 rounded-lg p-3 mb-4 w-fit">
                          <span className="px-2 py-1 text-xs font-bold text-green-700 bg-green-100 rounded">GET</span>
                          <code className="text-slate-800 font-mono">/pincode/validate/:pincode</code>
                        </div>
                        <CodeBlock
                        language="CURL"
                        code={`curl -X 'GET' 
'https://lsorder.lakeetech.com/pincode/validate/600002'
-H 'accept: */*' 
-H 'Authorization: Bearer [TOKEN]'`}
    />
                      </div>
                      <div className="bg-card border border-border rounded-xl shadow-sm p-6">
                        <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                          <Icon name="BookText" size={20} className="text-primary" />
                          Path Parameters
                        </h3>
                        <table className="w-full border-collapse rounded-lg overflow-hidden shadow-sm">
                          <tbody>
                            {pincodePathParameter?.map((term, index) => (
                              <tr
                              key={`pincodepath-${index}`}
                              className={`transition-colors ${
                                index % 2 === 0 ? "bg-background" : "bg-muted/20"
                              } hover:bg-primary/5`}
                              >
                                <td className="border border-border px-6 py-3">
                                  <code className="bg-slate-100 px-2 py-1 rounded text-sm font-medium text-slate-800">
                                    {term?.term}
                                  </code>
                                </td>
                                <td className="border border-border px-6 py-3 text-sm text-muted-foreground">
                                  {term?.definition}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="grid md:grid-cols-2 gap-6">
                        <CodeBlock
                          title="✅ Success Response"
                          language="json"
                          code={`
                            {"status": "success",
                            "code": "200",
                            "message": "Pincode Serviceable"
                            }`}
                            className="shadow-md rounded-lg"
                          />
                            <CodeBlock
                              title="❌ Failed Response"
                              language="json"
                              code={`{
                                "status": "fail",
                                "code": "404",
                                "message": "Pincode not serviceable"
                                }`}
                                className="shadow-md rounded-lg"
                            />
                        </div>
                        <section id="brand-path-parameter" className="scroll-mt-6">
                          <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center mt-10">
                            <Icon name="BookText" size={28} className="mr-3 text-primary" />
                            Response Parameters
                          </h2>
                          <div className="overflow-x-auto mb-10 shadow-md rounded-2xl border border-border bg-card">
                            <table className="w-full text-left border-collapse">
                              <thead>
                                <tr className="bg-muted/40 text-sm uppercase tracking-wide text-foreground">
                                  <th className="px-6 py-4 font-semibold">Parameter</th>
                                  <th className="px-6 py-4 font-semibold">Description</th>
                                </tr>
                              </thead>
                              <tbody>
                                {responseParameters?.map((term, index) => (
                                  <tr
                                    key={`brand-path-${index}`}
                                    className={`transition-colors ${
                                      index % 2 === 0 ? "bg-background" : "bg-muted/10"
                                    } hover:bg-muted/30`}
                                    >
                                      <td className="px-6 py-4">
                                        <code className="bg-slate-100 px-2 py-1 rounded-md text-sm font-medium text-slate-800">
                                          {term?.term}
                                        </code>
                                      </td>
                                      <td className="px-6 py-4 text-sm text-muted-foreground">
                                        {term?.definition}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </section>
                          {/* Error Response Parameters */}
                          <section id="pincode-error" className="scroll-mt-6">
                            <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center mt-10">
                              <Icon name="BookText" size={28} className="mr-3 text-destructive" />
                              Error Response Parameters
                            </h2>
                            <p className="text-muted-foreground mb-8">
                              Below are the possible errors you may face while fetching pincode service availability.
                            </p>
                            <div className="overflow-x-auto shadow-md rounded-2xl border border-border bg-card">
                              <table className="w-full text-left border-collapse">
                                <thead>
                                  <tr className="bg-destructive/10 text-sm uppercase tracking-wide text-foreground">
                                    <th className="px-6 py-4 font-semibold">Error</th>
                                    <th className="px-6 py-4 font-semibold">Cause</th>
                                    <th className="px-6 py-4 font-semibold">Solution</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {pincodeErrorResponseParameters?.map((item, index) => (
                                    <tr
                                      key={`pincode-error-row-${index}`}
                                      className={`transition-colors ${
                                        index % 2 === 0 ? "bg-background" : "bg-muted/10"
                                      } hover:bg-destructive/5`}
                                    >
                                      <td className="px-6 py-4 text-sm font-medium text-red-600 flex items-center gap-2">
                                        <Icon name="AlertCircle" size={18} className="text-red-500" />
                                        {item.term}
                                      </td>
                                      <td className="px-6 py-4 text-sm text-muted-foreground">
                                        {item.definition}
                                      </td>
                                      <td className="px-6 py-4 text-sm text-emerald-600 font-medium">
                                        {item.solution}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </section>
                        </section>
                        {/* Product Stock Validate */}
                        <section id="stock-api" className="scroll-mt-6">
                          <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center">
                            <Icon name="Box" size={28} className="mr-3 text-primary" />
                            Product Stock Validate
                          </h2>
                          <p className="text-muted-foreground mb-6">
                            Check stock availability status using Lakeee Shop APIs.
                          </p>
                          <p className="text-muted-foreground mb-6">
                            Base URL for accessing the product stock API:
                          </p>
                          <div className="bg-card border border-border rounded-2xl p-6 mb-6 shadow-md">
                            <h3 className="text-lg font-semibold text-foreground mb-4">URL</h3>
                            <div className="bg-slate-100 rounded-lg p-4 mb-4">
                              <code className="text-slate-800 font-mono">https://lsorder.lakeetech.com</code>
                            </div>
                          </div>
                          <p className="text-muted-foreground mb-4">
                            Lakeee Shop APIs are fully RESTful and return JSON responses asynchronously.
                          </p>
                          <p className="text-muted-foreground mb-4">
                            Use product code and quantity to fetch stock availability status.
                          </p>
                          <section id="stock-auth" className="scroll-mt-6 mb-8">
                            <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                              <Icon name="Key" size={20} className="text-yellow-500" />
                              Authentication
                            </h3>
                            <p className="text-muted-foreground mb-6">
                              Include your API key in request headers:
                            </p>
                            <CodeBlock language="json" code={`Authorization: Bearer [TOKEN]`} />
                          </section>
                          <section id="stock-endpoint" className="scroll-mt-6 mb-8">
                            <h3 className="text-xl font-bold text-foreground mb-2 flex items-center gap-2">
                              <Icon name="Server" size={20} className="text-blue-500" />
                              Endpoint
                            </h3>
                            <div className="bg-card border border-border rounded-2xl p-6 shadow-sm">
                              <h3 className="text-lg font-semibold text-foreground mb-4">GET</h3>
                              <div className="bg-slate-100 rounded-lg p-4 mb-4 break-all">
                                <code className="text-slate-800 font-mono">
                                  /product/stock_validate/product_code/:product_code/quantity/:quantity
                                </code>
                              </div>
                            </div>
                          </section>
                          <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 shadow-sm mb-8">
                            <h3 className="text-lg font-semibold text-orange-800 flex items-center mb-4">
                              <Icon name="BookText" size={20} className="mr-2 text-orange-600" />
                              Path Parameters
                            </h3>
                            <div className="overflow-x-auto mb-6">
                              <table className="w-full border-collapse border border-border rounded-lg">
                                <tbody>
                                  {stockPathParameters?.map((param, index) => (
                                    <tr
                                    key={`stock-path-${index}`}
                                    className={index % 2 === 0 ? 'bg-background' : 'bg-muted/20'}
                                    >
                                      <td className="border border-border px-6 py-4">
                                        <code className="bg-slate-100 px-2 py-1 rounded text-sm font-medium text-slate-800">
                                          {param.term}
                                        </code>
                                    </td>
                                      <td className="border border-border px-6 py-4 text-sm text-muted-foreground">
                                        {param.definition}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            <CodeBlock
                              language="bash"
                              code={`curl -X 'GET' 
'https://lsorder.lakeetech.com/product/stock_validate/product_code/MTA-MOA-EPH-0368/quantity/2' 
-H 'accept: */*' 
-H 'Authorization: Bearer [TOKEN]'`}
  />
                          </div>
                          {/* Sample Responses */}
                          <CodeBlock
                            title="Sample Success Response"
                            language="json"
                            code={`{
                              "status": "success",
                              "code": "200",
                              "message": "Product exists"
                            }`}
                            className="mt-6 mb-6"
                          />
                          <CodeBlock
                            title="Sample Failed Response"
                            language="json"
                            code={`{
                              "status": "success",
                              "code": "200",
                              "message": "Product not found"
                            }`}
                            className="mb-8"
                          />
                          
                          {/* Response Parameters */}
                          <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 shadow-sm mb-8">
                            <h3 className="text-lg font-semibold text-orange-800 flex items-center mb-4">
                              <Icon name="BookText" size={20} className="mr-2 text-orange-600" />
                              Response Parameters
                            </h3>
                            <div className="overflow-x-auto mb-6">
                              <table className="w-full border-collapse border border-border rounded-lg">
                                <tbody>
                                  {stockResponseParameters?.map((term, index) => (
                                    <tr
                                      key={`stock-response-${index}`}
                                      className={index % 2 === 0 ? 'bg-background' : 'bg-muted/20'}
                                    >
                                      <td className="border border-border px-6 py-4">
                                        <code className="bg-slate-100 px-2 py-1 rounded text-sm font-medium text-slate-800">
                                          {term?.term}
                                        </code>
                                      </td>
                                      <td className="border border-border px-6 py-4 text-sm text-muted-foreground">
                                        {term?.definition}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* Sample Responses */}
                          <CodeBlock
                            title="Sample Success Response"
                            language="json"
                            code={`{
                              "status": "success",
                              "code": "200",
                              "message": "Product exists"
                            }`}
                            className="mt-6 mb-6"
                          />
                          <CodeBlock
                            title="Sample Failed Response"
                            language="json"
                            code={`{
                              "status": "success",
                              "code": "200",
                              "message": "Product not found"
                            }`}
                            className="mb-8"
                          />

                          {/* Response Parameters */}
                          <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 shadow-sm mb-8">
                            <h3 className="text-lg font-semibold text-orange-800 flex items-center mb-4">
                              <Icon name="BookText" size={20} className="mr-2 text-orange-600" />
                              Response Parameters
                            </h3>
                            <div className="overflow-x-auto mb-6">
                              <table className="w-full border-collapse border border-border rounded-lg">
                                <tbody>
                                  {stockResponseParameters?.map((term, index) => (
                                    <tr
                                      key={`stock-response-${index}`}
                                      className={index % 2 === 0 ? 'bg-background' : 'bg-muted/20'}
                                    >
                                      <td className="border border-border px-6 py-4">
                                        <code className="bg-slate-100 px-2 py-1 rounded text-sm font-medium text-slate-800">
                                          {term?.term}
                                        </code>
                                      </td>
                                      <td className="border border-border px-6 py-4 text-sm text-muted-foreground">
                                        {term?.definition}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                          {/* Error Response Parameters */}
                          <div className="bg-red-50 border border-red-200 rounded-xl p-6 shadow-sm mb-8">
                            <h3 className="text-lg font-semibold text-red-800 flex items-center mb-4">
                              <Icon name="BookText" size={20} className="mr-2 text-red-600" />
                              Error Response Parameters
                            </h3>
                            <p className="text-sm text-red-700 mb-6">
                              Here is a list of possible errors you may encounter while validating product stock.
                            </p>
                            <div className="overflow-x-auto">
                              <table className="w-full border-collapse border border-border rounded-lg">
                                <thead>
                                  <tr className="bg-red-100">
                                    <th className="border border-border px-6 py-3 text-left font-bold">Error</th>
                                    <th className="border border-border px-6 py-3 text-left font-bold">Cause</th>
                                    <th className="border border-border px-6 py-3 text-left font-bold">Solution</th>
                                  </tr>
                                </thead>
                              <tbody>
                                {stockErrorResponseParameters?.map((item, index) => (
                                  <tr
                                    key={`stock-error-${index}`}
                                    className={index % 2 === 0 ? 'bg-background' : 'bg-muted/20'}
                                  >
                                    <td className="border border-border px-6 py-4 text-sm font-medium text-red-800">
                                      {item.term}
                                    </td>
                                    <td className="border border-border px-6 py-4 text-sm text-muted-foreground">
                                      {item.definition}
                                    </td>
                                    <td className="border border-border px-6 py-4 text-sm text-muted-foreground">
                                      {item.solution}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </section>
                      
                      {/* Orders Section */}
                      <section id="orders-api" className="scroll-mt-6 space-y-8">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-primary/10 rounded-xl">
                            <Icon name="ShoppingCart" size={24} className="text-primary" />
                          </div>
                          <h2 className="text-3xl font-extrabold text-foreground">Orders</h2>
                        </div>
                        <div className="space-y-3 text-muted-foreground leading-relaxed">
                          <p>Create and manage orders using Lakeeshop APIs.</p>
                          <p>Lakeee Shop APIs are completely <span className="font-semibold text-foreground">RESTful</span>, 
                            and all responses are returned in JSON format. Use the 
                            <span className="font-semibold text-foreground"> Order Creation API</span> to submit merchant, 
                            customer, product, and additional details to initiate new orders.
                          </p>
                        </div>
                        <div className="bg-card border border-border rounded-2xl shadow-sm p-6">
                          <h3 className="text-lg font-semibold text-foreground mb-3">Base URL</h3>
                          <div className="bg-slate-100 rounded-xl p-4">
                            <code className="text-slate-800 font-mono text-sm">
                              https://lsorder.lakeetech.com
                            </code>
                          </div>
                        </div>
                        <section id="additional-info" className="scroll-mt-6 mt-10">
                          <div className="bg-card border border-border rounded-2xl shadow-lg overflow-hidden">
                            <div className="flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-indigo-100/70 to-primary/10 border-b border-border">
                              <div className="p-2 bg-primary/20 rounded-xl">
                                <Icon name="BookText" size={22} className="text-primary" />
                              </div>
                              <h2 className="text-xl font-semibold text-foreground">
                                Order Entity
                              </h2>
                            </div>
                            <p className="text-muted-foreground px-6 py-4">
                              The order entity has the following fields:
                            </p>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <tbody>
                                  {orderEntityTable?.map((term, index) => (
                                    <tr
                                      key={`additional-info-${index}`}
                                      className={`transition ${
                                        index % 2 === 0 ? "bg-background" : "bg-muted/30"
                                      } hover:bg-muted/50`}
                                    >
                                      <td className="px-6 py-3 border-t border-border w-1/3">
                                        <span className="inline-block bg-primary/10 px-3 py-1 rounded-lg text-xs font-medium text-primary shadow-sm">
                                          {term?.term}
                                        </span>
                                      </td>
                                      <td className="px-6 py-3 border-t border-border text-muted-foreground leading-relaxed">
                                        {term?.definition}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </section>
                        {/* Customer Details Object */}
                        <section id="additional-info" className="scroll-mt-6 mt-10">
                          <div className="bg-card border border-border rounded-2xl shadow-lg overflow-hidden">
                            <div className="flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-indigo-100/70 to-primary/10 border-b border-border">
                              <div className="p-2 bg-primary/20 rounded-xl">
                                <Icon name="BookText" size={22} className="text-primary" />
                              </div>
                              <h2 className="text-xl font-semibold text-foreground">
                                Customer Details Object
                              </h2>
                            </div>
                            <p className="text-muted-foreground px-6 py-4">
                              The customer details has the following fields:
                            </p>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <tbody>
                                  {ordercustomerdetailsObject?.map((term, index) => (
                                    <tr
                                      key={`additional-info-${index}`}
                                      className={`transition ${
                                        index % 2 === 0 ? "bg-background" : "bg-muted/30"
                                      } hover:bg-muted/50`}
                                    >
                                      <td className="px-6 py-3 border-t border-border w-1/3">
                                        <span className="inline-block bg-primary/10 px-3 py-1 rounded-lg text-xs font-medium text-primary shadow-sm">
                                          {term?.term}
                                        </span>
                                      </td>
                                      <td className="px-6 py-3 border-t border-border text-muted-foreground leading-relaxed">
                                        {term?.definition}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </section>
                        {/* Address Details Object */}
                        <section id="additional-info" className="scroll-mt-6 mt-10">
                          <div className="bg-card border border-border rounded-2xl shadow-lg overflow-hidden">
                            {/* Header */}
                            <div className="flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-indigo-100/70 to-primary/10 border-b border-border">
                              <div className="p-2 bg-primary/20 rounded-xl">
                                <Icon name="BookText" size={22} className="text-primary" />
                              </div>
                              <h2 className="text-xl font-semibold text-foreground">
                                Address Details Object
                              </h2>
                            </div>
                            <p className="text-muted-foreground px-6 py-4">
                              The address details has the following fields:
                            </p>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <tbody>
                                  {customerdetailsObject?.map((term, index) => (
                                    <tr
                                      key={`additional-info-${index}`}
                                      className={`transition ${
                                        index % 2 === 0 ? "bg-background" : "bg-muted/30"
                                      } hover:bg-muted/50`}
                                    >
                                      <td className="px-6 py-3 border-t border-border w-1/3">
                                        <span className="inline-block bg-primary/10 px-3 py-1 rounded-lg text-xs font-medium text-primary shadow-sm">
                                          {term?.term}
                                        </span>
                                      </td>

                                      <td className="px-6 py-3 border-t border-border text-muted-foreground leading-relaxed">
                                        {term?.definition}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </section>
                        {/* Product Details Object */}
                        <section id="additional-info" className="scroll-mt-6 mt-10">
                          <div className="bg-card border border-border rounded-2xl shadow-lg overflow-hidden">
                            <div className="flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-indigo-100/70 to-primary/10 border-b border-border">
                              <div className="p-2 bg-primary/20 rounded-xl">
                                <Icon name="BookText" size={22} className="text-primary" />
                              </div>
                              <h2 className="text-xl font-semibold text-foreground">
                                Product Details Object
                              </h2>
                            </div>
                            <p className="text-muted-foreground px-6 py-4">
                              The product details has the following fields:
                            </p>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <tbody>
                                  {productdetailsObject?.map((term, index) => (
                                    <tr
                                      key={`additional-info-${index}`}
                                      className={`transition ${
                                        index % 2 === 0 ? "bg-background" : "bg-muted/30"
                                      } hover:bg-muted/50`}
                                    >
                                      <td className="px-6 py-3 border-t border-border w-1/3">
                                        <span className="inline-block bg-primary/10 px-3 py-1 rounded-lg text-xs font-medium text-primary shadow-sm">
                                          {term?.term}
                                        </span>
                                      </td>
                                      <td className="px-6 py-3 border-t border-border text-muted-foreground leading-relaxed">
                                        {term?.definition}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </section>
                        {/* Additional Info Object */}
                        <section id="additional-info" className="scroll-mt-6 mt-10">
                          <div className="bg-card border border-border rounded-2xl shadow-lg overflow-hidden">
                            <div className="flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-indigo-100/70 to-primary/10 border-b border-border">
                              <div className="p-2 bg-primary/20 rounded-xl">
                                <Icon name="BookText" size={22} className="text-primary" />
                              </div>
                              <h2 className="text-xl font-semibold text-foreground">
                                Additional Info Object
                              </h2>
                            </div>
                            <p className="text-muted-foreground px-6 py-4">
                              The additional information has the following fields:
                            </p>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <tbody>
                                  {additionalinfoObject?.map((term, index) => (
                                    <tr
                                      key={`additional-info-${index}`}
                                      className={`transition ${
                                        index % 2 === 0 ? "bg-background" : "bg-muted/30"
                                      } hover:bg-muted/50`}
                                    >
                                      <td className="px-6 py-3 border-t border-border w-1/3">
                                        <span className="inline-block bg-primary/10 px-3 py-1 rounded-lg text-xs font-medium text-primary shadow-sm">
                                          {term?.term}
                                        </span>
                                      </td>
                                      <td className="px-6 py-3 border-t border-border text-muted-foreground leading-relaxed">
                                        {term?.definition}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </section>
                        {/* Redemption Details Object */}
                        <section id="redemption-details" className="scroll-mt-6 mt-10">
                          <div className="bg-card border border-border rounded-2xl shadow-lg overflow-hidden">
                            <div className="flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-indigo-100/70 to-primary/10 border-b border-border">
                              <div className="p-2 bg-primary/20 rounded-xl">
                                <Icon name="BookText" size={22} className="text-primary" />
                              </div>
                              <h2 className="text-xl font-semibold text-foreground">
                                Redemption Details Object
                              </h2>
                            </div>
                            <p className="text-muted-foreground px-6 py-4">
                              The redemption details has the following fields:
                            </p>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <tbody>
                                  {redemptiondetailsObject?.map((term, index) => (
                                    <tr
                                      key={`additional-info-${index}`}
                                      className={`transition ${
                                        index % 2 === 0 ? "bg-background" : "bg-muted/30"
                                      } hover:bg-muted/50`}
                                      >
                                        <td className="px-6 py-3 border-t border-border w-1/3">
                                        <span className="inline-block bg-primary/10 px-3 py-1 rounded-lg text-xs font-medium text-primary shadow-sm">
                                          {term?.term}
                                        </span>
                                      </td>
                                      <td className="px-6 py-3 border-t border-border text-muted-foreground leading-relaxed">
                                        {term?.definition}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </section>

                        <CodeBlock
                          title="Order Entity Response"
                          language="json"
                          code={ordersampleentityResponse}
                          className="mb-6 mt-8"
                        />
                        
                        {/* Create Order Endpoint */}
                        <section id="orders-endpoint" className="scroll-mt-6 mt-10">
                          <div className="bg-card border border-border rounded-2xl shadow-lg overflow-hidden">
                            <div className="flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-primary/10 to-primary/5 border-b border-border">
                              <div className="p-2 bg-primary/20 rounded-xl">
                                <Icon name="Server" size={20} className="text-primary" />
                              </div>
                              <h3 className="text-lg font-semibold text-foreground">Create Order</h3>
                            </div>
                            <div className="px-6 py-5">
                              <p className="text-muted-foreground mb-4">
                                The following endpoint retrieves details of all products.
                              </p>
                              <div className="mb-6">
                                <h4 className="text-base font-semibold text-foreground mb-2">🔐 Authentication</h4>
                                <p className="text-muted-foreground mb-3">
                                  This API requires authentication using an API key. Include your API key in the request headers:
                                </p>
                                <CodeBlock language="json" code={`Authorization: Bearer [TOKEN]`} />
                              </div>
                              <div className="mb-6">
                                <h4 className="text-base font-semibold text-foreground mb-2">🌐 Endpoints</h4>
                                <p className="text-muted-foreground mb-3">Push bulk orders.</p>
                                <div className="bg-muted rounded-xl p-5 border border-border">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-semibold px-2 py-1 bg-green-100 text-green-700 rounded-md">POST</span>
                                    <code className="text-slate-800 font-mono text-sm">/order/create</code>
                                  </div>
                                </div>
                              </div>
                              <CodeBlock
                                title="cURL Example"
                                language="bash"
                                code={sampleOrderCurl}
                                className="mb-6 mt-8"
                              />
                              {/* Sample Response */}
                              <CodeBlock
                              title="✅ Sample Success Response"
                              language="json"
                              code={`{
                                "status": "success",
                                "code": "200",
                                "message": "Order Created",
                                "data": {
                                  "batch_no": "20230000000010",
                                  "total_orders": "1",
                                  "pass_orders": "1",
                                  "fail_orders": "0",
                                  "fail_orders_details": []
                                  }
                                  }`}
                                  className="mt-6 mb-4"
                                />
                            </div>
                          </div>
                        </section>
                        <section id="orders-response" className="scroll-mt-6 mt-10">
                          <div className="bg-card border border-border rounded-2xl shadow-lg p-6">
                            <h3 className="text-xl font-bold text-foreground flex items-center gap-2 mb-3">
                              <Icon name="ListChecks" size={20} className="text-primary" />
                              Response Parameters
                            </h3>
                            <p className="text-muted-foreground text-sm">
                              Descriptions for the response parameters are present in the Order Entity parameters table.
                            </p>
                          </div>
                        </section>
                        {/* Order Status API */}
                        <section id="orders-status" className="scroll-mt-6 mt-10">
                          <div className="bg-card border border-border rounded-2xl shadow-lg overflow-hidden">
                            <div className="flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-primary/10 to-primary/5 border-b border-border">
                              <div className="p-2 bg-primary/20 rounded-xl">
                                <Icon name="Search" size={20} className="text-primary" />
                              </div>
                              <h3 className="text-lg font-semibold text-foreground">
                                Order Status API – Fetch by Order ID
                              </h3>
                            </div>
                            <div className="px-6 py-5">
                              <p className="text-muted-foreground mb-6">
                                This endpoint retrieves the status details of a particular order using its ID.
                              </p>
                              <div className="mb-6">
                                <h4 className="text-base font-semibold text-foreground mb-2">🔐 Authentication</h4>
                                <p className="text-muted-foreground text-sm mb-3">
                                  Use your API key in the request headers:
                                </p>
                                <CodeBlock language="json" code={`Authorization: Bearer [TOKEN]`} />
                              </div>
                              <div className="mb-8">
                                <h4 className="text-base font-semibold text-foreground mb-2">🌐 Endpoint</h4>
                                <div className="bg-muted rounded-xl p-4 border border-border flex items-center justify-between">
                                  <span className="text-xs font-semibold px-2 py-1 bg-blue-100 text-blue-700 rounded-md">
                                    GET
                                  </span>
                                  <code className="text-slate-800 font-mono text-sm">/order/id/:order_id</code>
                                </div>
                              </div>
                              <div className="mb-8">
                                <h4 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
                                  <Icon name="BookText" size={18} className="text-primary" />
                                  Path Parameters
                                </h4>
                                <div className="overflow-x-auto">
                                  <table className="w-full border-collapse border border-border rounded-lg">
                                    <tbody>
                                      {orderidPathParameters?.map((term, index) => (
                                        <tr
                                          key={`stock-path-${index}`}
                                          className={index % 2 === 0 ? "bg-background" : "bg-muted/20"}
                                        >
                                          <td className="border border-border px-6 py-3">
                                            <code className="bg-slate-100 px-2 py-1 rounded text-xs font-medium text-slate-800">
                                              {term?.term}
                                            </code>
                                          </td>
                                          <td className="border border-border px-6 py-3 text-sm text-muted-foreground">
                                            {term?.definition}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                              <CodeBlock
                                title="📡 CURL Example"
                                language="bash"
                                code={`curl -X 'GET' 
'https://lsorder.lakeetech.com/order/id/SAMPLE-LS-1-34903016' 
-H 'accept: */*' 
-H 'Authorization: Bearer [TOKEN]'`}
        className="mb-8"
      />
                            {/* Success Response */}
                              <CodeBlock
                                title="✅ Sample Success Response"
                                language="json"
                                code={`{
                                  "status": "success",
                                  "code": "200",
                                  "message": "Order Status Details",
                                  "data": {
                                    "client_order_id": "SAMPLE-LS-1-34903016",
                                    "merchant_id": "12345",
                                    "status": "Pending",
                                    "order_date": "2023-11-03",
                                    "dispatch_date": "2023-11-03",
                                    "delivery_date": "2023-11-03",
                                    "return_date": "2023-11-03",
                                    "awb_no": "876777889999",
                                    "courier": "SR Courier",
                                    "remarks": "Test Remarks"
                                  }
                                }`} className="mb-8" />
                                
                                {/* Failed Response */}
                                <CodeBlock
                                  title="❌ Failed Response"
                                  language="json"
                                  code={`{
                                    "status": "error",
                                    "code": "500",
                                    "message": "Order not found",
                                    "errors": null
                                    }`}
                                  />
                              </div>
                            </div>
                          </section>
                          
                          <section id="response-parameters" className="scroll-mt-6 mt-10">
                            <div className="bg-card border border-border rounded-2xl shadow-md overflow-hidden">
                              <div className="flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-primary/10 to-primary/5 border-b border-border">
                                <Icon name="BookText" size={22} className="text-primary" />
                                <h2 className="text-lg font-semibold text-foreground">Response Parameters</h2>
                              </div>
                              <div className="overflow-x-auto">
                                <table className="w-full border-collapse">
                                  <tbody>
                                    {orderresponseParameters?.map((term, index) => (
                                      <tr
                                        key={`response-param-${index}`}
                                        className={`transition-colors ${
                                          index % 2 === 0 ? "bg-background" : "bg-muted/30"
                                        } hover:bg-primary/5`}
                                      >
                                        <td className="px-6 py-4 border-t border-border">
                                          <span className="inline-block px-3 py-1 text-xs font-medium text-primary bg-primary/10 rounded-full">
                                            {term?.term}
                                          </span>
                                        </td>
                                        <td className="px-6 py-4 border-t border-border text-sm text-muted-foreground">
                                          {term?.definition}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </section>
                          {/* Data Object */}
                          <section id="data-object" className="scroll-mt-6 mt-10">
                            <div className="bg-card border border-border rounded-2xl shadow-md overflow-hidden">
                              <div className="flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-secondary/10 to-secondary/5 border-b border-border">
                                <Icon name="Database" size={22} className="text-secondary" />
                                <h2 className="text-lg font-semibold text-foreground">Data Object</h2>
                              </div>
                              <p className="text-muted-foreground text-sm px-6 pt-4">
                                The data has the following fields:
                              </p>
                              <div className="overflow-x-auto mt-3">
                                <table className="w-full border-collapse">
                                  <tbody>
                                    {orderdataObject?.map((term, index) => (
                                      <tr
                                      key={`data-object-${index}`}
                                      className={`transition-colors ${
                                        index % 2 === 0 ? "bg-background" : "bg-muted/30"
                                      } hover:bg-secondary/5`}
                                      >
                                        <td className="px-6 py-4 border-t border-border">
                                          <span className="inline-block px-3 py-1 text-xs font-medium text-secondary bg-secondary/10 rounded-full">
                                            {term?.term}
                                          </span>
                                        </td>
                                        <td className="px-6 py-4 border-t border-border text-sm text-muted-foreground">
                                          {term?.definition}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </section>
                          {/* Fetch Orders by Batch */}
                          <section id="orders-batch" className="scroll-mt-6 mt-10">
                            <div className="flex items-center gap-3 mb-6">
                              <Icon name="PackageSearch" size={26} className="text-primary" />
                              <h2 className="text-2xl font-bold text-foreground">Fetch Orders by Batch</h2>
                            </div>
                            <p className="text-muted-foreground mb-8">
                              The following endpoint retrieves details of a particular order as per the batch number.
                            </p>
                            <div className="bg-card border border-border rounded-2xl shadow-sm mb-8">
                              <div className="px-6 py-4 border-b border-border bg-gradient-to-r from-primary/10 to-primary/5 flex items-center gap-2">
                              <Icon name="ShieldCheck" size={20} className="text-primary" />
                              <h3 className="text-lg font-semibold text-foreground">Authentication</h3>
                            </div>
                            <div className="p-6">
                              <p className="text-muted-foreground mb-4">
                                This API requires authentication using an API key.
                                You must include your API key in the request headers as follows:
                              </p>
                              <CodeBlock language="json" code={`Authorization: Bearer [TOKEN]`} />
                            </div>
                          </div>
                          <div className="bg-card border border-border rounded-2xl shadow-sm mb-8">
                            <div className="px-6 py-4 border-b border-border bg-gradient-to-r from-secondary/10 to-secondary/5 flex items-center gap-2">
                              <Icon name="Network" size={20} className="text-secondary" />
                              <h3 className="text-lg font-semibold text-foreground">Endpoint</h3>
                            </div>
                            <div className="p-6">
                              <span className="inline-block text-xs font-bold px-2 py-1 rounded-full bg-green-100 text-green-700 mb-3">
                                GET
                              </span>
                              <div className="bg-slate-100 rounded-lg p-4 mb-4 font-mono text-sm text-slate-800">
                                /order/batch/:batch_no
                              </div>
                              <p className="text-muted-foreground text-sm">Push Bulk Orders.</p>
                            </div>
                          </div>
                        <div className="bg-card border border-border rounded-2xl shadow-sm mb-8">
                            <div className="px-6 py-4 border-b border-border bg-gradient-to-r from-primary/10 to-primary/5 flex items-center gap-2">
                              <Icon name="BookText" size={20} className="text-primary" />
                              <h3 className="text-lg font-semibold text-foreground">Path Parameters</h3>
                            </div>
                            <div className="overflow-x-auto">
                              <table className="w-full border-collapse">
                                <tbody>
                                  {batchnoPathParameters?.map((term, index) => (
                                    <tr
                                      key={`path-${index}`}
                                      className={`transition-colors ${
                                        index % 2 === 0 ? "bg-background" : "bg-muted/30"
                                      } hover:bg-primary/5`}
                                    >
                                      <td className="px-6 py-4 border-t border-border">
                                        <span className="inline-block px-3 py-1 text-xs font-medium text-primary bg-primary/10 rounded-full">
                                          {term?.term}
                                        </span>
                                      </td>
                                      <td className="px-6 py-4 border-t border-border text-sm text-muted-foreground">
                                        {term?.definition}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                          <CodeBlock
                            title="Request Example"
                            language="CURL"
                            code={`curl -X 'GET' 
'https://lsorder.lakeetech.com/order/batch/20230000000010' 
-H 'accept: */*' 
-H 'Authorization: Bearer [TOKEN]'`}
    className="mb-8"
  />

                            {/* Success Response */}
                            <CodeBlock
                              title="✅ Sample Success Response"
                              language="json"
                              code={`[
                                {
                                "client_order_id": "SAMPLE-LS-1-34903016",
                                "status": "Pending",
                                "order_date": "2023-09-22"
                                },
                                {
                                "client_order_id": "SAMPLE-LS-1-34903017",
                                "status": "Pending",
                                "order_date": "2023-09-23"
                                }
                                ]`} className="mb-8"
                              />
                              
                              {/* Failed Response */}
                              <CodeBlock
                                title="❌ Failed Response"
                                language="json"
                                code={`[
                                {
                                  "client_order_id": null,
                                  "status": null
                                }
                                ]`} className="mb-8"
                              />
                              
                              {/* Response Parameters */}
                              <div className="bg-card border border-border rounded-2xl shadow-sm">
                                <div className="px-6 py-4 border-b border-border bg-gradient-to-r from-secondary/10 to-secondary/5 flex items-center gap-2">
                                  <Icon name="BookText" size={20} className="text-secondary" />
                                  <h3 className="text-lg font-semibold text-foreground">Response Parameters</h3>
                                </div>
                                <div className="overflow-x-auto">
                                  <table className="w-full border-collapse">
                                    <tbody>
                                      {clientorderidParameters?.map((term, index) => (
                                        <tr
                                        key={`response-${index}`}
                                        className={`transition-colors ${
                                          index % 2 === 0 ? "bg-background" : "bg-muted/30"
                                        } hover:bg-secondary/5`}
                                        >
                                          <td className="px-6 py-4 border-t border-border">
                                            <span className="inline-block px-3 py-1 text-xs font-medium text-secondary bg-secondary/10 rounded-full">
                                              {term?.term}
                                            </span>
                                          </td>
                                          <td className="px-6 py-4 border-t border-border text-sm text-muted-foreground">
                                            {term?.definition}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </section>
                            
                            {/* Order Cancellation */}
                            <section id="orders-batch" className="scroll-mt-6 mt-10">
                              <div className="flex items-center gap-3 mb-6">
                                <Icon name="PackageSearch" size={26} className="text-primary" />
                                <h2 className="text-2xl font-bold text-foreground">Order Cancellation</h2>
                              </div>
                              <p className="text-muted-foreground mb-8">
                                Cancel an order using client order ID.
                              </p>
                              <div className="bg-card border border-border rounded-2xl shadow-sm mb-8">
                                <div className="px-6 py-4 border-b border-border bg-gradient-to-r from-primary/10 to-primary/5 flex items-center gap-2">
                                  <Icon name="ShieldCheck" size={20} className="text-primary" />
                                  <h3 className="text-lg font-semibold text-foreground">Authentication</h3>
                                </div>
                                <div className="p-6">
                                  <p className="text-muted-foreground mb-4">
                                    This API requires authentication using an API key.  
                                    You must include your API key in the request headers as follows:
                                  </p>
                                  <CodeBlock language="json" 
                                  code={`Authorization: Bearer [TOKEN]`} />
                                </div>
                              </div>
                              <div className="bg-card border border-border rounded-2xl shadow-sm mb-8">
                                <div className="px-6 py-4 border-b border-border bg-gradient-to-r from-secondary/10 to-secondary/5 flex items-center gap-2">
                                  <Icon name="Network" size={20} className="text-secondary" />
                                  <h3 className="text-lg font-semibold text-foreground">Endpoint</h3>
                                </div>
                                <div className="p-6">
                                  <span className="inline-block text-xs font-bold px-2 py-1 rounded-full bg-green-100 text-green-700 mb-3">
                                    DELETE
                                  </span>
                                  <div className="bg-slate-100 rounded-lg p-4 mb-4 font-mono text-sm text-slate-800">
                                    /order/id/:order_id
                                  </div>
                                  <p className="text-muted-foreground text-sm">Delete Order</p>
                                </div>
                              </div>

                              {/* Path Parameters */}
                              <div className="bg-card border border-border rounded-2xl shadow-sm mb-8">
                                <div className="px-6 py-4 border-b border-border bg-gradient-to-r from-primary/10 to-primary/5 flex items-center gap-2">
                                  <Icon name="BookText" size={20} className="text-primary" />
                                  <h3 className="text-lg font-semibold text-foreground">Path Parameters</h3>
                                </div>
                                <div className="overflow-x-auto">
                                  <table className="w-full border-collapse">
                                    <tbody>
                                      {clientorderidpathParameters?.map((term, index) => (
                                        <tr
                                          key={`path-${index}`}
                                          className={`transition-colors ${
                                            index % 2 === 0 ? "bg-background" : "bg-muted/30"
                                          } hover:bg-primary/5`}
                                        >
                                          <td className="px-6 py-4 border-t border-border">
                                            <span className="inline-block px-3 py-1 text-xs font-medium text-primary bg-primary/10 rounded-full">
                                              {term?.term}
                                            </span>
                                          </td>
                                          <td className="px-6 py-4 border-t border-border text-sm text-muted-foreground">
                                            {term?.definition}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                              
                              <CodeBlock
                                title="Request Example"
                                language="CURL"
                                code={`curl -X 'DELETE' 
  'https://lsorder.lakeetech.com/order/id/SAMPLE-LS-1-34903016'
  -H 'accept: */*' 
  -H 'Authorization: Bearer [TOKEN]'`}
                                className="mb-8"
                              />
                              
                              {/* Success Response */}
                              <CodeBlock
                                title="✅ Sample Success Response"
                                language="json"
                                code={`{
                                  "status": "Success",
                                  "code": "200",
                                  "message": "Order Cancelled"
                                  }`} className="mb-8"
                                />
                                
                                {/* Failed Response */}
                                <CodeBlock
                                  title="❌ Failed Response"
                                  language="json"
                                  code={`{
                                    "status": "error",
                                    "code": "210",
                                    "message": "Order cannot be Cancelled at this stage"
                                    }`} className="mb-8"
                                />
                                
                                {/* Response Parameters */}
                                <div className="bg-card border border-border rounded-2xl shadow-sm">
                                  <div className="px-6 py-4 border-b border-border bg-gradient-to-r from-secondary/10 to-secondary/5 flex items-center gap-2">
                                    <Icon name="BookText" size={20} className="text-secondary" />
                                    <h3 className="text-lg font-semibold text-foreground">Response Parameters</h3>
                                  </div>
                                  <div className="overflow-x-auto">
                                    <table className="w-full border-collapse">
                                      <tbody>
                                        {clientorderresponseParameters?.map((term, index) => (
                                          <tr
                                            key={`response-${index}`}className={`transition-colors ${
                                              index % 2 === 0 ? "bg-background" : "bg-muted/30"
                                            } hover:bg-secondary/5`}
                                          >
                                            <td className="px-6 py-4 border-t border-border">
                                              <span className="inline-block px-3 py-1 text-xs font-medium text-secondary bg-secondary/10 rounded-full">
                                                {term?.term}
                                              </span>
                                            </td>
                                            <td className="px-6 py-4 border-t border-border text-sm text-muted-foreground">
                                              {term?.definition}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </section>
                            </section>
                            
                            {/* Error Response Parameters */}
                            <section id="error-response" className="scroll-mt-10 mt-12">
                              <h2 className="text-2xl font-bold flex items-center gap-3 mb-4">
                                <Icon name="AlertTriangle" size={26} className="text-red-600" />
                                Error Response Parameters
                              </h2>
                              <p className="text-muted-foreground mb-6">
                                Below are possible errors you may encounter while creating a category:
                              </p>
                              <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
                                <table className="w-full border-collapse">
                                  <thead className="bg-red-50">
                                    <tr>
                                      <th className="px-6 py-3 text-left font-semibold text-sm text-foreground">Error</th>
                                      <th className="px-6 py-3 text-left font-semibold text-sm text-foreground">Cause</th>
                                      <th className="px-6 py-3 text-left font-semibold text-sm text-foreground">Solution</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {errorResponseParameters.slice(1).map((item, index) => (
                                      <tr
                                        key={`error-row-${index}`}
                                        className={index % 2 === 0 ? "bg-background" : "bg-muted/20"}
                                      >
                                        <td className="px-6 py-4 text-sm font-medium text-foreground">
                                          {item.term}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-muted-foreground">
                                          {item.definition}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-muted-foreground">
                                          {item.solution}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </section>
                            
                            <section id="invoice-api" className="scroll-mt-6 mt-10">
                              <div className="flex items-center gap-3 mb-4">
                                <Icon name="ShoppingCart" size={28} className="text-primary" />
                                <h2 className="text-3xl font-bold text-foreground">Invoice</h2>
                              </div>
                              <p className="text-muted-foreground mb-3">
                                Fetch invoice using <span className="font-semibold text-foreground">LakeeeShop APIs</span>.
                              </p>
                              <p className="text-muted-foreground mb-8">
                                Lakeee Shop APIs are <span className="font-semibold">RESTful</span> and responses are returned in JSON format. 
                                The Invoice API helps you fetch invoices with <span className="text-foreground">vendor, item, customer, and other details</span>.
                              </p>
                              <div className="bg-card border border-border rounded-2xl shadow-md transition hover:shadow-lg">
                                <div className="px-6 py-4 border-b border-border flex items-center gap-2 bg-gradient-to-r from-primary/10 to-primary/5">
                                  <Icon name="BookText" size={22} className="text-primary" />
                                  <h3 className="text-lg font-semibold text-foreground">Invoice Entity</h3>
                                </div>
                                <div className="p-6">
                                  <p className="text-muted-foreground mb-4">
                                    The invoice entity includes the following fields:
                                  </p>
                                  <div className="overflow-x-auto rounded-lg border border-border">
                                    <table className="w-full border-collapse text-sm">
                                      <tbody>
                                        {invoiceEntityObject?.map((term, index) => (
                                          <tr
                                            key={`invoice-entity-${index}`}
                                            className={`transition-colors ${
                                              index % 2 === 0 ? "bg-background" : "bg-muted/30"
                                            } hover:bg-primary/5`}
                                          >
                                            <td className="px-6 py-3 border-t border-border">
                                              <span className="inline-block px-3 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full">
                                                {term?.term}
                                              </span>
                                            </td>
                                            <td className="px-6 py-3 border-t border-border text-muted-foreground">
                                              {term?.definition}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Data Object */}
                              <section id="data-object" className="scroll-mt-6 mt-10">
                                <div className="flex items-center gap-3 mb-4">
                                  <Icon name="Database" size={26} className="text-primary" />
                                  <h2 className="text-2xl font-bold text-foreground">Data Object</h2>
                                </div>
                                <p className="text-muted-foreground mb-8">
                                  The <span className="font-medium text-foreground">Data Object</span> contains the following fields:
                                </p>
                                <div className="overflow-x-auto rounded-xl border border-border shadow-md">
                                  <table className="w-full border-collapse text-sm">
                                    <thead className="bg-gradient-to-r from-primary/10 to-primary/5 text-left">
                                      <tr>
                                        <th className="px-6 py-3 font-semibold text-foreground border-b border-border">Field</th>
                                        <th className="px-6 py-3 font-semibold text-foreground border-b border-border">Description</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {invoicedataObject?.map((term, index) => (
                                        <tr
                                        key={`data-object-${index}`}
                                        className={`transition-colors ${
                                          index % 2 === 0 ? 'bg-background' : 'bg-muted/30'
                                        } hover:bg-primary/5`}
                                        >
                                          <td className="px-6 py-4 border-b border-border align-top">
                                            <code className="bg-primary/10 px-2 py-1 rounded text-xs font-medium text-primary">
                                              {term?.term}
                                            </code>
                                          </td>
                                          <td className="px-6 py-4 border-b border-border text-muted-foreground align-top">
                                            {term?.definition}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </section>

                              {/* Vendor Data Object */}
                              <section id="data-object" className="scroll-mt-6 mt-10">
                                <div className="flex items-center gap-3 mb-4">
                                  <Icon name="Database" size={26} className="text-primary" />
                                  <h2 className="text-2xl font-bold text-foreground">Vendor Data Object</h2>
                                </div>
                                <p className="text-muted-foreground mb-8">
                                  The vendor details has the following fields:
                                </p>
                                <div className="overflow-x-auto rounded-xl border border-border shadow-md">
                                  <table className="w-full border-collapse text-sm">
                                    <thead className="bg-gradient-to-r from-primary/10 to-primary/5 text-left">
                                      <tr>
                                        <th className="px-6 py-3 font-semibold text-foreground border-b border-border">Field</th>
                                        <th className="px-6 py-3 font-semibold text-foreground border-b border-border">Description</th>
                                      </tr>
                                    </thead>
                                  <tbody>
                                    {vendorDataObject?.map((term, index) => (
                                      <tr
                                      key={`data-object-${index}`}
                                      className={`transition-colors ${
                                        index % 2 === 0 ? 'bg-background' : 'bg-muted/30'
                                      } hover:bg-primary/5`}
                                      >
                                        <td className="px-6 py-4 border-b border-border align-top">
                                          <code className="bg-primary/10 px-2 py-1 rounded text-xs font-medium text-primary">
                                            {term?.term}
                                          </code>
                                        </td>
                                        <td className="px-6 py-4 border-b border-border text-muted-foreground align-top">
                                          {term?.definition}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </section>
                            
                            {/* Items Data Object */}
                            <section id="data-object" className="scroll-mt-6 mt-10">
                              <div className="flex items-center gap-3 mb-4">
                                <Icon name="Database" size={26} className="text-primary" />
                                <h2 className="text-2xl font-bold text-foreground">Items Data Object</h2>
                              </div>
                              <p className="text-muted-foreground mb-8">
                                The items details has the following fields:
                              </p>
                              <div className="overflow-x-auto rounded-xl border border-border shadow-md">
                                <table className="w-full border-collapse text-sm">
                                  <thead className="bg-gradient-to-r from-primary/10 to-primary/5 text-left">
                                    <tr>
                                      <th className="px-6 py-3 font-semibold text-foreground border-b border-border">Field</th>
                                      <th className="px-6 py-3 font-semibold text-foreground border-b border-border">Description</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {itemsDataObject?.map((term, index) => (
                                      <tr
                                        key={`data-object-${index}`}
                                        className={`transition-colors ${
                                          index % 2 === 0 ? 'bg-background' : 'bg-muted/30'
                                        } hover:bg-primary/5`}
                                      >
                                        <td className="px-6 py-4 border-b border-border align-top">
                                          <code className="bg-primary/10 px-2 py-1 rounded text-xs font-medium text-primary">
                                            {term?.term}
                                          </code>
                                        </td>
                                        <td className="px-6 py-4 border-b border-border text-muted-foreground align-top">
                                          {term?.definition}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </section>
                            
                            {/* Customer Data Object */}
                            <section id="data-object" className="scroll-mt-6 mt-10">
                              <div className="flex items-center gap-3 mb-4">
                                <Icon name="Database" size={26} className="text-primary" />
                                <h2 className="text-2xl font-bold text-foreground">Customer Data Object</h2>
                              </div>
                              <p className="text-muted-foreground mb-8">
                                The items details has the following fields:
                              </p>
                              <div className="overflow-x-auto rounded-xl border border-border shadow-md">
                                <table className="w-full border-collapse text-sm">
                                  <thead className="bg-gradient-to-r from-primary/10 to-primary/5 text-left">
                                    <tr>
                                      <th className="px-6 py-3 font-semibold text-foreground border-b border-border">Field</th>
                                      <th className="px-6 py-3 font-semibold text-foreground border-b border-border">Description</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {customerDataObject?.map((term, index) => (
                                      <tr
                                        key={`data-object-${index}`}
                                        className={`transition-colors ${
                                          index % 2 === 0 ? 'bg-background' : 'bg-muted/30'
                                        } hover:bg-primary/5`}
                                      >
                                        <td className="px-6 py-4 border-b border-border align-top">
                                          <code className="bg-primary/10 px-2 py-1 rounded text-xs font-medium text-primary">
                                            {term?.term}
                                          </code>
                                        </td>
                                        <td className="px-6 py-4 border-b border-border text-muted-foreground align-top">
                                          {term?.definition}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </section>
                            
                            {/* Additional Details Object */}
                            <section id="additional-details" className="scroll-mt-6 mt-10">
                              <div className="flex items-center gap-3 mb-4">
                                <Icon name="Database" size={26} className="text-primary" />
                                <h2 className="text-2xl font-bold text-foreground">Additional Details Object</h2>
                              </div>
                              <p className="text-muted-foreground mb-8">
                                The additional details has the following fields:
                              </p>
                              <div className="overflow-x-auto rounded-xl border border-border shadow-md">
                                <table className="w-full border-collapse text-sm">
                                  <thead className="bg-gradient-to-r from-primary/10 to-primary/5 text-left">
                                    <tr>
                                      <th className="px-6 py-3 font-semibold text-foreground border-b border-border">Field</th>
                                      <th className="px-6 py-3 font-semibold text-foreground border-b border-border">Description</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {additionalDetailsObject?.map((term, index) => (
                                      <tr
                                        key={`data-object-${index}`}
                                        className={`transition-colors ${
                                          index % 2 === 0 ? 'bg-background' : 'bg-muted/30'
                                        } hover:bg-primary/5`}
                                      >
                                        <td className="px-6 py-4 border-b border-border align-top">
                                          <code className="bg-primary/10 px-2 py-1 rounded text-xs font-medium text-primary">
                                            {term?.term}
                                          </code>
                                        </td>
                                        <td className="px-6 py-4 border-b border-border text-muted-foreground align-top">
                                          {term?.definition}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </section>
                            
                            {/* Invoice Details Object */}
                            <section id="invoice-details" className="scroll-mt-6 mt-10">
                              <div className="flex items-center gap-3 mb-4">
                                <Icon name="Database" size={26} className="text-primary" />
                                <h2 className="text-2xl font-bold text-foreground">Invoice Details Object</h2>
                              </div>
                              <p className="text-muted-foreground mb-8">
                                The invoice details has the following fields:
                              </p>
                              <div className="overflow-x-auto rounded-xl border border-border shadow-md">
                                <table className="w-full border-collapse text-sm">
                                  <thead className="bg-gradient-to-r from-primary/10 to-primary/5 text-left">
                                    <tr>
                                      <th className="px-6 py-3 font-semibold text-foreground border-b border-border">Field</th>
                                      <th className="px-6 py-3 font-semibold text-foreground border-b border-border">Description</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {invoiceDetailsObject?.map((term, index) => (
                                      <tr
                                        key={`data-object-${index}`}
                                        className={`transition-colors ${
                                          index % 2 === 0 ? 'bg-background' : 'bg-muted/30'
                                        } hover:bg-primary/5`}
                                        >
                                          <td className="px-6 py-4 border-b border-border align-top">
                                            <code className="bg-primary/10 px-2 py-1 rounded text-xs font-medium text-primary">
                                              {term?.term}
                                            </code>
                                          </td>
                                          <td className="px-6 py-4 border-b border-border text-muted-foreground align-top">
                                            {term?.definition}
                                          </td>
                                        </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </section>
                            <CodeBlock
                              title="Invoice Entity Response"
                              language="json"
                              code={invoicesampleentityResponse}
                              className="mb-6 mt-8"
                            />
                          </section>
                          
                          {/* INVOICE API */}
                          <section id="invoice-api" className="scroll-mt-6 mt-10">
                            <div className="flex items-center gap-3 mb-4">
                              <Icon name="FileText" size={28} className="text-primary" />
                              <h3 className="text-2xl font-bold text-foreground">
                                Invoice API – Fetch Invoice Details with OrderID
                              </h3>
                            </div>
                            <p className="text-muted-foreground mb-6">
                              Use this endpoint to retrieve the invoice details of a specific order by its ID.
                            </p>
                            <div className="bg-card border border-border rounded-xl p-6 mb-8 shadow-sm">
                              <h4 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                                <Icon name="Shield" size={20} className="text-primary" />
                                Authentication
                              </h4>
                              <p className="text-muted-foreground mb-4">
                                This API requires authentication using an API key. 
                                Include your key in the request headers:
                              </p>
                              <CodeBlock language="json" 
                              code={`Authorization: Bearer [TOKEN]`} 
                              />
                            </div>
                            <div className="bg-card border border-border rounded-xl p-6 mb-8 shadow-sm">
                              <h4 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                                <Icon name="Globe" size={20} className="text-primary" />
                                Endpoint
                              </h4>
                              <div className="flex items-center gap-3 mb-3">
                                <span className="bg-green-100 text-green-700 text-xs font-semibold px-3 py-1 rounded-full">GET</span>
                                <code className="text-sm font-mono bg-slate-100 px-3 py-1 rounded">
                                  /order/invoice/order_id/:order_id
                                </code>
                              </div>
                              <p className="text-muted-foreground">Fetch invoice details for a specific order.</p>
                            </div>
                            <section id="stock-path" className="scroll-mt-6">
                              <h4 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                                <Icon name="BookText" size={20} className="text-primary" />
                                Path Parameters
                              </h4>
                              <div className="overflow-x-auto mb-8 rounded-xl border border-border shadow-sm">
                                <table className="w-full border-collapse text-sm">
                                  <thead className="bg-gradient-to-r from-primary/10 to-primary/5 text-left">
                                    <tr>
                                      <th className="px-6 py-3 font-semibold text-foreground border-b border-border">Parameter</th>
                                      <th className="px-6 py-3 font-semibold text-foreground border-b border-border">Description</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {invoiceorderidpathParameters?.map((term, index) => (
                                      <tr
                                        key={`stock-path-${index}`}
                                        className={`transition-colors ${
                                          index % 2 === 0 ? "bg-background" : "bg-muted/30"
                                        } hover:bg-primary/5`}
                                      >
                                        <td className="px-6 py-4 border-b border-border">
                                          <code className="bg-primary/10 px-2 py-1 rounded text-xs font-medium text-primary">
                                            {term?.term}
                                          </code>
                                        </td>
                                        <td className="px-6 py-4 border-b border-border text-muted-foreground">
                                          <code className="bg-primary/10 px-2 py-1 rounded text-xs font-medium text-primary">
                                            {term?.definition}
                                          </code>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </section>
                            
                            <CodeBlock
                              title="Example Request"
                              language="CURL"
                              code={`curl -X 'GET' 
'https://lsorder.lakeetech.com/order/id/SAMPLE-LS-1-34903016' 
-H 'accept: */*' 
-H 'Authorization: Bearer [TOKEN]'
`}
                            />
{/* Success Response */}
  <CodeBlock
    title="✅ Sample Success Response"
    language="json"
    code={`{
  "status": "success",
  "code": "200",
  "message": "Invoice Details",
  "data": {
    "vendor": { ... },
    "customerData": { ... },
    "paymentMode": "Prepaid",
    "additionalDetails": { ... },
    "invoiceData": { ... },
    "thankYouMessage": "Thank you for shopping with US."
  }
}`}
    className="mt-6 mb-8"
                              />

  {/* Failed Response */}
  <CodeBlock
    title="❌ Failed Response"
    language="json"
    code={`{
  "status": "error",
  "code": "500",
  "message": "Order not found",
  "errors": null
}`}
                              />
                            </section>

                            {/* Response Parameters */}
                            <section id="category-entity" className="scroll-mt-6 mt-10">
                              <div className="flex items-center gap-3 mb-4">
                                <Icon name="BookText" size={24} className="text-primary" />
                                <h2 className="text-2xl font-bold text-foreground">Response Parameters</h2>
                              </div>
                              <div className="overflow-x-auto rounded-xl border border-border shadow-sm">
                                <table className="w-full border-collapse text-sm">
                                  <thead className="bg-gradient-to-r from-primary/10 to-primary/5 text-left">
                                    <tr>
                                      <th className="px-6 py-3 font-semibold text-foreground border-b border-border">
                                        Parameter
                                      </th>
                                      <th className="px-6 py-3 font-semibold text-foreground border-b border-border">
                                        Description
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {orderresponseParameters?.map((term, index) => (
                                      <tr
                                        key={`category-entity-${index}`}
                                        className={`transition-colors ${
                                          index % 2 === 0 ? "bg-background" : "bg-muted/30"
                                        } hover:bg-primary/5`}
                                      >
                                        <td className="px-6 py-4 border-b border-border">
                                          <code className="bg-primary/10 px-2 py-1 rounded text-xs font-medium text-primary">
                                            {term?.term}
                                          </code>
                                        </td>
                                        <td className="px-6 py-4 border-b border-border text-muted-foreground">
                                          {term?.definition}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </section>
                            
                            {/* Error Response Parameters */}
                            <section id="error-response" className="scroll-mt-10 mt-12">
                              <h2 className="text-2xl font-bold flex items-center gap-3 mb-4">
                                <Icon name="AlertTriangle" size={26} className="text-red-600" />
                                Error Response Parameters
                              </h2>
                              <p className="text-muted-foreground mb-6">
                                Below are possible errors you may encounter while creating a category:
                              </p>
                              <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
                                <table className="w-full border-collapse">
                                  <thead className="bg-red-50">
                                    <tr>
                                      <th className="px-6 py-3 text-left font-semibold text-sm text-foreground">Error</th>
                                      <th className="px-6 py-3 text-left font-semibold text-sm text-foreground">Cause</th>
                                      <th className="px-6 py-3 text-left font-semibold text-sm text-foreground">Solution</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {errorResponseParameters.slice(1).map((item, index) => (
                                      <tr
                                      key={`error-row-${index}`}
                                      className={index % 2 === 0 ? "bg-background" : "bg-muted/20"}
                                      >
                                        <td className="px-6 py-4 text-sm font-medium text-foreground">
                                          {item.term}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-muted-foreground">
                                          {item.definition}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-muted-foreground">
                                          {item.solution}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </section>
                            
                            {/* Error Codes */}
                            <section id="error-codes" className="scroll-mt-6">
                              <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center">
                                <Icon name="ShieldAlert" size={28} className="mr-3 text-destructive" />
                                Error Codes
                              </h2>
                              <div className="bg-card border border-border rounded-xl shadow-sm p-6 mb-8">
                                <p className="text-muted-foreground mb-4 text-sm leading-relaxed">
                                  Lakeetech APIs can return both <span className="font-medium text-foreground">success</span> 
                                  and <span className="font-medium text-destructive">error</span> responses. 
                                  All successful responses return with <code className="bg-slate-100 px-2 py-1 rounded text-xs font-medium">HTTP 200</code>.
                                  Failure responses return a structured JSON error with details for debugging.
                                </p>
                              </div>
                              {/* Best Practices */}
                              <section id="error-handling" className="scroll-mt-6 mb-8">
                                <div className="bg-muted/40 border border-border rounded-xl p-6">
                                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                                    <Icon name="CheckCircle" size={20} className="mr-2 text-green-600" />
                                    Best Practices
                                  </h3>
                                  <ul className="space-y-3 text-sm text-muted-foreground list-disc list-inside">
                                    <li>Keep your API key secret and never expose it on public platforms.</li>
                                    <li>Respect DNS TTL and avoid aggressive caching when calling APIs.</li>
                                    <li>Always validate and sanitize user inputs before sending API requests.</li>
                                  </ul>
                                </div>
                              </section>
                              <section id="products" className="scroll-mt-6 mb-8">
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start shadow-sm">
                                  <Icon name="Info" size={20} className="mr-3 text-amber-600 mt-1" />
                                  <div>
                                    <h4 className="font-semibold text-amber-800">Handy Tips</h4>
                                    <p className="text-amber-700 text-sm mt-1">
                                      When using our SDKs, error responses trigger exceptions. 
                                      Make sure you handle them gracefully in your integration.
                                    </p>
                                  </div>
                                </div>
                              </section>
                              {/* Error Structure */}
                              <section id="error-structure" className="scroll-mt-6 mb-8">
                                <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center">
                                  <Icon name="FileWarning" size={24} className="mr-3 text-primary" />
                                  Error Structure
                                </h2>
                                <p className="text-muted-foreground text-sm leading-relaxed">
                                  Error responses include <code className="bg-slate-100 px-1 rounded text-xs">code</code>, 
                                  <code className="bg-slate-100 px-1 rounded text-xs">description</code>, 
                                  <code className="bg-slate-100 px-1 rounded text-xs">field</code>, 
                                  <code className="bg-slate-100 px-1 rounded text-xs">source</code>, 
                                  <code className="bg-slate-100 px-1 rounded text-xs">step</code>, 
                                  <code className="bg-slate-100 px-1 rounded text-xs">reason</code> and 
                                  <code className="bg-slate-100 px-1 rounded text-xs">metadata</code> for quick debugging.
                                </p>
                              </section>
                              <section id="products" className="scroll-mt-6 mb-8">
                                <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center">
                                  <Icon name="Code2" size={24} className="mr-3 text-primary" />
                                  Sample Code
                                </h2>
                                <p className="text-muted-foreground text-sm leading-relaxed mb-3">
                                  Below is a sample error response indicating an authentication failure.
                                </p>
<pre className="bg-slate-900 text-slate-100 text-sm p-4 rounded-lg overflow-x-auto shadow-inner">
{`{
  "code": "401",
  "description": "Authentication failed",
  "source": "AuthService",
  "step": "TokenValidation",
  "reason": "Invalid API Key"
}`}
    </pre>
                              </section>
                              
                              {/* Error Response Parameters Table */}
                              <section id="error-response-parameters" className="scroll-mt-6">
                                <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center">
                                  <Icon name="Table" size={24} className="mr-3 text-primary" />
                                  Error Response Parameters
                                </h2>
                                <div className="overflow-x-auto rounded-lg border border-border shadow-sm">
                                  <table className="w-full text-sm">
                                    <thead className="bg-muted/50 text-foreground">
                                      <tr>
                                        <th className="px-6 py-3 text-left font-semibold">Parameter</th>
                                        <th className="px-6 py-3 text-left font-semibold">Description</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {errorcodeResponseParameters?.map((term, index) => (
                                        <tr 
                                        key={`error-response-${index}`} 
                                        className={index % 2 === 0 ? "bg-background" : "bg-muted/30"}
                                        >
                                          <td className="px-6 py-4 font-mono text-xs">{term?.term}</td>
                                          <td className="px-6 py-4 text-muted-foreground">{term?.definition}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </section>
                            </section>
                            
                            {/* Error Reasons Section */}
                            <section id="error-reasons" className="scroll-mt-6 mb-10">
                              <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center">
                                <Icon name="ListChecks" size={26} className="mr-3 text-primary" />
                                List of Error Reasons for Orders
                              </h2>
                              <p className="text-muted-foreground mb-6 text-sm leading-relaxed">
                                Below are possible error reasons you may encounter while working with the 
                                Orders API, along with explanations and recommended next steps.
                              </p>
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 shadow-sm">
                                <p className="text-blue-800 text-sm">
                                  💡 These error reasons can help you quickly identify issues and apply 
                                  the right fixes when integrating with Lakeetech APIs.
                                </p>
                              </div>
                            </section>
                            
                            {/* Generic Errors */}
                            <section id="generic-errors" className="scroll-mt-6 mb-10">
                              <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center">
                                <Icon name="AlertCircle" size={22} className="mr-3 text-destructive" />
                                Generic Errors
                              </h2>
                              <p className="text-muted-foreground mb-6 text-sm leading-relaxed">
                                These are common errors returned in Lakeetech API responses along with
                                their suggested solutions.
                              </p>
                              <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
                                <h3 className="font-semibold text-foreground mb-2">Example</h3>
                                <pre className="bg-slate-900 text-slate-100 text-sm rounded-lg p-4 overflow-x-auto shadow-inner">
                                  {`Error 1: The requested URL was not found on the server
Cause:
- Incorrect API method while calling the API.
- Feature required for the API call is not enabled on your account.

Solution:
- Use the correct API method.
- Contact Support to enable the feature on your account.`}
    </pre>
                              </div>
                            </section>
                            
                            {/* List of Errors */}
                            <section id="list-of-errors" className="scroll-mt-6">
                              <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center">
                                <Icon name="BookText" size={24} className="mr-3 text-primary" />
                                List of Errors
                              </h2>
                              <p className="text-muted-foreground mb-6 text-sm">
                                Common API errors with explanations and resolution steps.
                              </p>
                              <div className="overflow-x-auto rounded-lg border border-border shadow-md">
                                <table className="w-full text-sm">
                                  <thead className="bg-muted/50 text-foreground">
                                    <tr>
                                      <th className="px-6 py-3 text-left font-semibold">Error Code</th>
                                      <th className="px-6 py-3 text-left font-semibold">Description</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {listOfErrors?.map((term, index) => (
                                      <tr
                                      key={`list-of-errors-${index}`}
                                      className={index % 2 === 0 ? "bg-background" : "bg-muted/30"}
                                      >
                                        <td className="px-6 py-4 font-mono text-xs">{term?.term}</td>
                                        <td className="px-6 py-4 text-muted-foreground">{term?.definition}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </section>
                            
                            {/* Webhooks Section */}
                            <section id="webhooks-api" className="scroll-mt-6 mb-12">
                              <h2 className="text-3xl font-bold text-foreground mb-4 flex items-center">
                                <Icon name="Webhook" size={28} className="mr-3 text-primary" />
                                Webhooks
                              </h2>
                              <p className="text-muted-foreground mb-6 text-sm leading-relaxed">
                                Webhooks allow Lakeee Shop APIs to notify your application in real-time when a specific event occurs.
                                No polling required—just subscribe and get updates instantly.
                              </p>
                              <div className="bg-card border border-border rounded-xl p-6 shadow-sm mb-8">
                                <ul className="list-disc list-inside space-y-3 text-sm text-muted-foreground leading-relaxed">
                                  <li>
                                    Webhooks (also called <span className="font-medium text-foreground">HTTP Callbacks</span> or <span className="font-medium text-foreground">Reverse APIs</span>) 
                                    send real-time information from one application to another.
                                  </li>
                                  <li>
                                    Example: If you subscribe to the <code className="bg-slate-100 px-1 py-0.5 rounded">orderstatus</code> webhook, 
                                    you’ll get a notification whenever an order status changes.
                                  </li>
                                </ul>
                              </div>
                            </section>
                            
                            {/* Webhook vs API Comparison */}
                            <section id="webhook-vs-api" className="scroll-mt-6 mb-12">
                              <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center">
                                <Icon name="GitCompare" size={22} className="mr-3 text-primary" />
                                Webhook vs API
                              </h2>
                              <div className="grid md:grid-cols-2 gap-6">
                                <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 shadow-sm">
                                  <h3 className="text-lg font-semibold text-slate-800 mb-2">API</h3>
                                  <p className="text-slate-600 text-sm leading-relaxed">
                                    APIs send you the data <span className="font-medium">only when you request it</span>.
                                  </p>
                                  <div className="mt-3 bg-slate-900 text-slate-100 text-xs rounded-lg p-4 font-mono shadow-inner">
                                    You must poll every few seconds to check if an order is delivered.
                                  </div>
                                </div>
                                <div className="bg-green-50 border border-green-200 rounded-xl p-5 shadow-sm">
                                  <h3 className="text-lg font-semibold text-green-800 mb-2">Webhook</h3>
                                  <p className="text-green-700 text-sm leading-relaxed">
                                    Webhooks automatically <span className="font-medium">push data</span> to your system when it’s available.
                                  </p>
                                  <div className="mt-3 bg-green-100 text-green-900 text-xs rounded-lg p-4 font-mono shadow-inner">
                                    You instantly receive a notification when an order status changes.
                                  </div>
                                </div>
                              </div>
                            </section>
                            
                            {/* When to Use Webhooks */}
                            <section id="when-to-use-webhooks" className="scroll-mt-6 mb-12">
                              <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center">
                                <Icon name="Clock" size={22} className="mr-3 text-primary" />
                                When to Use Lakeeshop Webhooks
                              </h2>
                              <p className="text-muted-foreground mb-6 text-sm leading-relaxed">
                                Use Lakeeshop Webhooks to configure notifications when specific events occur. 
                                When triggered, an HTTP POST payload (JSON) is sent to your configured URL.
                              </p>
                              <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
                                <ul className="list-disc list-inside space-y-3 text-sm text-muted-foreground leading-relaxed">
                                  <li>
                                    Configure Webhooks from the <span className="font-medium text-foreground">Lakeeshop Dashboard</span> 
                                    with separate URLs for <span className="font-medium">Live</span> and <span className="font-medium">Test</span> modes.
                                  </li>
                                  <li>
                                    Test Mode webhooks receive events for your test transactions, helping you simulate real scenarios.
                                  </li>
                                </ul>
                              </div>
                            </section>
                            
                            {/* Default Webhook Format */}
                            <section id="default-webhook" className="scroll-mt-6">
                              <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center">
                                <Icon name="Webhook" size={24} className="mr-3 text-primary" />
                                Default Webhook Format - Lakeeshop
                              </h2>
                              <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 shadow-sm rounded-xl p-6 mb-8">
                                <div className="flex items-start mb-4">
                                  <Icon name="Lock" size={20} className="text-primary mr-3 mt-1" />
                                <div>
                                  <h3 className="text-lg font-semibold text-foreground mb-2">
                                    Login API
                                  </h3>
                                  <p className="text-sm text-muted-foreground mb-3">
                                    Use the Login API to authenticate and retrieve an authorization token for secured requests.
                                  </p>
                                </div>
                              </div>
                              <ul className="space-y-3 text-sm text-muted-foreground">
                                <li className="flex items-start">
                                  <Icon name="Link" size={16} className="text-blue-600 mr-2 mt-0.5" />
                                  <strong className="text-foreground mr-1">URL:</strong> Provide the URL and endpoint
                                </li>
                                <li className="flex items-start">
                                  <Icon name="Send" size={16} className="text-indigo-600 mr-2 mt-0.5" />
                                  <strong className="text-foreground mr-1">Method:</strong> POST
                                </li>
                                <li className="flex items-start">
                                  <Icon name="FileText" size={16} className="text-green-600 mr-2 mt-0.5" />
                                  <div>
                                    <strong className="text-foreground">Request Payload:</strong>
                                    <ul className="mt-2 space-y-1 ml-6">
                                      <li>
                                        <code className="bg-slate-100 px-2 py-0.5 rounded text-xs font-medium text-slate-800">
                                          email
                                        </code> : string – mandatory
                                      </li>
                                      <li>
                                        <code className="bg-slate-100 px-2 py-0.5 rounded text-xs font-medium text-slate-800">
                                          secret
                                        </code> : string – mandatory
                                      </li>
                                    </ul>
                                  </div>
                                </li>
                              </ul>

                              <h4 className="font-medium text-foreground mt-6 mb-3">Response</h4>
                              <CodeBlock
                                title="Login API Response"
                                language="json"
                                code={`{
  "status": "<Authorization status>",
  "token": "U2FsdGVkX1+…."
}`}
    />
  </div>
                  
                      <section id="authorization-status" className="scroll-mt-6">
                        <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center">
                          <Icon name="Lock" size={24} className="mr-3 text-primary" />
                          Authorization Status
                        </h2>
                        <p className="text-muted-foreground mb-6">
                          The API returns the following authorization status codes:
                        </p>
                        <div className="overflow-x-auto rounded-lg border border-border shadow-sm">
                          <table className="w-full border-collapse">
                            <thead className="bg-muted/50">
                              <tr>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Code</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Status</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Description</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Example</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="bg-background">
                                <td className="px-4 py-3 font-mono text-sm text-primary">200</td>
                                <td className="px-4 py-3 text-sm font-medium text-foreground">Success</td>
                                <td className="px-4 py-3 text-sm text-muted-foreground">
                                  Authorization successful, request allowed
                                </td>
                                <td className="px-4 py-3 text-sm text-muted-foreground">
                                  Token verified successfully
                                </td>
                              </tr>
                              <tr className="bg-muted/20">
                                <td className="px-4 py-3 font-mono text-sm text-primary">401</td>
                                <td className="px-4 py-3 text-sm font-medium text-foreground">Unauthorized</td>
                                <td className="px-4 py-3 text-sm text-muted-foreground">
                                  Authorization failed, invalid or missing token
                                </td>
                                <td className="px-4 py-3 text-sm text-muted-foreground">
                                  Invalid or expired token provided
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </section>

                      {/* Order Status API */}
                      <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 shadow-sm rounded-xl p-6 mb-8 mt-8">
                        <div className="flex items-start mb-4">
                          <Icon name="Package" size={20} className="text-primary mr-3 mt-1" />
                          <div>
                            <h3 className="text-lg font-semibold text-foreground mb-2">
                              Order Status API
                            </h3>
                            <p className="text-sm text-muted-foreground mb-3">
                              Use this API to update the order status according to their <code>statusCode</code>.
                            </p>
                          </div>
                        </div>
                        <ul className="space-y-3 text-sm text-muted-foreground">
                          <li className="flex items-start">
                            <Icon name="Link" size={16} className="text-blue-600 mr-2 mt-0.5" />
                            <strong className="text-foreground mr-1">URL:</strong> Provide Callback URL and endpoint
                          </li>
                          <li className="flex items-start">
                            <Icon name="KeyRound" size={16} className="text-amber-600 mr-2 mt-0.5" />
                            <strong className="text-foreground mr-1">Header:</strong>
                            <code className="bg-slate-100 px-2 py-0.5 rounded text-xs font-medium text-slate-800 ml-1">
                              Authorization: Bearer &lt;token&gt;
                            </code>
                          </li>
                          <li className="flex items-start">
                            <Icon name="Send" size={16} className="text-indigo-600 mr-2 mt-0.5" />
                            <strong className="text-foreground mr-1">Method:</strong> POST
                          </li>
                          <li className="flex items-start">
                            <Icon name="FileText" size={16} className="text-green-600 mr-2 mt-0.5" />
                            <div>
                              <strong className="text-foreground">Request Payload:</strong>
                              <ul className="mt-2 space-y-1 ml-6">
                                <li>
                                  <code className="bg-slate-100 px-2 py-0.5 rounded text-xs font-medium text-slate-800">
                                    referenceId
                                  </code> : string – mandatory
                                </li>
                                <li>
                                  <code className="bg-slate-100 px-2 py-0.5 rounded text-xs font-medium text-slate-800">
                                    statusCode
                                  </code> : integer – mandatory
                                </li>
                                <li>
                                  <code className="bg-slate-100 px-2 py-0.5 rounded text-xs font-medium text-slate-800">
                                    trackingUrl
                                  </code> : string – optional
                                </li>
                              </ul>
                            </div>
                          </li>
                        </ul>
                        <h4 className="font-medium text-foreground mt-6 mb-3">Response</h4>
                        <CodeBlock
                          title="Order Status API Response"
                          language="json"
                          code={`{
                            "status": "<Webhook update status>"
                            }`}
                        />
                        
                        {/* Webhook Update Status */}
                        <section id="webhook-status-codes" className="scroll-mt-6">
                          <h2 className="text-2xl font-bold text-foreground mb-6 mt-8 flex items-center">
                            <Icon name="Webhook" size={24} className="mr-3 text-primary" />
                            Webhook Update Status
                          </h2>
                          <p className="text-muted-foreground mb-6">
                            The webhook callback may return the following status codes:
                          </p>
                          <WebhookStatusTable />
                        </section>
                        
                        {/* Status Codes Table */}
                        <div className="mt-8">
                          <h4 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                            <Icon name="ListOrdered" size={20} className="mr-2 text-primary" />
                            Status Codes
                          </h4>
                          <div className="overflow-x-auto rounded-xl border border-border shadow-md">
                            <table className="w-full border-collapse text-sm">
                              <thead>
                                <tr className="bg-muted/60">
                                  <th className="px-5 py-3 text-left font-semibold text-foreground">Code</th>
                                  <th className="px-5 py-3 text-left font-semibold text-foreground">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {[
                                  [0, "ORDER_PROCESSING"],
                                  [1, "CONFIRMED"],
                                  [2, "SHIPPED"],
                                  [3, "OUT_FOR_DELIVERY"],
                                  [4, "DELIVERED"],
                                  [8, "CANCELED"],
                                  [5, "RETURNED"],
                                  [6, "ORDER_FAILED"],
                                  [9, "REFUNDED"],
                                  [10, "REFUND_INITIATED"],
                                  [11, "REFUND_COMPLETED"],
                                  [12, "RETURN_REQUEST"],
                                  [13, "RETURN_REJECT"],
                                  [14, "RETURN_ACCEPTED"],
                                  [16, "RETURN_DELIVERED"],
                                ].map(([code, status], idx) => (
                                <tr
                                key={code}
                                className={idx % 2 === 0 ? "bg-background" : "bg-muted/20"}
                                >
                                  <td className="px-5 py-3 border-t border-border font-mono text-primary font-medium">
                                    {code}
                                  </td>
                                  <td className="px-5 py-3 border-t border-border font-medium text-foreground">
                                    {status}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            </div>
            
            {/* Quick Reference Panel */}
            <div className={`${quickPanelCollapsed ? 'w-0' : 'w-80'} transition-all duration-300 hidden xl:block`}>
              <QuickReferencePanel />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default APIDocumentationHub;